import React, { useState, useEffect, useCallback, useRef, useMemo, useSyncExternalStore } from 'react';
import { combineEditorBusy, getQuoteEditorBusy, subscribeEditorBusy } from './services/editorBusy';
// `aktuellerBuildStempel` statt `__BUILD_TIME__`: derselbe Wert, der als
// Kopfzeile `x-app-build` an den Server geht — im Entwicklungslauf gaebe es
// den define-Block nicht, und Anzeige, Regel und Kopfzeile liefen auseinander.
import { aktuellerBuildStempel, supabase } from './services/supabaseClient';
import { AuthScreen } from './components/AuthScreen';
import { Sidebar } from './components/Sidebar';
import { MobileNavigation } from './components/MobileNavigation';
import { AdminView } from './components/AdminView';
import { ADMIN_BEREICH_VIEWS, ersterTab, istAdminBereichView } from './components/admin/adminBereiche';
import { BereichsUmschalter, type UmschalterEintrag } from './components/navigation/BereichsUmschalter';
import { ProjectHub } from './components/ProjectHub';
import { VorankuendigungView } from './components/UfsA/VorankuendigungView';
import { UnterlageView } from './components/UfsA/UnterlageView';
import { SiGeKoInspectionModule } from './components/SiGeKoInspectionModule'; 
import { HolidayPlanner } from './components/HolidayPlanner';
import { UserSettingsModal } from './components/UfsA/UserSettingsModal';
import { GlobalMapDashboard } from './components/GlobalMapDashboard';
import { ProjectSetupWizard } from './components/ProjectSetupWizard';
import { TasksView } from './components/TasksView';
import { ImportPreviewModal } from './components/ImportPreviewModal';
import { useOfflineSync, entwurfAufServerstand, SaveStatus, type ZuAltKennung } from './hooks/useOfflineSync';
import { getQueueEntry } from './services/offlineQueue';
import { queueEntryProjectLabel, type QueueEntry } from './services/offlineQueueLogic';
import { uebernimmGeschriebenenStand } from './services/reportMerge';
import { ProjectTransferModal } from './components/ProjectTransferModal';
import { ProjectKickoffGate } from './components/kickoff/ProjectKickoffGate';
import { KICKOFF_AKTUALISIEREN } from './components/kickoff/kickoffDialogLogic';
import { benachrichtigeKickoff } from './services/kickoff/kickoffService';
import { applyRepresentatives } from './services/projectRepresentatives';
import { planungsEintraege, planungsPersonenAusSetup, profilName, zuweisungsNotizText } from './services/planungsZuweisung';
import { DashboardView } from './components/DashboardView';
import { ProjectOverview } from './components/ProjectOverview';
import { CrmView } from './components/crm/CrmView';
import { LeadsView } from './components/crm/LeadsView';
import { CrmMapView } from './components/crm/map/CrmMapView';
import { FreelancerView } from './components/freelancer/FreelancerView';
import { FreelancerPortal } from './components/freelancer/portal/FreelancerPortal';
import { QuotesView } from './components/crm/QuotesView';
import { InvoicesView } from './components/invoices/InvoicesView';
import { ErpCockpit } from './components/erp/ErpCockpit';
import { LernzentrumView } from './components/lernzentrum/LernzentrumView';
import { KontextHilfe } from './components/lernzentrum/KontextHilfe';
import { HilfeChat } from './components/lernzentrum/HilfeChat';
import { LernzentrumOeffnerContext } from './components/lernzentrum/lernzentrumOeffner';
import { TourOverlay } from './components/lernzentrum/TourOverlay';
import { useTour } from './hooks/useTour';
import { TOUR_SCHLUESSEL, schreibeTourUebergabe } from './components/lernzentrum/tourLogic';
import { useIsMobile } from './hooks/useIsMobile';
import type { TutorialKapitelId, TutorialRolle } from './tutorial/types';
import { useLernRollen } from './hooks/useLernzentrum';
import { kapitelNachId } from './tutorial/register';
import { markiereAbgeschlossen as merkeTourAbschluss, markiereTourAbgebrochen, merkeErsteinweisung } from './services/tutorial/tutorialService';
import { resolveInvoiceConfig } from './services/invoiceConfig';
import { loadCompanyInvoiceConfig } from './services/companyInvoiceConfig';
import { loadCompanyProjectCatalog, clearCompanyProjectCatalog, CompanyProjectCatalog } from './services/companyProjectCatalog';
import { ladeMangelKatalog, leereMangelKatalog } from './services/mangelKatalog/laden';
import { ladeSigePlanVorlagen, leereSigePlanVorlagen } from './services/sigePlanVorlagen';
import { applyLetterheadConfig } from './services/letterheadConfig';
import { resolveFooterText } from './services/profileSettings';
import { AlertTriangle, Play, X, MapPin, Search, ArrowRight, Clock, Loader2, RefreshCw, FolderKanban, Building2, BarChart3, Gauge } from 'lucide-react';
import { Report, ReportMetadata, UserProfile, AppView, InvoiceConfig, IntervalConfig } from './types';
import { flushJournalMirror, getServiceLog, ladeBuchungsartenInsRegister, retryJournalMirrors } from './services/serviceJournal';
import { leereBuchungsartenRegister, vergissBuchungsarten } from './services/serviceLogUtils';
import { loadProjectsFromCloud, loadProjectsFromCloudMitBeginn, invalidateProjectsLoadCache, respondToTransfer, saveProjectToCloud, loadProjectById, createNotificationsForMentions, loadSingleProjectOverview, updateProjectExportStatus, saveReportSnapshot, logEmailAttempt } from './services/cloudService';
import type { ListProject } from './services/listProject';
import { notifyDueFollowUps, linkProjectsToCustomer } from './services/crmService';
import { subscribeWithAuthRecovery, attachWakeAuthRefresh } from './services/realtimeResilience';
import { ERSTE_SITZUNGSRUNDE, bildeRueckkehr, entscheideAuthLaden, erzeugeNachholer, naechsteSitzungsRunde, rundeGilt } from './services/authRefreshPolicy';
import { erzeugeProjektSammler, fuegeRealtimeEintraegeEin, istBekannterStand, leseProjektAenderung, type ProjektSammler, type Zeile } from './services/realtimeProjectSync';
import { istFehlendeSignalTabelle, ladeListenEintraege, ladeRealtimeEintraege, leseSichtbareKennungen, leseSignaleSeit, leseSignalVorlauf, type SignalAbruf } from './services/realtimeProjektLader';
import { LEERER_LISTENSTAND, behalteJuengere, brauchtVollLoad, naechsterVollLoadAmMs, fuehreZusammen, istAbgedeckt, juengererStand, lesenAb, leseSignalAenderung, lichteSichtungsbuch, markeVon, naechsteMarke, neuesSichtungsbuch, planeDelta, sichte, signalVormerkung, zeileDerVormerkung, type AbrufMarken, type ListenStand, type Signal, type SignalModus, type Sichtungsbuch } from './services/projektListeDelta';
import { baueCacheStand, erzeugeCacheSpeicherung, projektListeSpeicher, pruefeCache, stelleCacheHer, type CacheSpeicherung } from './services/projektListeCache';
import { geraetOnline, istNetzFehler } from './services/netzFehler';
import { saveNavState, loadNavState, clearNavState, isProjectView } from './services/navigationPersistence';
import { forget as forgetViewMemory } from './services/viewMemory';
import { useRememberedState } from './hooks/useRemembered';
import { hatProjektStatus, istProjektGesperrtFuer, PROJEKT_GESPERRT_HINWEIS } from './services/projectOpenGate';
import { isProjectFinished, type ProjectStatusSource } from './services/projectBilling';
import type { ProjectAccessRow } from './services/projectAccess';
import { readOnlyMessage, READ_ONLY_BANNER, READ_ONLY_BANNER_MODUL, LOCK_UEBERNAHME_FEHLGESCHLAGEN, LOCK_UEBERNAHME_OHNE_NEULADEN, FOREIGN_OHNE_NEULADEN, FOREIGN_FREIGABE_ABGELAUFEN, lockTakeoverConfirm, idleReleaseNotice, idleWarningNotice, IDLE_KEEP_ALIVE_LABEL } from './services/readOnlyNotice';
import { ProjectNoticeBanner } from './components/ProjectNoticeBanner';
import { ForeignAccessDialog } from './components/ForeignAccessDialog';
import { LoeschantragPruefDialog } from './components/admin/LoeschantragPruefDialog';
import { refreshNotifications, resetNotifications } from './services/notificationStore';
import { ForeignAccessContext, type ForeignAccessContextValue } from './contexts/ForeignAccessContext';
import { classifyForeignAccessError, darfOhneBegruendungOeffnen, decideForeignGrantExpiry, fetchActiveForeignGrant, grantForeignAccess, grantForeignAccessByRight, isForeignGrantActive, isForeignProjectFor, type ForeignAccessGrant } from './services/foreignAccess';
import { useRechte } from './hooks/useRechte';
import { aktualisiereMeineRechte } from './services/rechte/rechteService';
import { rechtFehlerOder } from './services/rechte/rechtFehler';
import { ohneRechtHinweisFuer, speicherRechtHinweis } from './services/rechte/speicherRechtHinweis';
import { ansichtErlaubt, ansichtWache, keinZugriffText, projektAnlegenVerweigert } from './services/rechte/ansichtRechte';
import { darfIrgendeinProjektAnlegen } from './services/projektAnlageRechte';
import { MaterialBereich } from './components/material/MaterialBereich';
import { useMaterialOffen } from './hooks/useMaterialOffen';
import { TalentpoolBereich } from './components/talentpool/TalentpoolBereich';
import { useTalentpoolNeu } from './hooks/useTalentpoolNeu';
import { KeinZugriff } from './components/KeinZugriff';
import { ProjectLock, LockPresence, QueueUploadDecision, LOCK_HEARTBEAT_MS, LOCK_IDLE_EXIT_MINUTES, isAdminRole, isLockHeldByOther, canTakeOverLock, decideLockPresence, decideQueueUpload, describeProjectNotice, hasLostHeldLock, lockHolderName, shouldExitForIdle, describeIdleCountdown, formatIdleCountdown } from './services/projectLockLogic';
import { isIdleExitHeld } from './services/idleHold';
import { recordAppStart, recordProjectOpen, recordProjectClose, recordProjectCloseBeacon, recordProjectRestore, recordProjectHidden, recordProjectVisible } from './services/userActivityService';
import type { UserActivityOpenReason } from './services/userActivityTypes';
import { acquireProjectLock, acquireProjectLockWithin, fetchProjectLock, releaseProjectLock, renewProjectLock, takeoverProjectLock, type ProjectLockResult } from './services/projectLockService';
import { useProjectLocks } from './hooks/useProjectLocks';
import { ProjectLockHint } from './components/ProjectLockHint';
import { resumePendingSendPersistence } from './services/pendingSendPersistence';
import {
    BUILD_VERALTET_MELDUNG,
    createVersionGate,
    decideOnVeraltet,
    istBuildVeraltet,
    onBuildGuardRejected,
    setzeVersandPruefung,
    type VeraltetAnlass,
} from './services/versionGate';
import {
    ABMELDEN_FEHLGESCHLAGEN, abmelden, decideOnSignedOut, istLokaleAbmeldung,
    merkeSitzungsende, tabSpeicher, vergissLokaleAbmeldung, warteAufRuhe,
} from './services/sessionRevocation';
import { useBauphaseGate } from './hooks/useBauphaseGate';
import { BauphaseScreen, BauphaseBanner } from './components/bauphase';
import { calculateNextDueDate } from './services/dateService';
// Verschieben/Ueberspringen aus der Liste: voll laden, nur den Termin aendern (Vorfall 21.10.2026).
import { erstelleFolgeterminAnzeige, folgeterminBasis, mitListenTermin, ueberspringeTurnus, verschiebeUmTage, zielNachTagen, zielUebersprungen, type FolgeterminErgebnis, type ListenTermin } from './services/folgeterminService';
import { Button } from './components/Button';
import { SigePlanModul } from './components/sige-plan/SigePlanModul';
import type { SigePlanReiter } from './components/sige-plan/SigePlanModul';
import { EinweisungModul } from './components/sige-einweisung/EinweisungModul';
import { UnterweisungModul } from './components/sifa/UnterweisungModul';
import { MitarbeiterModul } from './components/sifa/MitarbeiterModul';
import { FristenModul } from './components/sifa/FristenModul';
import { TaetigkeitenModul } from './components/sifa/TaetigkeitenModul';
import { GbuModul } from './components/sifa/gbu/GbuModul';
import VorlagenView from './components/sifa/vorlagen/VorlagenView';
import { istSifaFachkraft } from './services/sifa/vorlagenService';
import { PlanungModul } from './components/planungsphase/PlanungModul';
import { DokumenteModul } from './components/dokumente/DokumenteModul';
import { istUebungsprojekt } from './services/uebung/uebungService';
import { AsplanModul } from './components/asplan/AsplanModul';
import { BauordnungModul } from './components/bauordnung/BauordnungModul';
import { mapServicesToDb } from './services/listenSpiegel';
import { ProjectArchiveView } from './components/ProjectArchiveView';

import { PasswordResetView } from './components/PasswordResetView';
// Android-App (docs/android-app-plan.md, docs/android-app-vertrag.md): jeder
// Weg hier greift nur, wenn istNativeApp() wahr ist (die Umfangsgrenze nur in
// der Handy-Variante) — ausser der Seite `#/app` samt Menueeintrag und dem
// Download-Hinweis, die auch im Browser gelten.
import type { PluginListenerHandle } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { istNativeApp, hatPlugin, appVariante } from './services/nativePlatform';
import {
    ansichtInAppErlaubt, entscheideZurueck, erzeugeZurueckWaechter, hatRegistriertenZurueck, istAppSeitenAdresse,
    istEingabeFokussiert, istOverlayVerlauf, neueWaechterKennung, offenerDialogImDom,
    registriertenZurueckAusfuehren, umfangBeschraenkt,
    type WaechterOberflaeche, type ZurueckLage, type ZurueckWaechter,
} from './services/appUmfang';
import { NurImBrowser } from './components/NurImBrowser';
import { AppUpdateBanner } from './components/app/AppUpdateBanner';
import { AppDownloadHinweis } from './components/app/AppDownloadHinweis';
import { AppDownloadView } from './components/app/AppDownloadView';
import { sichereEingabenVorUpdate } from './services/appUpdate';
import {
    meldeNeuerEintrag, plusEntfernung, plusOeffnenSchritt, plusSchrittNachStandort, plusStandortFehler,
    PLUS_OFFENE_EINGABE, PLUS_STANDORT_OPTIONEN, type PlusBlatt, type PlusProjekt,
} from './services/plusStandort';

/**
 * Die SiFa-Ansichten (SifaModuleGrid → executeModuleNavigation).
 * `AppView` (types.ts) und `VIEW_KIND` (navigationPersistence.ts, 'project')
 * kennen beide Werte; die Konstanten stehen hier nur, damit Navigation,
 * isModuleView und Rendering dieselbe Schreibweise nutzen.
 */
const VIEW_SIFA_UNTERWEISUNG: AppView = 'module-sifa-unterweisung';
const VIEW_SIFA_MITARBEITER: AppView = 'module-sifa-mitarbeiter';
const VIEW_SIFA_FRISTEN: AppView = 'module-sifa-fristen';
const VIEW_SIFA_TAETIGKEITEN: AppView = 'module-sifa-taetigkeiten';
const VIEW_SIFA_GBU: AppView = 'module-sifa-gbu';

/**
 * Ansichten mit eigenem Kontext-"?" in der Kopfleiste — dort entfaellt das
 * schwebende Fragezeichen unten rechts.
 *
 * Alle Verwaltungsbereiche rendern DIESELBE `AdminView`, und deren "?" sitzt
 * in der Reiterleiste. Die Liste kommt deshalb aus adminBereiche.ts: stuende
 * hier eine Abschrift, haette jeder neue Bereich zwei Knoepfe.
 */
const OHNE_GLOBALES_FRAGEZEICHEN: AppView[] = ['project-hub', 'invoices', ...ADMIN_BEREICH_VIEWS];

/**
 * Rueckweg der vollen Oberflaeche fuer die Android-Zurueck-Taste (nur in der
 * App, docs/android-app-plan.md P4).
 *
 * Der Zuhoerer selbst sitzt in App UEBER den fruehen Returns und minimiert
 * auf Anmeldemaske, Ladeschirm, Bauphase und Freelancer-Portal nur. Die volle
 * Oberflaeche traegt hier ihren Rueckweg in `zurueckRef` ein — so wirkt immer
 * genau einer. Eine eigene kleine Komponente statt eines Effekts in App: der
 * Rueckweg braucht `handleLeaveProject` und `isModuleView`, die erst UNTER den
 * fruehen Returns entstehen. Ein Effekt ueber den Returns, der sie schliesst,
 * waere genau die TDZ-Falle vom 13.09.2026. Diese Komponente wird erst nach
 * den Returns gerendert; ihr Rueckruf stammt deshalb immer aus einem Render,
 * der vollstaendig durchgelaufen ist.
 *
 * `onZurueck` liefert false, wenn es kein Zurueck mehr gibt (Startseite) —
 * dann minimiert der Zuhoerer die App, nie beendet er sie.
 *
 * Im Handy-Browser liest der Verlaufswaechter denselben Ref (Zurueck-Geste,
 * siehe „Handy-Browser" in App): gesetzt heisst dort „volle Oberflaeche".
 */
function AndroidZurueckTaste({ onZurueck, zurueckRef }: { onZurueck: () => boolean; zurueckRef: React.RefObject<(() => boolean) | null> }) {
    useEffect(() => { zurueckRef.current = onZurueck; });
    useEffect(() => () => { zurueckRef.current = null; }, [zurueckRef]);
    return null;
}

/**
 * Kennung des Verlaufswaechters fuer DIESEN Seitenaufruf (appUmfang). Einmal je
 * Laden, nicht je Waechter: ein neu angelegter Waechter (Drehen ueber die
 * Mobilgrenze) muss den eigenen Eintrag wiedererkennen, sonst laege ein
 * zweiter darueber.
 */
const ZURUECK_WAECHTER_KENNUNG = neueWaechterKennung();

/**
 * Ref sofort, State fuer den naechsten Render — fuer die Zustaende der
 * Projektliste (Nr. 175), die zwischen zwei Renders entscheiden UND die
 * Oberflaeche steuern. Ausserhalb der Komponente, damit `fetchProjects` nur
 * Refs und Setter schliesst und aus jedem Effekt gerufen werden darf.
 */
const setzeMitRef = <T,>(ref: { current: T }, setzen: (wert: T) => void, wert: T): void => {
    ref.current = wert;
    setzen(wert);
};

/**
 * Was `handleOpenProject` von seinem Argument liest: Kennung, Name, Status
 * und Zustaendigkeit. Die Aufrufer wissen verschieden viel — ein Eintrag der
 * Projektliste (`ListProject`), die Zeile eines eben angelegten Projekts, ein
 * voll geladenes Projekt (Mutter oeffnen) oder nur `{ id }` (Angebot,
 * Rechnung). Geladen wird in jedem Fall neu (`loadProjectById`); aus dem
 * Argument selbst wird nichts gespeichert.
 */
type ZuOeffnendesProjekt = { id: string; name?: string | null } & ProjectStatusSource & ProjectAccessRow;

/** Wie `setzeMitRef`, aus dem juengsten Wert berechnet; ohne Aenderung nichts. */
const aendereMitRef = <T,>(ref: { current: T }, setzen: (wert: T) => void, aendern: (alt: T) => T): void => {
    const neu = aendern(ref.current);
    if (neu === ref.current) return;
    ref.current = neu;
    setzen(neu);
};

export default function App() {
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  /**
   * Eigene Kennung fuer den Sperrvergleich.
   *
   * Die Session ist Sekunden vor dem Profil da; die Offline-Warteschlange
   * laeuft aber schon mit der Session los. Verglich man in diesem Fenster
   * gegen `userProfile.id` (noch undefined), zaehlte die EIGENE lebende
   * Sperre als fremd — Rolf Petersen las am 07.09.2026 „weil Rolf Petersen
   * das Projekt gerade bearbeitet". Beide Werte sind dieselbe auth.uid().
   */
  const lockUserId: string | undefined = session?.user?.id ?? userProfile?.id;
  /**
   * Die Nutzerkennung der Sitzung — daran haengen die Effekte, nicht am
   * Sitzungsobjekt. auth-js liefert bei jeder Rueckkehr in den Tab (Kamera,
   * App-Wechsel) und bei jedem Token-Refresh ein NEUES Objekt
   * (authRefreshPolicy.ts); ein Effekt mit `[session]` lief dann jedes Mal
   * neu, baute Kanaele neu auf und lud die ganze Projektliste.
   */
  const sitzungsNutzerId: string | null = session?.user?.id ?? null;
  /**
   * Rechte der angemeldeten Person (Rollensystem, docs/rollensystem-plan.md
   * §6.1). Ohne Migration bzw. bis die Antwort da ist: Rechte der Systemrolle
   * — das heutige Verhalten. Hier oben, vor den fruehen Returns: Effekte und
   * Rueckrufe weiter oben lesen sie (TDZ-Lehre vom 13.09.2026).
   *
   * `ohneBegruendung`: fremde Projekte oeffnen ohne Begruendungsdialog —
   * nur mit dem Recht aus der Datenbank, nie aus dem Rueckfall.
   */
  const rechte = useRechte(userProfile?.role);
  const ohneBegruendung = darfOhneBegruendungOeffnen(rechte);
  /**
   * Offene Materialmeldungen fuers Abzeichen am Menuepunkt „Material"
   * (docs/materialverwaltung-plan.md). Gefragt wird nur mit Material-Recht —
   * dieselbe Regel wie Wache und Seitenleiste; das SiFa-Haekchen spielt fuer
   * diese Ansicht keine Rolle. Hier oben, vor den fruehen Returns: der Hook
   * traegt Effekte (TDZ-Lehre vom 13.09.2026).
   */
  const materialSichtbar = ansichtErlaubt('material', rechte, userProfile?.role, { istSifaFachkraft: false });
  const materialOffen = useMaterialOffen(materialSichtbar);
  /**
   * Neue Bewerbungen fuers Abzeichen am Menuepunkt „Talentpool"
   * (docs/talentpool-plan.md). Gefragt wird nur mit Recht
   * `talentpool.verwalten` und nicht in der Handy-App, wo der Punkt fehlt
   * (appUmfang). Hier oben, vor den fruehen Returns: der Hook traegt
   * Effekte (TDZ-Lehre vom 13.09.2026).
   */
  const talentpoolSichtbar = ansichtErlaubt('talentpool', rechte, userProfile?.role, { istSifaFachkraft: false })
    && !umfangBeschraenkt(appVariante());
  const talentpoolNeu = useTalentpoolNeu(talentpoolSichtbar);
  // Rechnungs-Grundeinstellungen der Firma (siehe companyInvoiceConfig.ts).
  // undefined = noch nicht geladen bzw. nichts hinterlegt.
  const [companyInvoiceConfig, setCompanyInvoiceConfig] = useState<Partial<InvoiceConfig> | undefined>(undefined);
  // Projekt-Kategorisierung der Firma (Kategorien, Bauarten) — siehe
  // companyProjectCatalog.ts. undefined = noch nicht geladen.
  const [companyProjectCatalog, setCompanyProjectCatalog] = useState<CompanyProjectCatalog | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  
  const [view, setView] = useState<AppView>('dashboard');
  const [previousView, setPreviousView] = useState<AppView>('dashboard'); 
  
  // Zuletzt gewaehlter Reiter der Verwaltung. Gemerkt (viewMemory), damit der
  // Rueckweg aus einem Projekt — und F5 — wieder auf DIESEM Reiter landet,
  // nicht auf dem Standardreiter des Seitenleisten-Eintrags. Den Standard
  // setzt nur noch der Klick in der Seitenleiste (navigateToView).
  const [lastAdminTab, setLastAdminTab] = useRememberedState<string>('admin.tab', 'dashboard');
  // Deep-Link Projekt → Buchhaltung: Projekt, dessen Abrechnung im Admin-Tab aufgehen soll
  // Der Sprung "Projekt -> Buchhaltung" merkt sich, welches Projekt gemeint
  // ist. Beim Verlassen der Rechnungen muss das wieder weg: sonst oeffnete der
  // naechste Aufruf ueber die Navigation erneut die Buchhaltung dieses
  // Projekts statt der Belegliste.
  const [accountingFocusId, setAccountingFocusId] = useState<string | null>(null);
  // Sprungziel aus der CRM-Karte in die Kundenakte (Plan Abschnitt 6,
  // "Drilldown ... -> Sprung in CustomerDetail"). Gleiches Muster wie
  // accountingFocusId: die Ziel-Ansicht bekommt die Id als Startwert.
  const [crmFocusCustomerId, setCrmFocusCustomerId] = useState<string | null>(null);
  // Sprungziele aus der Historie der Kundenakte in Angebot bzw. Rechnung.
  // Startwert der Ziel-Ansicht; beim Verlassen der Ansicht wieder weg (s. u.),
  // sonst oeffnete der naechste Aufruf ueber die Navigation denselben Beleg.
  const [quoteFocusId, setQuoteFocusId] = useState<string | null>(null);
  const [invoiceFocusId, setInvoiceFocusId] = useState<string | null>(null);
  // Sprungziel aus dem Kontext-"?" in ein bestimmtes Kapitel des Lernzentrums.
  // Dasselbe Muster wie accountingFocusId und crmFocusCustomerId: die
  // Ziel-Ansicht bekommt die Id als STARTWERT und steuert sich danach selbst.
  const [lernzentrumKapitelId, setLernzentrumKapitelId] = useState<TutorialKapitelId | null>(null);
  // Kapitel, mit dem der Lern-Chat „Frag OS-IA" geoeffnet wird, wenn er von
  // einer Kapitelseite aus per „Dazu fragen" angestossen wird (Plan §1.8,
  // Paket E). Dasselbe Muster wie lernzentrumKapitelId, nur fuer `HilfeChat`
  // statt fuer die Lernzentrums-Ansicht selbst.
  const [hilfeChatKapitel, setHilfeChatKapitel] = useState<TutorialKapitelId | undefined>(undefined);
  // Faellige Wiedervorlagen der Neukunden-Pipeline fuers Sidebar-Abzeichen.
  const [leadsDueCount, setLeadsDueCount] = useState(0);
  /**
   * Haekchen „SiFa" des angemeldeten Nutzers (docs/sifa-vorlagen-plan.md §3h):
   * schaltet Menuepunkt und Bearbeiten der Vorlagen frei; die Rechte selbst
   * prueft RLS. Fail-open false — ein Lesefehler kostet nur den Menuepunkt.
   *
   * Hier oben, vor den fruehen Returns, und der Effekt schliesst nur
   * `session` (oben deklariert), den eigenen Setter und den Modul-Import
   * (TDZ-Lehre vom 13.09.2026). Abhaengig nur von der User-Id: ein
   * Token-Refresh laedt nicht neu, ein Benutzerwechsel faengt bei false an
   * — sonst erbte der Naechste bis zur Antwort das Haekchen des Vorgaengers.
   */
  const [istSifa, setIstSifa] = useState(false);
  /**
   * Antwort zum Haekchen da (auch ein Fehler zaehlt)? Erst dann zeigt die
   * Ansichts-Wache fuer die SiFa-Vorlagen „Kein Zugriff" — nach F5 stuende die
   * Karte sonst kurz da, bis das Haekchen geladen ist.
   */
  const [istSifaGeladen, setIstSifaGeladen] = useState(false);
  useEffect(() => {
    setIstSifa(false);
    setIstSifaGeladen(false);
    const uid = session?.user?.id;
    if (!uid) return;
    let abgebrochen = false;
    istSifaFachkraft(uid)
      .then((ja) => { if (!abgebrochen) setIstSifa(ja); })
      .catch(() => undefined)
      .finally(() => { if (!abgebrochen) setIstSifaGeladen(true); });
    return () => { abgebrochen = true; };
  }, [session?.user?.id]);
  const [projects, setProjects] = useState<ListProject[]>([]);
  // Die Liste von JETZT fuer den Realtime-Sammler (realtimeProjectSync): er
  // entscheidet Sekunden nach dem Ereignis, nicht im Render.
  const projekteRef = useRef(projects);
  useEffect(() => { projekteRef.current = projects; }, [projects]);
  // ─── Projektliste im Geraet und Abgleich (Nr. 175) ────────────────────────
  //
  // projektListeDelta.ts / projektListeCache.ts erklaeren die Begriffe. Hier
  // oben, weil der Wiederherstellungs-Effekt und `incomingTransfers` sie
  // lesen (TDZ-Lehre vom 13.09.2026).
  //
  // Worauf die Liste hoert (unbekannt / an / aus). State fuer den Kanal-
  // Effekt, Ref fuer alles, was zwischen zwei Renders entscheidet.
  const [signalModus, setSignalModusState] = useState<SignalModus>('unbekannt');
  const signalModusRef = useRef<SignalModus>('unbekannt');
  const setzeSignalModus = (modus: SignalModus) => setzeMitRef(signalModusRef, setSignalModusState, modus);
  // Wasserstand und Staende — als State, damit sie MIT der Liste
  // festgeschrieben werden (der Geraetespeicher speichert beides zusammen);
  // der Ref traegt den juengsten Wert fuer die Entscheidungen.
  const [listenStand, setListenStand] = useState<ListenStand>(LEERER_LISTENSTAND);
  const listenStandRef = useRef<ListenStand>(LEERER_LISTENSTAND);
  const aendereListenStand = (aendern: (s: ListenStand) => ListenStand) => aendereMitRef(listenStandRef, setListenStand, aendern);
  // Die Liste kommt (noch) aus dem Geraetespeicher: der Abgleich laeuft oder
  // ist gescheitert (offline). Solange entscheidet nichts ueber sie, was
  // frische Daten braucht — Wiederherstellen nach F5, Status und
  // Zustaendigkeit beim Oeffnen, Verschieben/Ueberspringen, die
  // Uebertragungs-Abfrage. Der Ref ist sofort gesetzt, die Wartenden werden
  // erst nach dem Festschreiben geweckt (dann steht die frische Liste in
  // `projekteRef`).
  const [listeAusCache, setListeAusCacheState] = useState(false);
  const listeAusCacheRef = useRef(false);
  const setzeListeAusCache = (ausCache: boolean) => setzeMitRef(listeAusCacheRef, setListeAusCacheState, ausCache);
  const frischWartendeRef = useRef<Array<(frisch: boolean) => void>>([]);
  /** Alle Wartenden wecken — mit dem Ergebnis, ob die Liste jetzt abgeglichen ist. */
  const weckeFrischWartende = (frisch: boolean) => {
      const wartende = frischWartendeRef.current;
      frischWartendeRef.current = [];
      for (const wecken of wartende) wecken(frisch);
  };
  useEffect(() => {
      // Erst NACH dem Festschreiben: dann steht die frische Liste in `projekteRef`.
      if (!listeAusCache) weckeFrischWartende(true);
  }, [listeAusCache]);
  /**
   * Wartet, bis die Liste abgeglichen ist — hoechstens `ms`, und nur, solange
   * ein Abgleich laeuft (gescheitert, etwa offline: sofort bzw. mit seinem
   * Ende `false`). Liefert, ob sie es ist — und `false`, wenn inzwischen ein
   * anderer Nutzer angemeldet ist (Codex-Review 21.10.2026: sonst schriebe
   * eine wartende Aktion von A aus der Liste von B).
   */
  const warteAufFrischeListe = (ms: number): Promise<boolean> => {
      const runde = sitzungsRundeRef.current.runde;
      if (!listeAusCacheRef.current) return Promise.resolve(true);
      if (!listenAbgleichRef.current) return Promise.resolve(false);
      return new Promise<boolean>((resolve) => {
          let erledigt = false;
          const uhr = window.setTimeout(() => {
              if (erledigt) return;
              erledigt = true;
              resolve(false);
          }, ms);
          frischWartendeRef.current.push((frisch) => {
              if (erledigt) return;
              erledigt = true;
              window.clearTimeout(uhr);
              resolve(frisch && rundeGilt(runde, sitzungsRundeRef.current));
          });
      });
  };
  // Abruf-Marke je Eintrag-OBJEKT (projektListeDelta, logische Uhr): aus
  // welchem Abruf der Eintrag stammt. Ein Eintrag ohne Marke wurde hier
  // veraendert (Verschieben, eigenes Speichern: neues Objekt) und gilt als
  // LOKAL — der naechste Abgleich laedt ihn neu.
  const abrufMarkenRef = useRef<AbrufMarken>(new WeakMap());
  // Das Sichtungsbuch: welche Signalzeile mit welcher Marke zuerst gesehen
  // wurde, und der Zaehler der Uhr.
  const sichtungsbuchRef = useRef<Sichtungsbuch>(neuesSichtungsbuch());
  // Start-Marke des juengsten erfolgreichen Abgleichs bzw. Voll-Loads ueber die
  // ganze Liste (projektListeDelta.fuehreZusammen).
  const letzterGanzerAbgleichRef = useRef<number | null>(null);
  // Beginn (Geraetezeit, cloudService) → Marke der Voll-Loads, die
  // fetchProjects begonnen hat: wer sich einen laufenden teilt, uebernimmt
  // dessen Marke.
  const vollLoadMarkenRef = useRef<Map<number, number>>(new Map());
  // Juengstes Signal, das ueber den Kanal oder sein Nachziehen kam
  // (Serverzeit) — die Basis fuer das Nachziehen nach einem (Wieder-)Beitritt.
  const kanalWasserstandRef = useRef<string | null>(null);
  // Die Speicherung in die IndexedDB der laufenden Sitzung, und was sie
  // speichert: der zuletzt FESTGESCHRIEBENE Stand von Liste, Wasserstand und
  // Staenden (aus einem Effekt, nie aus einem halben Render).
  const cacheSpeicherungRef = useRef<CacheSpeicherung | null>(null);
  const cacheSchnappschussRef = useRef<{ eintraege: readonly Zeile[]; listenStand: ListenStand; ausCache: boolean } | null>(null);
  const zuletztGemerkterWasserstandRef = useRef<string | null>(null);
  // Was zuletzt im Geraet steht (gelesen oder geschrieben) — dieselben
  // Objekte: dann gibt es nichts zu schreiben (ein Kaltstart ohne Aenderung
  // kopiert nicht die ganze Liste zurueck).
  const cacheGeschriebenRef = useRef<{ eintraege: unknown; listenStand: unknown } | null>(null);
  // Der Start der Liste je Sitzungsrunde (Geraetespeicher, dann Abgleich) und
  // der laufende Abgleich oder Voll-Load — beide geteilt, nie doppelt.
  const listenStartRef = useRef<{ runde: number; laeuft: boolean; erfolgreich: boolean; bereit: Promise<void> } | null>(null);
  const listenAbgleichRef = useRef<{ runde: number; lauf: Promise<number | null> } | null>(null);
  // In welcher Runde die Liste zuletzt erfolgreich vom Server kam — dann
  // malt der Geraetespeicher nicht mehr darueber.
  const listeGeladenInRundeRef = useRef<number | null>(null);
  /** Alles zur Liste dieser Sitzung vergessen (Abmelden, Sitzungsentzug, Nutzerwechsel). */
  const vergissListenAbgleich = () => {
      // Wer noch auf die Liste der alten Sitzung wartet (Oeffnen, Verschieben),
      // erfaehrt jetzt ausdruecklich: nicht schreiben (Codex-Review
      // 21.10.2026). Erst danach faellt `listeAusCache` — der Effekt fande
      // sonst die alten Wartenden und weckte sie mit `true`.
      weckeFrischWartende(false);
      abrufMarkenRef.current = new WeakMap();
      sichtungsbuchRef.current = neuesSichtungsbuch();
      letzterGanzerAbgleichRef.current = null;
      vollLoadMarkenRef.current = new Map();
      kanalWasserstandRef.current = null;
      zuletztGemerkterWasserstandRef.current = null;
      cacheGeschriebenRef.current = null;
      listenStartRef.current = null;
      listenAbgleichRef.current = null;
      aendereListenStand(() => LEERER_LISTENSTAND);
      setzeListeAusCache(false);
      if (signalModusRef.current !== 'unbekannt') setzeSignalModus('unbekannt');
  };
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [processingTransferId, setProcessingTransferId] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<{ isOpen: boolean, data: Report | null, existingProject: any | null }>({ isOpen: false, data: null, existingProject: null });
  const [senderNames, setSenderNames] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState('items'); 
  const [report, setReport] = useState<Report | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isProjectReadOnly, setIsProjectReadOnlyState] = useState(false);

  // ─── Projektsperre („nur eine Person zugleich") ───────────────────────────
  //
  // Drei Zustaende, bewusst getrennt:
  //   * projectLock  — die geltende Sperrzeile des offenen Projekts (auch die
  //                    eigene). Quelle fuer den Namen im Balken.
  //   * lockBlocked  — WIR halten sie nicht. Nur dann steht der Sperrbalken da.
  //                    Aus isProjectReadOnly allein waere das nicht ablesbar:
  //                    diese Flagge kennt den Grund nicht.
  //   * heldLockRef  — welches Projekt wir tatsaechlich gesperrt haben. Ein Ref
  //                    und kein State, weil pagehide und die Aufraeumfunktionen
  //                    den Wert lesen, ohne dass ein Render dazwischenliegt.
  const [projectLock, setProjectLock] = useState<ProjectLock | null>(null);
  const [lockBlocked, setLockBlocked] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const heldLockRef = useRef<string | null>(null);

  /**
   * Meldung nach dem Leerlauf-Rueckzug: „Ihr Projekt wurde geschlossen, weil
   * eine halbe Stunde nichts passiert ist."
   *
   * Der Nutzer war zu diesem Zeitpunkt per Definition nicht da — er findet
   * beim Zurueckkommen eine andere Ansicht vor, als er verlassen hat. Ohne
   * diesen Satz sieht das nach einem Absturz aus.
   *
   * Derselbe Hinweis traegt auch den Ablauf einer Fremdfreigabe um
   * Mitternacht (FOREIGN_FREIGABE_ABGELAUFEN): wieder hat sich der Zustand ohne
   * Zutun des Nutzers geaendert, und wieder braucht das eine Erklaerung, keine
   * Warnung.
   */
  const [idleNotice, setIdleNotice] = useState<string | null>(null);

  /**
   * Restzeit bis zum Leerlauf-Rueckzug — gesetzt NUR waehrend der Vorwarnung.
   *
   * Ausserhalb der letzten zwei Minuten steht hier null, und genau deshalb
   * zeichnet die Anwendung im Normalbetrieb nichts neu. Die eigentliche Uhr
   * ist der Zeitstempel der letzten Eingabe; dieser Zustand ist nur ihr
   * sichtbares Ende.
   */
  const [idleCountdownMs, setIdleCountdownMs] = useState<number | null>(null);

  /**
   * Der Speicherstatus als Ref NEBEN dem State.
   *
   * Der Leerlauf-Rueckzug entscheidet aus einem Zeitgeber heraus und braucht
   * den Stand von JETZT, nicht den aus dem Render, in dem sein Callback
   * entstanden ist. Ihn in die Abhaengigkeiten des Herzschlag-Effekts zu
   * nehmen waere die Alternative gewesen — dann baute jeder Speichervorgang
   * Abo und Zeitgeber neu auf.
   */
  const saveStatusRef = useRef<SaveStatus>('idle');

  /**
   * Wann der Nutzer zuletzt etwas angefasst hat; null = noch nichts erfasst.
   *
   * Gemessen wird die EINGABE, nicht die Aenderung am Bericht — Begruendung
   * bei `shouldExitForIdle` in projectLockLogic. Ein Ref, weil kein Render
   * davon abhaengt: gelesen wird der Wert einzig beim Herzschlag.
   */
  const lastActivityAtRef = useRef<number | null>(null);

  /**
   * Das Projekt wurde AUSDRUECKLICH schreibgeschuetzt geoeffnet („Offene
   * Positionen") — im Unterschied zu einem Lesemodus, der nur von einer
   * fremden Sperre kommt.
   *
   * Frueher wurde das aus `isProjectReadOnly && !lockBlocked` erschlossen.
   * Das stimmt nicht mehr, seit der Rueckweg in die Projektansichten
   * vorsorglich in den Lesemodus geht, WAEHREND die Sperre angefordert wird:
   * in diesem Fenster ist lockBlocked noch false, und der Umkehrschluss
   * meldete faelschlich „schreibgeschuetzt geoeffnet" — mit dem gelben Balken
   * der Offenen Positionen und einem Eintrag im gemerkten Navigationszustand,
   * der das Projekt nach F5 dauerhaft im Lesemodus gehalten haette.
   */
  const [explicitReadOnly, setExplicitReadOnly] = useState(false);

  // ─── Fremdes Projekt: erst ansehen, dann begruenden (SQL Nr. 128) ─────────
  //
  // Der dritte Grund fuer den Lesemodus, neben ausdruecklichem Schreibschutz
  // und fremder Sperre: das Projekt gehoert jemand anderem, und fuer heute
  // liegt noch keine Begruendung vor. Beim Oeffnen wird dann die Sperre gar
  // nicht erst angefordert (sonst blockierte ein blosser Blick den, der
  // wirklich arbeitet) und ohne Selbstheilung geladen.
  //
  // Bewusst NICHT explicitReadOnly: die Flagge wird im Navigationszustand
  // gemerkt und hielte das Projekt nach F5 dauerhaft im Lesemodus, auch wenn
  // inzwischen eine Begruendung vorliegt. So fragt der Restore die Freigabe
  // frisch ab und entscheidet neu.
  //
  // Alles hier oben und nur aus Refs und Settern gebaut: die Rueckrufe werden
  // gemerkt und stecken im Kontext. Eine Closure ueber eine `const` unterhalb
  // der fruehen Returns waere genau die TDZ-Falle vom 13.09.2026.
  const [foreignPending, setForeignPendingState] = useState(false);
  /** Derselbe Wert fuer Rueckrufe, die laenger laufen als ein Render. */
  const foreignPendingRef = useRef(false);
  const setForeignPending = useCallback((value: boolean) => {
      foreignPendingRef.current = value;
      setForeignPendingState(value);
  }, []);
  const [foreignDialogOpen, setForeignDialogOpen] = useState(false);
  const [foreignBusy, setForeignBusy] = useState(false);
  const [foreignError, setForeignError] = useState<string | null>(null);
  /** Die offene Anfrage — alle Aufrufer waehrend eines Dialogs teilen sie. */
  const foreignRequestRef = useRef<{ promise: Promise<boolean>; resolve: (ok: boolean) => void } | null>(null);
  /**
   * Nach jedem Neuladen durch eine Begruendung hochgezaehlt. Die Modul-
   * ansichten haengen an diesem Schluessel und werden damit neu aufgebaut —
   * das Pruefmodul haelt seinen Bericht im eigenen State und saehe sonst
   * weiter den Stand von vor der Begruendung (die Uebernahme erreicht dasselbe
   * ueber den Vollbild-Ladeschirm). Der ProjektHub bleibt stehen: dort wartet
   * der Begehungsdialog auf das Ergebnis der Anfrage.
   */
  const [foreignReloadKey, setForeignReloadKey] = useState(0);
  /**
   * Die Freigabe, aus der der Schreibmodus des offenen Projekts stammt — beim
   * Oeffnen gefunden oder eben begruendet. null fuer eigene Projekte, Admins
   * und alles ohne Freigabe. Sie laeuft um Mitternacht ab; der Zeitgeber
   * weiter unten schaltet das Projekt dann in die Nur-Ansicht zurueck.
   */
  const [foreignGrant, setForeignGrant] = useState<ForeignAccessGrant | null>(null);

  /** Dialog schliessen und die wartenden Aufrufer mit dem Ergebnis bedienen. */
  const settleForeignRequest = useCallback((ok: boolean) => {
      const offen = foreignRequestRef.current;
      foreignRequestRef.current = null;
      setForeignDialogOpen(false);
      setForeignBusy(false);
      setForeignError(null);
      offen?.resolve(ok);
  }, []);

  /**
   * Begruendungsdialog oeffnen (Kontext `ForeignAccessContext`).
   *
   * Ausserhalb eines fremden Projekts sofort false — dann ist der Lesemodus
   * ein anderer und hat seine eigene Absage. Ein zweiter Aufruf waehrend eines
   * offenen Dialogs bekommt dieselbe Zusage statt eines zweiten Dialogs.
   */
  const requestJustification = useCallback((): Promise<boolean> => {
      if (!foreignPendingRef.current) return Promise.resolve(false);
      if (foreignRequestRef.current) return foreignRequestRef.current.promise;
      let resolve: (ok: boolean) => void = () => {};
      const promise = new Promise<boolean>((r) => { resolve = r; });
      foreignRequestRef.current = { promise, resolve };
      setForeignError(null);
      setForeignDialogOpen(true);
      return promise;
  }, []);

  /** Projekt verlassen: kein fremder Zustand und kein offener Dialog bleiben stehen. */
  const resetForeignAccess = useCallback(() => {
      setForeignPending(false);
      setForeignGrant(null);
      settleForeignRequest(false);
  }, [setForeignPending, settleForeignRequest]);

  const foreignAccessContext = useMemo<ForeignAccessContextValue>(
      () => ({ pending: foreignPending, requestJustification }),
      [foreignPending, requestJustification],
  );

  /**
   * Laeuft gerade ein Sperr-Vorgang (Anfordern beim Oeffnen oder beim
   * Rueckweg)? Verhindert zweierlei: zwei gleichzeitige Anfragen — und dass
   * die Anwesenheits-Entscheidung waehrend eines Projektwechsels die Sperre
   * des eben verlassenen Projekts neu anfordert.
   */
  const lockOpRef = useRef(false);

  /** Das offene Projekt fuer asynchrone Fortsetzungen (Stand nach dem await). */
  const currentProjectIdRef = useRef<string | null>(null);
  useEffect(() => { currentProjectIdRef.current = currentProjectId; }, [currentProjectId]);

  /**
   * Anzeigename fuer die Sperrzeile. Vor-/Nachname, ersatzweise die E-Mail —
   * „Wird gerade von peter@… bearbeitet" ist immer noch brauchbarer als eine
   * UUID oder gar nichts.
   */
  const lockLabel = useMemo(() => {
      const name = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ').trim();
      return name || userProfile?.email || null;
  }, [userProfile]);

  /**
   * Wer blockiert gerade — oder niemand?
   *
   * Die Absage-Dialoge (readOnlyMessage/readOnlyTitle) nannten im Sperrfall den
   * falschen Grund: „schreibgeschuetzt geoeffnet ... zum Bearbeiten ueber die
   * Projektliste oeffnen" stimmt nur fuer den Weg aus den Offenen Positionen.
   * Wer von einer fremden Sperre geblockt ist, hat das Projekt ganz normal
   * geoeffnet, und ein zweiter Anlauf ueber die Projektliste aendert nichts.
   * Mit einem Namen hier formuliert readOnlyNotice den Sperrfall.
   */
  const readOnlyHolder = (): string | null => (lockBlocked ? lockHolderName(projectLock) : null);

  /**
   * Firmen- und Profildaten in den geladenen Bericht einsetzen.
   *
   * Herausgeloest aus handleOpenProject, weil inzwischen DREI Wege das
   * Projekt frisch laden und dabei genau dieselbe Angleichung brauchen:
   * das Oeffnen, die Uebernahme einer Sperre und der Rueckweg in die
   * Projektansichten. Drei Kopien davon waeren drei Orte, an denen kuenftig
   * eine Einstellung fehlt.
   */
  const withProfileSettings = useCallback((reportData: Report): Report => {
      if (!userProfile) return reportData;
      return {
          ...reportData,
          settings: {
              ...reportData.settings,
              companyName: userProfile.company_name || reportData.settings?.companyName || '',
              companyAddress: userProfile.company_address || reportData.settings?.companyAddress || '',
              companyLogo: userProfile.company_logo || reportData.settings?.companyLogo || '',
              appMode: userProfile.app_mode || reportData.settings?.appMode || 'sigeko',
              emailSignatureTemplate: userProfile.settings?.emailSignatureTemplate || reportData.settings?.emailSignatureTemplate || '',
              // Bauart-Liste und Kategorienkatalog kamen bisher NIE hier an:
              // die Stammdaten lasen report.settings.constructionTypes, das
              // Profil schrieb aber nur in profiles.settings — das Feld
              // „Bauart" blieb im Projekt unsichtbar, egal was der Firmen-
              // bereich pflegte.
              // Firmenweit (companyProjectCatalog.ts): der Block des Admins
              // gilt fuer alle Mitarbeiter, nicht nur fuer sein eigenes Profil.
              constructionTypes: companyProjectCatalog?.constructionTypes ?? userProfile.settings?.constructionTypes ?? reportData.settings?.constructionTypes,
              projectCategories: companyProjectCatalog?.projectCategories ?? userProfile.settings?.projectCategories ?? reportData.settings?.projectCategories,
              // Die zentral gepflegte PDF-Fusszeile erreichte den
              // Pruefbericht bisher nicht — der Lesezweig im Generator
              // stand ins Leere. ?? statt ||, damit ein bewusst
              // geleertes Feld ('') leer bleibt (resolveFooterText).
              footerText: (userProfile as any)?.settings?.footerText
                  ?? reportData.settings?.footerText,
          }
      };
  }, [userProfile, companyProjectCatalog]);

  /**
   * Gibt die selbst gehaltene Sperre frei — einmal, und ohne auf das Ergebnis
   * zu warten.
   *
   * Der Ref wird VOR dem Aufruf geleert: sonst gaebe ein zweiter Weg aus dem
   * Projekt (Zurueck-Knopf und gleich darauf pagehide) den RPC doppelt ab.
   * Scheitert er, ist nichts verloren — die Sperre verfaellt nach
   * LOCK_STALE_MS von selbst.
   */
  const releaseHeldLock = () => {
      const id = heldLockRef.current;
      if (!id) return;
      heldLockRef.current = null;
      void releaseProjectLock(id);
  };

  // Mobile Quick Select State
  const [showMobileProjectSelector, setShowMobileProjectSelector] = useState(false);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  // „+" mit Standortsuche (services/plusStandort): das Blatt ueber der Leiste,
  // der Satz ueber der Projektauswahl, wenn es dorthin zurueckfaellt, und die
  // Nummer der laufenden Suche — eine abgebrochene darf nichts mehr oeffnen.
  const [plusBlatt, setPlusBlatt] = useState<PlusBlatt<ListProject> | null>(null);
  const [plusHinweis, setPlusHinweis] = useState<string | null>(null);
  const plusSucheRef = useRef(0);
  // Im Blatt: das Projekt, das gerade geoeffnet wird (Knoepfe gesperrt), und
  // der Satz, warum nicht gewechselt wird (offene Maske).
  const [plusOeffnet, setPlusOeffnet] = useState<string | null>(null);
  const [plusBlattHinweis, setPlusBlattHinweis] = useState<string | null>(null);
  
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearSaveStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Bricht den wartenden Autosave ab (2-s-Debounce aus handleUpdateReport).
   *
   * Der Timer lief bisher jedem Zustandswechsel davon: wer beim Tippen die
   * Sperre verlor oder in den Lesemodus geriet, hatte zwei Sekunden spaeter
   * trotzdem einen Flush am Hals — genau den Stand, den er gerade nicht mehr
   * schreiben darf.
   */
  const cancelPendingAutosave = useCallback(() => {
      if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
      }
  }, []);

  /**
   * Der Lesemodus als Ref NEBEN dem State.
   *
   * Der Torwaechter der Offline-Queue laeuft aus Zeitgebern und Ereignissen
   * heraus, nicht aus dem Render — er braucht den Wert von JETZT, nicht den
   * aus dem Render, in dem sein Callback entstanden ist. Und weil beide immer
   * zusammen gesetzt werden muessen, gibt es nur diesen einen Weg dorthin.
   */
  const readOnlyRef = useRef(false);
  const setProjectReadOnly = useCallback((value: boolean) => {
      readOnlyRef.current = value;
      if (value) cancelPendingAutosave();
      setIsProjectReadOnlyState(value);
  }, [cancelPendingAutosave]);
  
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHolidayPlannerOpen, setIsHolidayPlannerOpen] = useState(false);
  // Name des beendeten Projekts, das ein Mitarbeiter öffnen wollte
  // (projectOpenGate) — solange gesetzt, steht der Hinweis.
  const [gesperrtesProjekt, setGesperrtesProjekt] = useState<string | null>(null);
  // Klartext nach einer Absage wegen Rechten (Rollensystem): fremdes Projekt
  // nicht freigegeben, Projekttyp darf nicht angelegt werden. Solange gesetzt,
  // steht der Hinweis — wie beim beendeten Projekt.
  const [zugriffHinweis, setZugriffHinweis] = useState<{ titel: string; text: string } | null>(null);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [holidayRange, setHolidayRange] = useState<{start?: string, end?: string}>({});
  const [pendingStatusChange, setPendingStatusChange] = useState<{ targetModule: any, currentStatus: string } | null>(null);
  const realtimeChannelRef = useRef<any>(null);
  // Hochgezaehlt, wenn der Kanal ohne Nutzerwechsel neu aufgebaut werden
  // muss (gescheitertes Abmelden, siehe handleLogout).
  const [kanalRunde, setKanalRunde] = useState(0);

  // ─── Drei Merkposten fuer den Sitzungsentzug (sessionRevocation.ts) ───────
  //
  // Alle drei als Ref und HIER oben: der Auth-Effekt darunter haengt an `[]`
  // und liest sie aus einer Closure, die den ersten Render festhaelt — ein
  // State-Wert waere dort fuer immer der vom Start. Refs stehen vor den
  // fruehen Returns und werden von keinem Render-Wert geschlossen (siehe die
  // TDZ-Falle vom 13.09.2026).
  /** Hatte dieser Tab in diesem Lauf schon eine Sitzung? */
  const hatteSessionRef = useRef(false);
  /** Laeuft in diesem Tab gerade ein Passwort-Zuruecksetzen? */
  const passwortWiederherstellungRef = useRef(false);
  /**
   * Fuer wen Profil und Projektliste geladen sind oder gerade laden
   * (authRefreshPolicy.entscheideAuthLaden). Dieselbe Person, die aus der
   * Kamera zurueckkommt, laedt damit nichts mehr; ein anderer Nutzer alles.
   */
  const profilGeladenFuerRef = useRef<string | null>(null);
  /** Das zuletzt geladene Profil — fuer recordAppStart bei der Rueckkehr. */
  const geladenesProfilRef = useRef<UserProfile | null>(null);
  /**
   * Die Sitzungsrunde (authRefreshPolicy): hochgezaehlt bei jedem Wechsel
   * des angemeldeten Nutzers (auch nach null). fetchProfile und
   * fetchProjects bekommen die Runde, FUER die sie laden, und schreiben nach
   * einem `await` nur, wenn sie noch gilt — sonst landete die spaete Antwort
   * fuer A in der Sitzung von B (Codex-Review 21.10.2026).
   */
  const sitzungsRundeRef = useRef(ERSTE_SITZUNGSRUNDE);
  /**
   * Zugang zur Beschaeftigungs-Auskunft der Offline-Warteschlange.
   *
   * Der Hook, der sie liefert, wird erst weit unter diesem Effekt aufgerufen.
   * Ihn hier direkt zu schliessen waere genau der Bootcrash vom 13.09.2026
   * (Effekt oben, `const` unten, TDZ). Der Ref ist zum Zeitpunkt des
   * Sitzungsentzugs laengst gefuellt; bis dahin antwortet er "nicht
   * beschaeftigt", und das ist die sichere Antwort (dann wird sofort geladen).
   */
  const queueBeschaeftigtRef = useRef<() => boolean>(() => false);

  /**
   * Zugang zu `useOfflineSync.lokalenEntwurfHolen` — aus demselben Grund ein
   * Ref: der Rueckweg nach einer Sperre (`reacquireProjectLock`) steht weit
   * ueber dem Hook. Bis der Ref gefuellt ist, gilt „kein Entwurf", also der
   * Serverstand; gebraucht wird er ohnehin erst nach dem ersten Render.
   */
  const lokalenEntwurfHolenRef = useRef<(projectId: string) => Promise<QueueEntry | undefined>>(async () => undefined);

  useEffect(() => {
    // Der erste Sitzungsabruf kann NACH dem Abbau dieses Effekts auflaufen
    // (StrictMode montiert im Entwicklungsmodus zweimal) oder sogar nach einem
    // zwischenzeitlichen SIGNED_OUT. Beides darf die Sitzung nicht wieder
    // aufleben lassen und "hatte Sitzung" nicht nachtraeglich setzen.
    let abgebrochen = false;

    // Was ein Ereignis laedt, entscheidet authRefreshPolicy. auth-js meldet
    // jede Rueckkehr in den Tab als SIGNED_IN — fuer dieselbe Person wird
    // dann nichts geladen; die Liste haelt der Realtime-Sammler und das
    // Nachholen weiter unten aktuell ("Projektliste aktuell halten").
    const neueRundeFuer = (nutzerId: string | null) => {
      sitzungsRundeRef.current = naechsteSitzungsRunde(sitzungsRundeRef.current, nutzerId);
    };

    const ladeZurSitzung = (event: string, nutzerId: string) => {
      neueRundeFuer(nutzerId);
      const art = entscheideAuthLaden({ event, sitzungNutzerId: nutzerId, geladenFuer: profilGeladenFuerRef.current });
      if (art === 'voll') {
        profilGeladenFuerRef.current = nutzerId;
        // Die Projektliste startet HIER, nicht erst nach dem Profil (Nr. 175):
        // erst aus dem Geraet, dann der Abgleich (starteListe). Scheitert das
        // Profil, steht die Liste trotzdem — wie frueher der eigene Effekt
        // dafuer. Was sich der Abgleich gemerkt hat, gilt nur fuer DIESE
        // Runde (dieselbe Person behaelt sie; ein zweiter Versuch nach einem
        // gescheiterten Profil wirft nichts weg). fetchProfile wartet auf
        // denselben Start.
        const runde = sitzungsRundeRef.current.runde;
        if (listenStartRef.current?.runde !== runde) vergissListenAbgleich();
        void starteListe(nutzerId, runde);
        void fetchProfile(nutzerId).then((ok) => {
          // Gescheitert (z. B. offline gestartet): das naechste Ereignis darf
          // es erneut versuchen, wie bisher jede Rueckkehr.
          if (!ok && profilGeladenFuerRef.current === nutzerId) profilGeladenFuerRef.current = null;
        });
      } else if (art === 'profil') {
        void fetchProfile(nutzerId, false);
      } else {
        // recordAppStart lief bisher bei jeder Rueckkehr mit (es haengt an
        // fetchProfile): Startmarke je Sitzung, liegengebliebene Schliessen
        // anderer Sitzungen nachtragen. Das bleibt so — ohne Netz, solange
        // nichts liegt. Erst wenn das Profil steht, wie bisher.
        const profil = geladenesProfilRef.current;
        if (profil?.id === nutzerId) recordAppStart(profil);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (abgebrochen) return;
      setSession(session);
      if (session) { hatteSessionRef.current = true; ladeZurSitzung('INITIAL_SESSION', session.user.id); }
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
          passwortWiederherstellungRef.current = true;
      }

      // Die Sitzung immer uebernehmen (frisches Token, aktueller Nutzer);
      // die Effekte haengen an `sitzungsNutzerId` und laufen davon nicht neu.
      setSession(session);
      if (session) {
          hatteSessionRef.current = true;
          // Eine neue Sitzung entschaerft ein liegengebliebenes Merkzeichen:
          // scheiterte das `signOut` einer frueheren Abmeldung, bliebe die
          // Flagge sonst fuer die Lebensdauer des Tabs stehen — und der
          // naechste ECHTE Entzug liefe als "war ja gewollt" durch.
          vergissLokaleAbmeldung();
          ladeZurSitzung(event, session.user.id);
      }
      else {
          // Die naechste Anmeldung laedt wieder alles — auch dieselbe Person.
          // Was fuer die alte Sitzung noch unterwegs ist, schreibt nichts mehr.
          neueRundeFuer(null);
          profilGeladenFuerRef.current = null;
          geladenesProfilRef.current = null;
          // Laufender Projekt-Load des ausgeloggten Users darf nicht in die
          // nächste Session durchsickern.
          invalidateProjectsLoadCache();
          // Die Projektliste im Geraet gehoert der alten Sitzung (Nr. 175):
          // weg damit, und alles, was der Abgleich sich gemerkt hat. Die Runde
          // ist oben schon weitergezaehlt — eine noch geplante Speicherung
          // schreibt nichts mehr.
          vergissListenAbgleich();
          const listeGeleert = projektListeSpeicher.alleLoeschen();
          // Die Sitzung endet hier OHNE handleLogout (Token abgelaufen,
          // Abmeldung in einem anderen Tab). Ohne Token kommt die Zeile
          // vermutlich nicht mehr durch die RLS-Pruefung — geschrieben wird
          // sie trotzdem, denn der eigentliche Zweck ist das Zuruecksetzen
          // der Wache: meldet sich als naechstes jemand anderes in diesem Tab
          // an, haenge sein erstes Oeffnen sonst ein `switch`-Schliessen des
          // fremden Projekts an SEINE user_id. Die Projekt-ID kommt aus der
          // Wache, nicht aus dem State — diese Closure haelt den Stand des
          // ersten Renderns.
          recordProjectClose({ reason: 'logout' });
          setLoading(false);
          setUserProfile(null);
          setCompanyInvoiceConfig(undefined);
          setCompanyProjectCatalog(undefined);
          clearCompanyProjectCatalog();
          leereMangelKatalog();
          leereSigePlanVorlagen();

          // ─── Widerruf von aussen: neu laden ──────────────────────────────
          //
          // Bis hierher ist alles abgeraeumt, was die alte Sitzung angeht —
          // der Reload kommt bewusst ZULETZT, damit `recordProjectClose` sein
          // Merkzeichen noch leeren konnte.
          //
          // Warum ueberhaupt neu laden: ohne das endet dieser Zweig bei der
          // Anmeldemaske im ALTEN Bundle. Genau so ist am 07.09.2026 ein seit
          // dem 19.08. offener Tab weitergelaufen und hat alte Zaehler
          // zurueckgeschrieben. Die Regel steht vollstaendig in
          // sessionRevocation.ts; hier wird nur verdrahtet.
          const entscheidung = decideOnSignedOut({
              event,
              hadSession: hatteSessionRef.current,
              localLogout: istLokaleAbmeldung(),
              passwordRecovery: passwortWiederherstellungRef.current,
              // Speicher DIESES Tabs (sessionStorage, ueber den abgesicherten
              // Helfer): der Schleifenschutz darf die uebrigen Tabs nicht
              // mit aussperren, und schon der Getter kann werfen.
              storage: tabSpeicher(),
              nowMs: Date.now(),
          });
          // Beide Merkposten sind verbraucht: die naechste Sitzung setzt sie
          // neu. Ohne das Zuruecksetzen wuerde ein zweites SIGNED_OUT ohne
          // zwischenzeitliche Anmeldung erneut als Entzug gelten.
          vergissLokaleAbmeldung();
          hatteSessionRef.current = false;
          if (entscheidung === 'reload') {
              // Gebunden warten, bis die Warteschlange ihre IndexedDB-
              // Transaktion abgesetzt hat (Begruendung bei `warteAufRuhe`),
              // dann laden. Der Rueckgabewert interessiert nicht — geladen
              // wird in beiden Faellen.
              // Auch ein laufendes Angebots-Speichern abwarten: der Reload
              // bricht den Request sonst ab (der Editor selbst ist mit
              // `session === null` bereits abgebaut).
              // Ebenso das Leeren der Projektliste im Geraet (hoechstens drei
              // Sekunden, es wirft nie).
              void Promise.all([
                  warteAufRuhe(() => queueBeschaeftigtRef.current() || getQuoteEditorBusy().saving),
                  listeGeleert,
              ]).then(() => {
                  // Das Merkzeichen ERST hier: `session` steht schon auf null,
                  // die Anmeldemaske kann waehrend der Wartezeit montieren und
                  // haette den Hinweis sonst verbraucht, bevor der Reload ihn
                  // ueberhaupt zeigen kann.
                  merkeSitzungsende(tabSpeicher());
                  window.location.reload();
              });
          }
      }
    });

    return () => { abgebrochen = true; subscription.unsubscribe(); };
  }, []);

  // Holt Sendebericht/Buchung/Archiv nach, falls die App beim letzten
  // Berichtsversand vor Abschluss der Speicherung beendet wurde
  // (Pending-Send-Marker aus dem EmailModal).
  // Den Buchhaltungs-Fokus fallen lassen, sobald die Rechnungen verlassen
  // werden. Sonst führt der nächste Aufruf über die Navigation wieder in die
  // Buchhaltung des zuletzt gesprungenen Projekts statt in die Belegliste.
  useEffect(() => {
      if (view !== 'invoices') {
          setAccountingFocusId(null);
          setInvoiceFocusId(null);
      }
      if (view !== 'quotes') setQuoteFocusId(null);
      // Kartensprung in die Kundenakte: ebenso nur EINMAL. Blieb die Id
      // stehen, oeffnete jeder spaetere Aufruf von „Kunden" wieder diesen
      // Kunden — und CrmView koennte einen frischen Kartensprung nicht von
      // einem alten unterscheiden (Rueckkehr aus einem Beleg, crm.returnTo).
      if (view !== 'crm') setCrmFocusCustomerId(null);
  }, [view]);

  // An der NUTZERKENNUNG, nicht am Sitzungsobjekt: `session` bekommt bei jedem
  // Token-Refresh eine neue Objektidentitaet (und Supabase refresht regelmaessig
  // sowie beim Zurueckkommen aus dem Standby). Der Effekt startete das
  // Nachholen dadurch mitten im laufenden Durchgang erneut — in Projekt 6110
  // buchte das Bericht #25 dreimal innerhalb einer halben Sekunde, 52 Minuten
  // nach dem Versand. Der Riegel in resumePendingSendPersistence faengt den
  // Fall inzwischen selbst ab; hier entsteht er gar nicht erst.
  const resumeUserId = session?.user?.id ?? null;
  useEffect(() => {
      if (!resumeUserId) return;
      resumePendingSendPersistence({ loadProjectById, saveProjectToCloud, updateProjectExportStatus, saveReportSnapshot, logEmailAttempt })
          .then(count => { if (count > 0) fetchProjects(true); })
          .catch(e => console.error('Nachholen ausstehender Versand-Daten fehlgeschlagen', e));
  }, [resumeUserId]);

  // Der Ausgangskorb der Zweitschrift (journalMirrorOutbox.ts): Vormerkungen
  // und faellige Journalzeilen, die eine fruehere Sitzung nicht mehr
  // geschrieben hat. Dieselben Ausloeser wie die Offline-Warteschlange —
  // Start, online, Tab sichtbar — plus ein Takt, falls alles drei ausbleibt.
  // Laeuft nie parallel zu sich selbst und schluckt jeden Fehler.
  useEffect(() => {
      if (!resumeUserId) return;
      const nachholen = () => { void retryJournalMirrors(resumeUserId).catch(() => undefined); };
      const beiSichtbar = () => { if (document.visibilityState === 'visible') nachholen(); };
      nachholen();
      window.addEventListener('online', nachholen);
      document.addEventListener('visibilitychange', beiSichtbar);
      const takt = window.setInterval(nachholen, 5 * 60 * 1000);
      return () => {
          window.removeEventListener('online', nachholen);
          document.removeEventListener('visibilitychange', beiSichtbar);
          window.clearInterval(takt);
      };
  }, [resumeUserId]);

  // Die Buchungsarten des geoeffneten Projekts (service_journal.kind) EINMAL
  // ins Register holen. Danach entscheidet ueber jede Protokollzeile die
  // Journal-Spalte statt Beleg und Text (serviceLogUtils.isPostingEntry) —
  // ohne dass das Projekt-JSON angefasst wuerde: `kind` gelangt nie in den
  // Blob. Ist das Journal unbekannt (Tabelle fehlt, offline, keine
  // Berechtigung), bleibt das Register unberuehrt, es gilt weiter der
  // Praefix- und Textrueckfall — und der Versuch wird EINMAL gemeldet. Ein
  // stiller Rueckfall waere die schlechtere Lage: niemand saehe, dass die
  // Entscheidung gerade wieder aus Beleg und Text kommt.
  //
  // Solange nichts geladen ist, wird nachgefasst: online und beim
  // Zurueckkommen auf den Tab — dieselben Ausloeser wie beim Ausgangskorb
  // oben, ohne eigenen Takt, weil hier nichts faellig ist.
  //
  // Der Effekt steht hier oben bei den uebrigen Sitzungseffekten und NICHT
  // weiter unten: er darf auf keinen Wert zugreifen, der erst spaeter im
  // Rumpf deklariert wird, und muss vor jedem fruehen Return stehen (der
  // Bootcrash vom 13.09.2026).
  useEffect(() => {
      if (!currentProjectId) return;
      const projektId = currentProjectId;
      let aktiv = true;
      let geladen = false;
      let laeuft = false;
      let gemeldet = false;
      const holen = () => {
          if (!aktiv || geladen || laeuft) return;
          laeuft = true;
          void ladeBuchungsartenInsRegister(projektId)
              .then(anzahl => {
                  // Spaet aufgeloest, Projekt laengst geschlossen: der gerade
                  // geschriebene Stand gehoert niemandem mehr und bliebe sonst
                  // bis zur Abmeldung liegen.
                  //
                  // Gefragt wird der REF und nicht `aktiv`: bei A → B → A ist
                  // dieser Durchgang zwar abgeloest, das Projekt aber wieder
                  // offen — und der zweite Durchgang hat seinen Stand schon
                  // geladen. Blind zu vergessen risse ihn weg, und weil dort
                  // `geladen` steht, holte ihn niemand zurueck.
                  if (!aktiv) {
                      if (currentProjectIdRef.current !== projektId) vergissBuchungsarten(projektId);
                      return;
                  }
                  if (anzahl === null) {
                      if (!gemeldet) {
                          gemeldet = true;
                          console.warn(`[App] Buchungsarten nicht geladen — Praefix-Rueckfall (${projektId})`);
                      }
                      return;
                  }
                  geladen = true;
              })
              .catch(() => undefined)
              .finally(() => { laeuft = false; });
      };
      const beiSichtbar = () => { if (document.visibilityState === 'visible') holen(); };
      holen();
      window.addEventListener('online', holen);
      document.addEventListener('visibilitychange', beiSichtbar);
      return () => {
          aktiv = false;
          window.removeEventListener('online', holen);
          document.removeEventListener('visibilitychange', beiSichtbar);
          vergissBuchungsarten(projektId);
      };
  }, [currentProjectId]);

  // Der Realtime-Kanal `global-app-changes` steht bei „Projektliste aktuell
  // halten" (nach fetchProjects) — er haengt nur noch an der Nutzerkennung.

  // Beim Aufwachen des Tabs sofort frisches Token an Realtime durchreichen.
  useEffect(() => attachWakeAuthRefresh(supabase, document), []);

  // ─── Anwesenheit im Projekt ───────────────────────────────────────────────
  //
  // Die Sperre haengt daran, ob eine PROJEKTANSICHT auf dem Schirm steht —
  // nicht daran, ob `currentProjectId` gesetzt ist. Wer ueber die Seitenleiste
  // in die Rechnungen oder die Projektliste wechselt, laesst das Projekt
  // geladen zurueck; frueher lief der Herzschlag dort munter weiter und ein
  // „geparktes" Projekt blockierte die Kollegen unbegrenzt.
  //
  // `isProjectView` ist dieselbe Quelle, aus der auch navigateToView seine
  // Unterscheidung zieht (VIEW_KIND in navigationPersistence) — inklusive
  // Archiv und Modulen. Eine eigene Liste hier waere die zweite, die beim
  // naechsten neuen View still veraltet.
  const inProjectView = isProjectView(view);

  // Wann die Projektansichten verlassen wurden; null = wir sind drin. Ein Ref,
  // weil die Karenzzeit nur beim naechsten Herzschlag geprueft wird und kein
  // Render sie antreiben muss. Dieser Effekt steht VOR dem Anwesenheits-
  // Effekt: beide laufen im selben Commit, und der Zeitpunkt muss vorher
  // stehen.
  const leftProjectViewAtRef = useRef<number | null>(null);
  useEffect(() => {
      if (inProjectView) leftProjectViewAtRef.current = null;
      else if (leftProjectViewAtRef.current === null) leftProjectViewAtRef.current = Date.now();
  }, [inProjectView]);

  // ─── Leerlauf: wann war der Nutzer zuletzt da? ────────────────────────────
  //
  // Der Feierabend-Fall, gegen den das hier steht: jemand laesst das Projekt
  // offen stehen und geht nach Hause. Herzschlag und Sperre laufen weiter, und
  // die Kollegen kommen bis zum naechsten Morgen nicht hinein. Gemessen wird
  // deshalb der MENSCH, nicht der Tab.
  //
  // Am `window` und in der Erfassungsphase (`capture`), damit auch Ereignisse
  // in Scroll-Containern und hinter einem `stopPropagation` ankommen; es geht
  // um „ist jemand da", nicht darum, wohin geklickt wurde. `passive`, damit
  // dieser Zuhoerer niemals das Scrollen ausbremst.
  useEffect(() => {
      const mark = () => { lastActivityAtRef.current = Date.now(); };
      // 'focus'/'blur' sind der Umweg fuer eingebettete Inhalte: ein Klick in
      // eine PDF-Vorschau landet im iframe und erreicht dieses Fenster nie —
      // wohl aber der Fokuswechsel dorthin. Ohne ihn faele heraus, wer eine
      // halbe Stunde in einem grossen SiGe-Plan blaettert.
      const events = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll', 'focus', 'blur'];
      events.forEach((name) => window.addEventListener(name, mark, { capture: true, passive: true }));
      return () => events.forEach((name) => window.removeEventListener(name, mark, { capture: true } as any));
  }, []);

  // Ein frisch geoeffnetes Projekt bekommt die volle Frist: das Oeffnen selbst
  // ist die Eingabe. Ohne diese Zeile traegt ein nach F5 wiederhergestelltes
  // Projekt den Zeitstempel von vor dem Neuladen — und faele beim ersten
  // Herzschlag heraus, obwohl gerade jemand F5 gedrueckt hat.
  useEffect(() => { if (currentProjectId) lastActivityAtRef.current = Date.now(); }, [currentProjectId]);

  // Den Speicherstatus fuer die Zeitgeber mitfuehren (Begruendung am Ref).
  useEffect(() => { saveStatusRef.current = saveStatus; }, [saveStatus]);

  /**
   * Sperre auf dem Rueckweg in die Projektansichten neu anfordern.
   *
   * Der Lesemodus wird VORHER gesetzt und erst nach einem klaren Ja wieder
   * abgelegt. Andersherum — erst weiterarbeiten lassen, dann fragen — landete
   * der Rueckkehrer stillschweigend im Bearbeitungsmodus, waehrend ein anderer
   * die Sperre haelt; sein erster Autosave ueberschriebe dann dessen Arbeit,
   * denn einen Konflikt-Anker gibt es nicht mehr. Ein bis vier Sekunden
   * Lesemodus zuviel sind dagegen folgenlos.
   */
  const reacquireProjectLock = useCallback(async (projectId: string) => {
      if (lockOpRef.current) return;
      lockOpRef.current = true;
      setProjectReadOnly(true);
      try {
          const res = await acquireProjectLockWithin(projectId, lockLabel);
          if (currentProjectIdRef.current !== projectId) {
              // Waehrend des Wartens gewechselt oder geschlossen: eine hier
              // genommene Sperre gehoert niemandem mehr und muss sofort weg.
              if (res.acquired) void releaseProjectLock(projectId);
              return;
          }
          const blocked = !res.acquired && isLockHeldByOther(res.lock, lockUserId, Date.now());
          setProjectLock(res.lock);
          setLockBlocked(blocked);
          if (blocked) return;
          heldLockRef.current = projectId;

          // ─── Der Stand im Speicher ist alt ────────────────────────────────
          //
          // Wer in die Rechnungen wechselt und nach der Karenzzeit zurueckkommt,
          // hat die Sperre zwischendurch abgegeben. In diesem Fenster kann ein
          // anderer das Projekt bearbeitet und wieder verlassen haben — die
          // Sperre ist jetzt frei, `report` aber minutenalt. Ohne Neuladen
          // ueberschriebe die erste Eingabe dessen Maengel, denn einen
          // Konflikt-Anker gibt es nicht mehr. Der Rueckweg muss sich deshalb
          // wie die Uebernahme verhalten und IMMER neu laden: „inzwischen hat
          // niemand geschrieben" laesst sich ohne Anker nicht belegen.
          //
          // Eigene, noch nicht hochgeladene Aenderungen liegen in der
          // Offline-Queue (Netzausfall, Flush noch nicht durch). Bis zum
          // 08.09.2026 blieb dann der lokale Stand stehen und der Server wurde
          // gar nicht erst geladen — alles, was seit dem Einreihen dort
          // passiert war, blieb unsichtbar (dieselbe Falle wie beim Oeffnen,
          // Meyer 26051016). Jetzt wie beim Oeffnen: Server zuerst, Entwurf
          // obendrauf (mergeReports mit dem Ausgangsstand des Eintrags, ohne
          // Ausgangsstand nur die Bergung). Der Eintrag selbst bleibt liegen
          // und geht mit dem naechsten Autosave bzw. dem 45-s-Intervall der
          // Queue durch den Torwaechter raus (wir halten die Sperre).
          // Ein vom Altersriegel angehaltener Eintrag zaehlt nicht: der geht
          // nie mehr raus und darf den Serverstand nicht verdraengen
          // (07.09.2026). Ebenso wenig einer, der zu alt IST, aber noch nicht
          // angehalten wurde — der wird dabei angehalten und gemeldet (Luecke
          // O1, 21.10.2026; useOfflineSync.lokalenEntwurfHolen).
          const queued = await lokalenEntwurfHolenRef.current(projectId);
          const fresh = await loadProjectById(projectId, undefined, { skipMigration: false })
              .catch((e: any) => { console.warn('[projectLock] Neuladen beim Rueckweg fehlgeschlagen:', e); return null; });
          // Waehrend des Ladens gewechselt: der Bericht des anderen
          // Projekts darf hier nicht in den State — und die eben genommene
          // Sperre muss weg. Bliebe heldLockRef auf dem verlassenen
          // Projekt stehen, lieferte decideLockPresence fuer das neue
          // dauerhaft 'idle' (Zweig „Wechsel laeuft"): kein Herzschlag,
          // keine Sperre, und das alte Projekt bliebe drei Minuten blockiert.
          if (currentProjectIdRef.current !== projectId) {
              if (heldLockRef.current === projectId) releaseHeldLock();
              return;
          }
          if (fresh?.data) {
              const server = fresh.data as Report;
              let anzeige = server;
              if (queued) {
                  const entwurf = entwurfAufServerstand(queued, server);
                  anzeige = entwurf.merged;
                  if (entwurf.conflicts.length > 0 || entwurf.keptForeignLogEntries > 0 || entwurf.uebernommeneServerZeilen > 0) {
                      console.info('[projectLock] Entwurf auf den Serverstand gelegt (Rueckweg)', projectId, {
                          mode: entwurf.mode,
                          conflicts: entwurf.conflicts.length,
                          kept: entwurf.keptForeignLogEntries,
                          removed: entwurf.removedLogEntries,
                          serverZeilen: entwurf.uebernommeneServerZeilen,
                      });
                  }
              }
              setReport(withProfileSettings(anzeige));
              // Der Ausgangsstand ist der SERVERSTAND, nie der Entwurf — wie
              // beim Oeffnen.
              projectBaseRef.current = {
                  projectId,
                  report: server,
                  updatedAt: fresh.updated_at ?? null,
              };
          }
          // Kein frischer Stand (offline, Ladefehler): fail-open wie
          // ueberall bei der Sperre. Wir halten sie, also darf niemand
          // sonst schreiben; jemanden am eigenen Projekt arbeiten zu
          // lassen wiegt schwerer als das Restrisiko eines alten Stands.
          // Der lokale Stand (inkl. Entwurf) bleibt dann stehen.
          setProjectReadOnly(false);
      } finally {
          lockOpRef.current = false;
      }
  }, [lockLabel, lockUserId, withProfileSettings, setProjectReadOnly]);

  /**
   * Rueckzug aus dem Projekt nach langer Untaetigkeit.
   *
   * Was hier passiert, ist bewusst dasselbe wie beim Zurueck-Knopf
   * (`handleLeaveProject`) — Sperre weg, Bericht weg, Flaggen zurueck —, nur
   * mit einem festen Ziel: die PROJEKTLISTE. `previousView` waere hier falsch;
   * wer nach der Mittagspause zurueckkommt, soll dort landen, wo er sein
   * Projekt wieder aufmacht, und nicht in der Ansicht, aus der er es vor
   * Stunden geoeffnet hat.
   *
   * Die eine Bedingung, die den Rueckzug verhindern kann, steht ganz oben:
   * ein wartender Eintrag der Offline-Warteschlange. Der kommt nur durch,
   * solange WIR die Sperre halten (Torwaechter in useOfflineSync) — erst
   * freigeben und dann feststellen, dass der Stand noch unten liegt, waere
   * genau die Reihenfolge, die Arbeit kostet. Dann bleibt alles stehen und der
   * naechste Herzschlag versucht es erneut.
   *
   * @returns true, wenn das Projekt tatsaechlich geschlossen wurde.
   */
  const exitProjectForIdle = useCallback(async (projectId: string): Promise<boolean> => {
      const queueUserId = session?.user?.id;
      const queued = queueUserId
          ? await getQueueEntry(queueUserId, projectId).catch(() => undefined)
          : undefined;
      if (queued) return false;

      // Waehrend der Abfrage gewechselt oder geschlossen: dann gibt es hier
      // nichts mehr zu beenden, und ein Rueckzug traefe das falsche Projekt.
      if (currentProjectIdRef.current !== projectId) return false;

      // Ab hier wird wirklich geschlossen — die beiden Abbrueche oben lassen
      // das Projekt offen und duerfen deshalb nichts melden.
      recordProjectClose({ projectId, reason: 'idle' });
      releaseHeldLock();
      setProjectReadOnly(false);
      setExplicitReadOnly(false);
      setLockBlocked(false);
      resetForeignAccess();
      setProjectLock(null);
      setReport(null);
      setCurrentProjectId(null);
      setView('projects');
      setIdleNotice(idleReleaseNotice(LOCK_IDLE_EXIT_MINUTES));
      return true;
  }, [session?.user?.id, setProjectReadOnly, resetForeignAccess]);

  /**
   * Rueckzug aus dem Projekt beim Zusperren der Bauphase (useBauphaseGate).
   *
   * Dieselben Schritte wie exitProjectForIdle, aber OHNE dessen beide
   * Abbrueche und ohne Leerlauf-Hinweis: der Gate hat die Warteschlange
   * vorher geleert und wartet auf „gesichert"; was danach noch liegt, bleibt
   * sicher in der IndexedDB und geht nach dem Neuladen raus. Das Projekt
   * muss trotzdem zu — der Server nimmt ohnehin nichts mehr an.
   */
  const closeProjectForMaintenance = () => {
      const projectId = currentProjectIdRef.current;
      if (!projectId) return;
      recordProjectClose({ projectId, reason: 'maintenance' });
      releaseHeldLock();
      setProjectReadOnly(false);
      setExplicitReadOnly(false);
      setLockBlocked(false);
      resetForeignAccess();
      setProjectLock(null);
      setReport(null);
      setCurrentProjectId(null);
      setView('projects');
  };

  /**
   * Vorwarnung und Rueckzug — ein Zeitgeber, der sich selbst neu plant.
   *
   * `describeIdleCountdown` liefert den Abstand bis zum naechsten Blick gleich
   * mit: ausserhalb der letzten zwei Minuten schlaeft der Zeitgeber (und
   * rechnet beim Aufwachen frisch aus dem Ref nach, sodass Eingaben in der
   * Zwischenzeit den Termin ganz von allein verschieben), in der Warnphase
   * tickt er im Sekundentakt. Ein durchlaufendes 1-s-Intervall waere die
   * einfachere, aber teuerste Loesung — es zeichnete den ganzen Arbeitstag
   * ueber jede Sekunde neu, um 99 % der Zeit „noch 29 Minuten" zu sagen.
   *
   * Der Rueckzug faellt HIER und nicht mehr im 45-s-Herzschlag: sonst stuende
   * die Uhr auf 0:00 und es passierte bis zu eine dreiviertel Minute lang
   * nichts. Der Herzschlag laesst in dieser Lage nur das Erneuern der Sperre
   * bleiben.
   *
   * Im Lesemodus laeuft gar nichts: dort halten wir keine Sperre, es gibt also
   * weder etwas freizugeben noch zu warnen.
   */
  useEffect(() => {
      if (!sitzungsNutzerId || !currentProjectId || isProjectReadOnly) { setIdleCountdownMs(null); return; }
      const projectId = currentProjectId;
      let timer: ReturnType<typeof setTimeout> | null = null;

      const check = () => {
          const state = describeIdleCountdown({
              holdsLock: heldLockRef.current === projectId,
              inProjectView,
              lastActivityAtMs: lastActivityAtRef.current,
              nowMs: Date.now(),
              saveStatus: saveStatusRef.current,
              held: isIdleExitHeld(),
          });
          setIdleCountdownMs(state.warn ? state.remainingMs : null);
          if (state.expired) void exitProjectForIdle(projectId);
          timer = setTimeout(check, state.nextCheckMs);
      };
      check();

      return () => { if (timer) clearTimeout(timer); };
  }, [sitzungsNutzerId, currentProjectId, inProjectView, isProjectReadOnly, exitProjectForIdle]);

  /**
   * Fremdfreigabe laeuft ab, waehrend das Projekt offen ist (Mitternacht).
   *
   * Ohne diesen Zeitgeber arbeitete der Nutzer einfach weiter: die Sperre
   * laesst ihn fail-open gewaehren (42501 heisst dort „keine Sperre bekannt"),
   * und erst der Server weist jedes Speichern ab — die Eingaben staenden in der
   * Warteschlange fest, ohne dass jemand wuesste, warum. Die Entscheidung ist
   * rein (decideForeignGrantExpiry): vor dem Ende warten, ein laufendes
   * Speichern kurz zu Ende kommen lassen, dann zurueck in die Nur-Ansicht mit
   * Begruendungsknopf.
   *
   * Zeitgeber schlafen auf dem Handy und im Hintergrund-Tab. Deshalb wird beim
   * Sichtbarwerden und beim Fokus neu gerechnet, statt dem Wecker zu trauen.
   *
   * Hier oben, vor den fruehen Returns, und nur mit Werten, die ebenfalls hier
   * oben stehen (TDZ-Falle vom 13.09.2026).
   */
  useEffect(() => {
      if (!sitzungsNutzerId || !currentProjectId || !foreignGrant || foreignGrant.projectId !== currentProjectId) return;
      const projectId = currentProjectId;
      const grant = foreignGrant;
      let timer: ReturnType<typeof setTimeout> | null = null;
      /** Effekt abgeraeumt: eine noch laufende Erneuerung zaehlt nicht mehr. */
      let vorbei = false;
      /** Erneuerung unterwegs — Fokus und Sichtbarkeit fragen sonst doppelt. */
      let erneuert = false;

      // Abgelaufen: Sperre her, Nur-Ansicht, Begruendung neu anbieten.
      // Eine gerade laufende Sperranzeige (fremder Halter) weicht dem
      // Fremdhinweis — ohne neue Begruendung gaebe es auch nichts zu
      // uebernehmen.
      const ablaufen = () => {
          releaseHeldLock();
          setLockBlocked(false);
          setProjectReadOnly(true);
          setForeignPending(true);
          setForeignGrant(null);
          setIdleNotice(FOREIGN_FREIGABE_ABGELAUFEN);
      };

      const check = () => {
          if (timer) { clearTimeout(timer); timer = null; }
          if (vorbei || erneuert || currentProjectIdRef.current !== projectId) return;
          const decision = decideForeignGrantExpiry({
              grant,
              nowMs: Date.now(),
              role: userProfile?.role,
              foreignPending: foreignPendingRef.current,
              saveStatus: saveStatusRef.current,
              alleSehen: ohneBegruendung,
          });
          if (decision.action === 'none') return;
          if (decision.action === 'wait') { timer = setTimeout(check, decision.delayMs); return; }
          if (decision.action === 'renew') {
              // Recht „Alle Projekte sehen": still fuer den neuen Tag holen.
              // Nur ein spaeteres Ende zaehlt — gaebe der Server dasselbe
              // zurueck (Uhr des Geraets vor), liefe sonst Anfrage auf Anfrage.
              erneuert = true;
              void grantForeignAccessByRight(projectId, lockUserId ?? '').then((r) => {
                  erneuert = false;
                  if (vorbei || currentProjectIdRef.current !== projectId) return;
                  if (r.outcome === 'granted' && Date.parse(r.grant.validUntil) > Date.parse(grant.validUntil)) {
                      setForeignGrant(r.grant);
                      return;
                  }
                  ablaufen();
              });
              return;
          }
          ablaufen();
      };
      check();

      const onVisible = () => { if (document.visibilityState === 'visible') check(); };
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('focus', check);
      return () => {
          vorbei = true;
          if (timer) clearTimeout(timer);
          document.removeEventListener('visibilitychange', onVisible);
          window.removeEventListener('focus', check);
      };
  // releaseHeldLock liest nur Refs und ruft die RPC — wie in den Sperr-Effekten
  // oben steht es bewusst nicht in den Abhaengigkeiten.
  // foreignPending gehoert dazu: nach einer frischen Begruendung wird die
  // Freigabe gesetzt, WAEHREND der Lesemodus noch steht (Entscheidung 'none').
  // Erst das spaetere Ablegen des Lesemodus darf den Wecker stellen — ohne
  // diese Abhaengigkeit liefe das Projekt ueber Mitternacht beschreibbar.
  }, [sitzungsNutzerId, currentProjectId, foreignGrant, foreignPending, userProfile?.role, ohneBegruendung, lockUserId, setProjectReadOnly, setForeignPending]);

  /**
   * „Ich bin noch da" — die Frist beginnt von vorn.
   *
   * Der Klick allein wuerde es schon tun (er laeuft durch denselben
   * pointerdown-Zuhoerer wie jede andere Eingabe); ausgeschrieben steht es
   * hier trotzdem, damit der Knopf nicht von einer Nebenwirkung abhaengt und
   * die Warnung ohne Wartezeit auf den naechsten Tick verschwindet.
   */
  const handleIdleKeepAlive = () => {
      lastActivityAtRef.current = Date.now();
      setIdleCountdownMs(null);
  };

  // Erwerb, Herzschlag und Freigabe in EINER Entscheidung (decideLockPresence,
  // rein und getestet). Im Lesemodus wird weder geschlagen noch angefordert:
  // sonst hielte ein Zuschauer die Sperre am Leben, die er gar nicht hat.
  //
  // Der Rueckgabewert des Herzschlags wird ausgewertet: `acquired: false`
  // heisst, dass uns jemand ueberholt hat (Admin-Uebernahme oder Ablauf nach
  // einem laengeren Netzausfall). Dann steigen wir sofort in den Lesemodus um,
  // statt weiter zu tippen und am Ende den anderen zu ueberschreiben.
  useEffect(() => {
      if (!sitzungsNutzerId || !currentProjectId) return;
      const projectId = currentProjectId;

      const decide = (): LockPresence => decideLockPresence({
          projectId,
          heldProjectId: heldLockRef.current,
          inProjectView,
          readOnly: isProjectReadOnly,
          leftViewAtMs: leftProjectViewAtRef.current,
          nowMs: Date.now(),
      });

      const apply = (presence: LockPresence) => {
          if (presence === 'acquire') { void reacquireProjectLock(projectId); return; }
          if (presence === 'release') { releaseHeldLock(); return; }
          if (presence !== 'hold' && presence !== 'grace') return;
          void renewProjectLock(projectId, lockLabel).then((res) => {
              if (res.lock) setProjectLock(res.lock);
              if (!res.acquired && isLockHeldByOther(res.lock, lockUserId, Date.now())) {
                  heldLockRef.current = null;
                  setLockBlocked(true);
                  setProjectReadOnly(true);
              }
          });
      };

      // Sofort nur die Zustandswechsel, nie ein Herzschlag: jeder Wechsel
      // zwischen zwei Modulen laesst diesen Effekt neu laufen, und ein RPC je
      // Klick waere Last ohne Nutzen. Die Freigabe kann hier nie fallen — die
      // Karenzzeit beginnt im selben Commit —, sie faellt beim ersten
      // Herzschlag danach.
      const first = decide();
      if (first === 'acquire' || first === 'release') apply(first);

      /**
       * Ein Schlag — oder der Rueckzug.
       *
       * Der Leerlauf schlaegt den Herzschlag: wer die Sperre haelt und seit
       * einer halben Stunde nichts angefasst hat, soll sie NICHT auch noch
       * erneuern. Nur im Zustand 'hold' ueberhaupt gefragt; 'grace' heisst,
       * dass die Karenzzeit den Fall bereits erledigt.
       *
       * Geschlossen wird das Projekt anderswo (Zeitgeber der Vorwarnung, auf
       * die Sekunde genau); hier bleibt nur der ausgesetzte Schlag. Der ist
       * folgenlos, falls der Rueckzug doch nicht zustande kommt: die Sperre
       * traegt drei Minuten, der naechste Schlag kommt in 45 Sekunden.
       */
      const tick = () => {
          const presence = decide();
          const idle = presence === 'hold' && shouldExitForIdle({
              holdsLock: heldLockRef.current === projectId,
              inProjectView,
              lastActivityAtMs: lastActivityAtRef.current,
              nowMs: Date.now(),
              saveStatus: saveStatusRef.current,
              held: isIdleExitHeld(),
          });
          if (!idle) apply(presence);
      };

      const timer = window.setInterval(tick, LOCK_HEARTBEAT_MS);
      return () => window.clearInterval(timer);
  }, [sitzungsNutzerId, currentProjectId, inProjectView, isProjectReadOnly, lockLabel, reacquireProjectLock, setProjectReadOnly, lockUserId, exitProjectForIdle]);

  // ─── Sperrzustand mitverfolgen ────────────────────────────────────────────
  //
  // Realtime, damit der Balken verschwindet, sobald der andere das Projekt
  // schliesst — ohne dass jemand F5 drueckt. Eigener Kanal je Projekt mit
  // Serverfilter: die Sperrzeilen aller anderen Projekte gehen uns nichts an.
  //
  // Der Zeitgeber daneben ist der Ausfallschutz. Realtime kann fehlen (der
  // Publikations-Block in project_locks.sql ist ausdruecklich optional und
  // darf scheitern), der Socket kann nach Standby tot sein, ein DELETE kann
  // verlorengehen. Ohne ihn bliebe der Balken ewig falsch stehen.
  //
  // ─── Und der HALTER haengt hier mit drin ──────────────────────────────────
  //
  // Frueher setzte dieser Zweig nur `projectLock` — den Umstieg in den
  // Lesemodus machte allein der Herzschlag oben, und der Zeitgeber hier lief
  // nur `isProjectReadOnly ? … : null`, also fuer den Halter gar nicht. Bricht
  // ein Admin die Sperre auf, tippte der Halter darum bis zu 45 s weiter (ueber
  // Realtime haette er es nie erfahren) und sein Autosave landete ueber dem
  // Stand, den der Admin gerade frisch geladen hat — genau das Gegenteil
  // dessen, was die Rueckfrage vor dem Aufbrechen verspricht.
  //
  // Deshalb: der Zeitgeber laeuft in JEDER Lage (zwei unabhaengig getaktete
  // Abfragen halbieren die Wartezeit, wenn Realtime schweigt), und beide Wege
  // werten den Sperrverlust aus.
  //
  // Nur in den Projektansichten: ausserhalb steht kein Balken, den es
  // aktualisieren koennte, und ein Kanal je geparktem Projekt waere Last ohne
  // Anzeige dahinter.
  useEffect(() => {
      if (!sitzungsNutzerId || !currentProjectId || !inProjectView) return;
      const projectId = currentProjectId;
      const refresh = () => {
          void fetchProjectLock(projectId).then((lock) => {
              setProjectLock(lock);
              if (!hasLostHeldLock({
                  heldProjectId: heldLockRef.current,
                  projectId,
                  lock,
                  currentUserId: lockUserId,
                  nowMs: Date.now(),
              })) return;
              // Aufgebrochen oder abgelaufen: sofort raus aus dem Schreibmodus.
              // setProjectReadOnly bricht dabei den wartenden Autosave ab —
              // ohne das ginge der zuletzt getippte Stand zwei Sekunden spaeter
              // trotzdem noch raus.
              heldLockRef.current = null;
              setLockBlocked(true);
              setProjectReadOnly(true);
          });
      };
      const handle = subscribeWithAuthRecovery(supabase, () => supabase.channel(`project-lock-${projectId}`)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'project_locks', filter: `project_id=eq.${projectId}` }, refresh));
      const timer = window.setInterval(refresh, LOCK_HEARTBEAT_MS);
      return () => {
          handle.close();
          window.clearInterval(timer);
      };
  }, [sitzungsNutzerId, currentProjectId, inProjectView, lockUserId, setProjectReadOnly]);

  // ─── Fenster/Tab weg ──────────────────────────────────────────────────────
  //
  // `pagehide` statt `beforeunload`: es feuert auch dann, wenn eine Seite in
  // den Back-Forward-Cache wandert oder eine mobile App weggewischt wird —
  // genau die Faelle, in denen beforeunload auf iOS still bleibt.
  //
  // Kommt der Aufruf nicht mehr durch (harter Absturz, Netz weg, abgebrochener
  // Request beim Entladen), ist das ausdruecklich kein Beinbruch: die Sperre
  // verfaellt nach LOCK_STALE_MS = 180 s von selbst, danach nimmt sie der
  // naechste Bearbeiter ueber den ganz normalen `acquire`.
  //
  // Dasselbe gilt fuer die Aktivitaetserfassung, nur mit einem Kniff: ein
  // gewoehnlicher Insert ueberlebt das Entladen nicht zuverlaessig, deshalb
  // geht das Schliessen hier als keepalive-fetch hinaus
  // (recordProjectCloseBeacon). Kommt es nicht durch, bleibt die Sitzung in
  // der Auswertung als „offen" stehen — ihre Dauer wird NICHT geschaetzt.
  useEffect(() => {
      const onPageHide = () => {
          recordProjectCloseBeacon('unload');
          releaseHeldLock();
      };
      window.addEventListener('pagehide', onPageHide);
      return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  // ─── ... und wieder da ────────────────────────────────────────────────────
  //
  // Genau die Staerke von `pagehide` ist hier der Haken: es feuert auch, wenn
  // die Seite bloss eingefroren wird (Zurueck-Navigation, weggewischte App auf
  // iOS, Back-Forward-Cache). Oben ist das Schliessen dann geschrieben und die
  // Wache leer — kommt die Seite zurueck, sitzt der Anwender aber unveraendert
  // im selben Projekt, denn der Cache stellt den kompletten JS-Zustand wieder
  // her. Ohne Gegenstueck bliebe die restliche Zeit unsichtbar, und das
  // spaetere echte Verlassen schriebe nichts mehr.
  //
  // `event.persisted` unterscheidet die Rueckkehr aus dem Cache vom ganz
  // gewoehnlichen Laden — `pageshow` feuert auch dort, und ein Oeffnen zu
  // melden, das niemand ausgeloest hat, waere erfunden.
  //
  // Nur die Erfassung: die Projektsperre bleibt freigegeben (sie verfaellt
  // ohnehin nach LOCK_STALE_MS und wird ueber ihren eigenen Weg neu geholt).
  //
  // Das neue Oeffnen wird dabei als WIEDERAUFNAHME gekennzeichnet
  // (`open_reason = 'resume'`, in recordProjectRestore). Auf iOS und Android
  // feuert dieser Weg bei jedem App-Wechsel; als Oeffnung mitgezaehlt wuerden
  // aus einem Vormittag mit einem Projekt und acht Telefonaten neun
  // „Projekte geoeffnet". Die verbrachte Zeit zaehlt weiter voll mit, die
  // Oeffnung nicht — die Trennung macht userActivityStats.ts.
  useEffect(() => {
      const onPageShow = (event: PageTransitionEvent) => {
          if (!event.persisted) return;
          recordProjectRestore();
      };
      window.addEventListener('pageshow', onPageShow);
      return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // ─── Mobil: der App-Wechsel IST das Verlassen ─────────────────────────────
  //
  // Auf dem Handy tippt fast niemand den Zurueck-Knopf; man wechselt in die
  // Kamera oder ans Telefon und kommt nicht wieder — das Betriebssystem
  // raeumt die Seite ab. Gemessen: bei einem Aussendienst-Konto blieben 14
  // von 19 Sitzungen dauerhaft „offen", weil ausschliesslich der Zurueck-Knopf
  // ein Schliessen erzeugt hat.
  //
  // `pagehide` faengt das nicht: beim reinen App-Wechsel bleibt es auf
  // Android aus, und wo es feuert, ist die Seite schon halb tot (der
  // keepalive-Beacon kam bei diesem Konto NIE an). `hidden` dagegen ist der
  // letzte Augenblick, in dem die Seite nachweislich laeuft — dort reicht der
  // gewoehnliche Insert.
  //
  // Die Karenzzeit und der ganze Rest der Entscheidung stecken im Dienst
  // (recordProjectHidden/-Visible); hier liegt nur der Draht zum Browser.
  useEffect(() => {
      const onActivityVisibility = () => {
          if (document.visibilityState === 'hidden') recordProjectHidden();
          else recordProjectVisible();
      };
      document.addEventListener('visibilitychange', onActivityVisibility);
      return () => document.removeEventListener('visibilitychange', onActivityVisibility);
  }, []);

  // Version-Gate: veraltete Bundles (Browser-/PWA-Cache) haben die
  // Datenverlust-Guards nicht — beim Aufwachen deployte Version prüfen und
  // neu laden, bevor ein alter Client weiter schreiben kann. Bei offenem
  // Bericht/laufendem Speichern wird nicht hart reloaded (ungespeicherte
  // Eingaben!), sondern ein Banner gezeigt; Reload folgt nach dem Schließen.
  /**
   * Warum der Balken steht — null heisst: er steht nicht.
   *
   * Nicht nur ein `boolean`, weil die beiden Anlaesse sich verschieden
   * verhalten: ein erkanntes Deployment darf nachtraeglich noch neu laden,
   * sobald der Bericht zu ist; der Build-Riegel nie (siehe
   * `decideOnVeraltet`). Und der Text ist ein anderer — „neue Version
   * verfuegbar" waere bei einem Riegel schlicht falsch, dort ist Speichern
   * bereits gesperrt.
   */
  const [updateAnlass, setUpdateAnlass] = useState<VeraltetAnlass | null>(null);
  // „Jetzt aktualisieren": Satz unter dem Balken, wenn nicht neu geladen wird,
  // und der Knopf, solange vorher gesichert wird (jetztAktualisieren unten).
  const [updateHinweis, setUpdateHinweis] = useState<string | null>(null);
  const [updateSichert, setUpdateSichert] = useState(false);
  // Betreiber 03.10.2026: „neue Version" nicht als kleiner Balken unten,
  // sondern mittig, damit es auffaellt. Das Fenster laesst sich mit „Spaeter"
  // auf den bisherigen Balken verkleinern (am Handy liegt es sonst ueber dem
  // Mangel-Assistenten). Jeder NEUE Anlass — etwa der Riegel nach einem
  // erkannten Deployment — zeigt es wieder gross.
  const [updateKlein, setUpdateKlein] = useState(false);
  useEffect(() => {
      if (updateAnlass !== null) setUpdateKlein(false);
  }, [updateAnlass]);
  // Modales Fenster auch fuer die Tastatur: beim Erscheinen Fokus auf
  // „Jetzt aktualisieren", Tab bleibt im Fenster, Escape = „Spaeter", danach
  // zurueck dorthin, wo der Fokus vorher stand.
  // State statt Ref: der Effekt muss laufen, sobald das Fenster WIRKLICH
  // im DOM haengt (es steht nur in der Hauptansicht, nicht in den frühen Returns).
  const [updateFensterEl, setUpdateFensterEl] = useState<HTMLDivElement | null>(null);
  const updateFokusVorherRef = useRef<HTMLElement | null>(null);
  const updateFensterOffen = updateAnlass !== null && !updateKlein;
  const updateVerkleinern = useCallback(() => {
      setUpdateKlein(true);
      const vorher = updateFokusVorherRef.current;
      updateFokusVorherRef.current = null;
      if (vorher && vorher.isConnected) vorher.focus();
  }, []);
  useEffect(() => {
      const fenster = updateFensterEl;
      if (!fenster) return;
      const aktiv = document.activeElement;
      if (aktiv instanceof HTMLElement && !fenster.contains(aktiv)) updateFokusVorherRef.current = aktiv;
      fenster.querySelector<HTMLButtonElement>('[data-update-haupt]')?.focus();
      const taste = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
              // Capture + stopPropagation: Escape schliesst sonst die Maske
              // DARUNTER (Mangel-Assistent), nicht dieses Fenster.
              e.preventDefault();
              e.stopPropagation();
              updateVerkleinern();
              return;
          }
          if (e.key !== 'Tab') return;
          const knoepfe = Array.from(fenster.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
          if (knoepfe.length === 0) { e.preventDefault(); return; }
          const erster = knoepfe[0];
          const letzter = knoepfe[knoepfe.length - 1];
          const da = document.activeElement;
          if (!da || !fenster.contains(da)) { e.preventDefault(); erster.focus(); return; }
          if (e.shiftKey && da === erster) { e.preventDefault(); letzter.focus(); }
          else if (!e.shiftKey && da === letzter) { e.preventDefault(); erster.focus(); }
      };
      document.addEventListener('keydown', taste, true);
      return () => document.removeEventListener('keydown', taste, true);
  }, [updateFensterEl, updateVerkleinern]);
  const updateHinweisUhrRef = useRef<number | null>(null);
  const editorBusyRef = useRef({ editorOpen: false, saving: false });
  useEffect(() => {
      editorBusyRef.current = { editorOpen: !!report, saving: saveStatus === 'saving' };
  }, [report, saveStatus]);
  // Der Angebots-Editor lebt tief in der CRM-Ansicht und meldet sich ueber
  // services/editorBusy. Ohne ihn galt ein offenes Angebot als „frei" und der
  // automatische Reload warf ungespeicherte Eingaben weg (Audit 24.09., Q5).
  // Als Store, damit das Nachholen unten beim Schliessen erneut laeuft.
  // Modul-Funktionen: keine TDZ-Gefahr ueber den fruehen Returns.
  const quoteEditorBusy = useSyncExternalStore(subscribeEditorBusy, getQuoteEditorBusy);

  /**
   * EIN Pfad fuer „dieses Bundle darf nicht mehr schreiben" — mit EINEM
   * Unterschied, der `anlass` heisst.
   *
   * Drei Melder muenden hier: das Version-Gate (/version.json ist neuer,
   * 'deployment'), der Mindest-Build aus `maintenance_status()` und der
   * Server, der einen Schreibvorgang mit `BUILD_VERALTET` abgewiesen hat
   * (beide 'riegel').
   *
   * Der Riegel laedt NIE von selbst neu. Die Begruendung steht bei
   * `decideOnVeraltet` in versionGate.ts, kurz: der Reload hier kennt nur
   * den Begehungs-Editor (`editorBusyRef`) und wuerde einem Admin-Klick
   * erlauben, flottenweit offene Rechnungen, Angebote und SiGe-Plaene
   * wegzuwerfen — und bei einem `min_build` ueber dem ausgelieferten Bundle
   * liefe er alle zehn Minuten erneut. Gebraucht wird er nicht: der Server
   * weist ab, der Marker haelt Speichern und Warteschlange an.
   *
   * Deklariert VOR jedem Effekt und Hook, der sie schliesst (Version-Gate,
   * useOfflineSync, useBauphaseGate) — die Regel aus dem Bootcrash vom
   * 13.09.2026: was ein Effekt oberhalb der fruehen Returns schliesst, muss
   * oberhalb davon deklariert sein.
   */
  const behandleVeraltetenBuild = useCallback((anlass: VeraltetAnlass) => {
      const busy = combineEditorBusy(editorBusyRef.current, getQuoteEditorBusy());
      const d = decideOnVeraltet(
          anlass,
          // Auch ohne offenen Bericht kann eine Maske laufen, die ein Reload
          // abschneidet (idleHold: Versand, Upload, Raeumen der Ablage).
          busy.editorOpen || isIdleExitHeld(),
          busy.saving,
          window.localStorage,
          Date.now(),
      );
      if (d === 'reload') window.location.reload();
      // 'defer' UND 'skip' zeigen den Balken: seit dem 07.09.2026
      // schreibt ein veraltetes Bundle nicht mehr (versionGate,
      // istBuildVeraltet), und dann muss der Nutzer sehen, warum —
      // auch im Backoff-Fenster, in dem frueher gar nichts zu sehen war.
      // Ein einmal gemeldeter Riegel bleibt stehen: er wiegt schwerer als
      // ein spaeter erkanntes Deployment und sagt dem Nutzer mehr.
      else setUpdateAnlass(bisher => (bisher === 'riegel' ? bisher : anlass));
  }, []);

  // Der Rueckkanal aus dem Supabase-Client: hat der Server IRGENDEINEN
  // Schreibvorgang mit dem Build-Riegel abgewiesen, kommt die Meldung von
  // dort — auch aus Wegen, die ihren Fehler nie selbst einordnen
  // (saveReportSnapshot, restoreInspectionItems, applyLinkTransfer,
  // projectLinkService, Journal-Spiegel). Die Abmeldung ist der Rueckgabewert
  // der Anmeldung und damit auch bei hartem Abbau sicher.
  useEffect(
      () => onBuildGuardRejected(() => behandleVeraltetenBuild('riegel')),
      [behandleVeraltetenBuild],
  );

  // Das Gate selbst, damit der Torwaechter der Warteschlange VOR dem
  // Hochladen nachfragen kann (canUploadQueueEntry). Im DEV-Modus bleibt es
  // leer, dann laedt die Warteschlange wie bisher.
  const versionGateRef = useRef<ReturnType<typeof createVersionGate> | null>(null);
  useEffect(() => {
      if ((import.meta as any).env?.DEV) return;
      const gate = createVersionGate({
          currentVersion: __APP_VERSION__,
          currentBuildTime: aktuellerBuildStempel,
          // Backoff über Reloads hinweg: bei stale Cache maximal ein
          // automatischer Reload pro 10 Minuten statt einer Endlos-Schleife.
          onOutdated: () => behandleVeraltetenBuild('deployment'),
      });
      versionGateRef.current = gate;
      // Der Versanddialog fragt vor dem Senden SOFORT nach (Vorfall 6051
      // #149: ein veralteter Tab mailte, durfte danach aber nicht speichern).
      const versandPruefungAbmelden = setzeVersandPruefung(() => gate.checkNow({ sofort: true }));
      const onVisibility = () => { if (document.visibilityState === 'visible') void gate.checkNow(); };
      void gate.checkNow();
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('focus', onVisibility);
      // Herzschlag: ein Tab, der stundenlang im Vordergrund steht, bekommt
      // weder focus noch visibilitychange — vor dem 07.09.2026 pruefte er
      // deshalb nie wieder und schrieb mit altem Build weiter. Der Abstand
      // entspricht minIntervalMs des Gates; oefter fetcht es ohnehin nicht.
      const herzschlag = window.setInterval(() => { void gate.checkNow(); }, 5 * 60 * 1000);
      return () => {
          versionGateRef.current = null;
          versandPruefungAbmelden();
          window.clearInterval(herzschlag);
          document.removeEventListener('visibilitychange', onVisibility);
          window.removeEventListener('focus', onVisibility);
      };
  }, [behandleVeraltetenBuild]);

  // Aufgeschobenes Update nachholen, sobald Bericht zu und nichts mehr
  // speichert. NUR fuer ein erkanntes Deployment — ein Riegel laedt auch
  // hier nicht von selbst nach (siehe decideOnVeraltet).
  useEffect(() => {
      if (updateAnlass === null || report || saveStatus === 'saving') return;
      if (quoteEditorBusy.open || quoteEditorBusy.saving) return;
      // Haelt eine Maske (idleHold), meldet der naechste Herzschlag erneut.
      if (isIdleExitHeld()) return;
      const d = decideOnVeraltet(updateAnlass, false, false, window.localStorage, Date.now());
      if (d === 'reload') window.location.reload();
      // 'skip': Backoff aktiv — Banner bleibt, manueller Button funktioniert weiter
      // 'defer': Riegel — der Balken bleibt, der Knopf bleibt bedienbar
  }, [updateAnlass, report, saveStatus, quoteEditorBusy]);

  /** Satz unter dem Balken: gilt dem letzten Tipp, nach 8 s waere er veraltet. */
  const zeigeUpdateHinweis = (text: string) => {
      if (updateHinweisUhrRef.current !== null) window.clearTimeout(updateHinweisUhrRef.current);
      setUpdateHinweis(text);
      updateHinweisUhrRef.current = window.setTimeout(() => {
          updateHinweisUhrRef.current = null;
          setUpdateHinweis(null);
      }, 8000);
  };

  /**
   * „Jetzt aktualisieren" im Balken. Bis 09/2026 lud der Knopf sofort neu —
   * auch mitten im Mangel-Assistenten, dessen Maske nur im Speicher liegt.
   *
   *  1. Haelt eine Maske den Leerlauf-Rueckzug auf (idleHold: Mangel-
   *     Assistent, E-Mail-Versand, laufender Upload …), wird NICHT neu
   *     geladen, nur ein Hinweis.
   *  2. Sonst sichern wie vor dem In-App-Update (sichereEingabenVorUpdate,
   *     nur bestaetigt geht es weiter) und bis zu 8 s warten, bis die
   *     Warteschlange ihre IndexedDB-Transaktion und ihr Hochladen abgesetzt
   *     hat (warteAufRuhe). Anders als beim Sitzungsentzug ist das Neuladen
   *     hier kein Muss: wird die Warteschlange nicht ruhig, bleibt die Seite
   *     stehen — sonst laege der Eintrag bis zu 5 min in 'syncing'. Der
   *     Bericht selbst liegt schon in der Warteschlange; das `pagehide` des
   *     Neuladens erreicht Angebots-Editor und Projektsperre wie beim
   *     In-App-Update.
   *
   * Nur aus dem Klick heraus, schliesst nichts unterhalb der fruehen Returns.
   */
  const jetztAktualisieren = async () => {
      const offeneEingabe = 'Erst offene Eingabe abschließen oder Vorgang abwarten, dann aktualisieren.';
      if (isIdleExitHeld()) {
          zeigeUpdateHinweis(offeneEingabe);
          return;
      }
      setUpdateHinweis(null);
      setUpdateSichert(true);
      const gesichert = await sichereEingabenVorUpdate();
      const ruhig = gesichert
          && await warteAufRuhe(() => queueBeschaeftigtRef.current() || getQuoteEditorBusy().saving, { obergrenzeMs: 8000 });
      // Waehrend des Sicherns kann eine Maske aufgegangen sein.
      const hinweis = !gesichert ? 'Sichern nicht bestätigt — bitte erneut versuchen.'
          : !ruhig ? 'Speichern läuft noch — gleich erneut tippen.'
          : isIdleExitHeld() ? offeneEingabe
          : null;
      if (hinweis) {
          setUpdateSichert(false);
          zeigeUpdateHinweis(hinweis);
          return;
      }
      window.location.reload();
      // Bleibt die Seite doch stehen (Rueckfrage des Angebots-Editors mit
      // „Bleiben" beantwortet), wird der Knopf wieder frei.
      window.setTimeout(() => setUpdateSichert(false), 3000);
  };

  // --- Ansicht über Refresh hinweg merken (sessionStorage) ---
  const restoredNavRef = useRef(false);
  // Merker fuer die Seite `#/app`: sie schliesst bei jedem Ansichtswechsel
  // (Effekt im Android-Block unten). Der Restore traegt seine Ansicht hier
  // vorher ein — ein F5 auf der Seite ist kein Wechsel und soll sie nicht
  // schliessen.
  const appSeiteViewRef = useRef<AppView>(view);

  // Restore läuft genau einmal; Projekt-Views erst, wenn die Projektliste da ist.
  useEffect(() => {
      if (restoredNavRef.current || !sitzungsNutzerId || loading) return;
      const saved = loadNavState(window.sessionStorage);
      if (!saved) { restoredNavRef.current = true; return; }
      if (saved.projectId) {
          if (projects.length === 0) return; // Projekte noch nicht geladen — nächster Lauf
          // Die Liste aus dem Geraetespeicher kann von gestern sein (Nr. 175):
          // Zustaendigkeit und Status der Zeile entscheiden gleich ueber den
          // Lesemodus. Erst nach dem Abgleich — nächster Lauf.
          if (listeAusCache) return;
          // Ohne Profil entscheidet handleOpenProject nicht, ob das Projekt
          // fremd ist (isForeignProjectFor liefert dann false) — ein
          // fremdes Projekt kaeme nach F5 beschreibbar zurueck, und die Sperre
          // liesse das 42501 fail-open durch. Also aufs Profil warten.
          if (!userProfile) return;
          restoredNavRef.current = true;
          const project = projects.find(p => p.id === saved.projectId);
          if (!project) return; // Projekt weg (gelöscht/übertragen) → Dashboard bleibt
          // Der Schreibschutz muss den Refresh ueberleben: ohne das zweite
          // Argument kam ein schreibgeschuetzt geoeffnetes Projekt hier im
          // Schreibmodus zurueck (Default false). Quelle ist der gemerkte
          // Zustand; fehlt die Flagge dort, liefert loadNavState gar nichts
          // erst zurueck.
          //
          // Das dritte Argument ist die Erfassung, nicht das Oeffnen: hier
          // stellt die Anwendung wieder her, was vor dem Neuladen offen war —
          // geoeffnet hat das niemand. Beim Neuladen (F5, Reload des
          // Versions-Gates, wiederhergestellter Tab) schreibt der
          // pagehide-Weg vorher ein Schliessen mit `unload`; kaeme das
          // Wiederherstellen danach als gewoehnliches Oeffnen herein, erhoehte
          // JEDES Neuladen „Projekte geoeffnet", Stundenhistogramm und
          // Heatmap. Das ist dieselbe Klasse wie die Rueckkehr aus dem
          // Hintergrund (`recordProjectRestore`), und sie wird genauso
          // behandelt: die verbrachte Zeit zaehlt voll, die Oeffnung nicht.
          appSeiteViewRef.current = 'project-hub';
          handleOpenProject(project, saved.isReadOnly, 'resume').then((ok) => {
              // Fehlgeschlagener Load: nicht in einen View ohne Report navigieren.
              if (!ok) return;
              appSeiteViewRef.current = saved.view;
              setView(saved.view);
              if (saved.activeSection) setActiveSection(saved.activeSection);
          });
      } else {
          restoredNavRef.current = true;
          appSeiteViewRef.current = saved.view;
          setView(saved.view);
      }
  }, [sitzungsNutzerId, loading, projects, userProfile, listeAusCache]);

  // Speichern erst NACH dem Restore — sonst überschreibt der initiale
  // Dashboard-Render den gemerkten Zustand, bevor er gelesen wurde.
  useEffect(() => {
      if (!sitzungsNutzerId || !restoredNavRef.current) return;
      // isProjectReadOnly gehoert mit in den gemerkten Zustand — sonst weiss
      // der Restore nach F5 nicht, dass nur gelesen werden darf.
      //
      // Gemerkt wird aber nur der AUSDRUECKLICHE Schreibschutz. Ein Lesemodus,
      // der bloss von einer fremden Sperre kam, waere nach F5 zementiert: der
      // Restore reicht die Flagge an handleOpenProject durch, das fordert dann
      // gar keine Sperre mehr an — und das Projekt bliebe schreibgeschuetzt,
      // obwohl der andere laengst raus ist. Ohne die Flagge fragt der Restore
      // die Sperre frisch ab und entscheidet neu.
      saveNavState(window.sessionStorage, { view, projectId: currentProjectId, activeSection, isReadOnly: explicitReadOnly });
  }, [sitzungsNutzerId, view, currentProjectId, activeSection, explicitReadOnly]);

  // Die Projektliste laedt nur bei Nutzerwechsel — nicht bei jeder
  // Navigation und nicht bei jedem neuen Sitzungsobjekt (Token-Refresh,
  // Rueckkehr in den Tab, authRefreshPolicy.ts). Gestartet wird sie im
  // Auth-Rueckruf (`ladeZurSitzung`, Art 'voll'): seit Nr. 175 zuerst aus dem
  // Geraet, dann nur der Abgleich (starteListe). Der eigene Effekt an der
  // Nutzerkennung, der bis dahin zusaetzlich voll lud, ist damit entfallen —
  // jeder neue Nutzer kommt ueber 'voll'. Aktualisierungen kommen ueber den
  // Realtime-Kanal, das Nachholen und explizites Refresh.

  // Mitteilungen laedt der gemeinsame Speicher selbst (useNotifications in
  // Navigation, Dashboard und Aufgaben) — hier nur die Nachfass-Erzeugung.
  useEffect(() => {
      if (userProfile?.id) {
          // Faellige Angebots-Nachfaesse als Glocken-Benachrichtigung erzeugen –
          // nur fuer Admins, fire-and-forget (darf den Login-Bootstrap nie stoeren).
          // Danach die Glocke frisch abrufen, sonst erschienen die eben
          // erzeugten Mitteilungen erst mit dem naechsten Minutentakt.
          const role = (userProfile as any)?.role;
          const uid = userProfile.id;
          if (role === 'admin' || role === 'super_admin') {
              notifyDueFollowUps(uid)
                  .then(() => refreshNotifications(uid, { force: true }))
                  .catch(console.warn);
          }
      }
  }, [userProfile?.id]);

  /**
   * Profil, Firmeneinstellungen und Kataloge laden — und, wenn nicht anders
   * verlangt, danach die Projektliste. `mitProjekten = false` fuer das
   * Auffrischen allein (USER_UPDATED, Nachholen, Realtime auf `profiles`).
   * Liefert, ob das Profil da ist.
   *
   * `runde`: die Sitzungsrunde, FUER die geladen wird. Ohne Angabe die
   * aktuelle — richtig fuer alles, was aus der laufenden Sitzung heraus
   * ruft. Zeitgeber und Rueckrufe eines Effekts geben die Runde mit, in der
   * der Effekt entstand: sie koennen nach einem Nutzerwechsel noch feuern.
   */
  const fetchProfile = async (userId: string, mitProjekten = true, runde = sitzungsRundeRef.current.runde): Promise<boolean> => {
      const giltNoch = () => rundeGilt(runde, sitzungsRundeRef.current);
      if (!giltNoch()) return false;
      try {
          const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
          if (!giltNoch()) return false;
          if (data) {
              geladenesProfilRef.current = data as UserProfile;
              setUserProfile(data as UserProfile);
              // Arbeitsbeginn festhalten — genau einmal je Browser-Sitzung
              // (F5 erzeugt keinen zweiten Start). Erst HIER und nicht schon
              // beim Anmelden: ohne Profil fehlte die Firma, und ohne Firma
              // faellt die Zeile aus der Mandantentrennung der Auswertung.
              // Fire-and-forget, wirft nie, haelt den Bootstrap nicht auf.
              recordAppStart(data as UserProfile);
              // NOCH VOR der Projektliste: die Rechnungs-Grundeinstellungen der
              // Firma muessen stehen, bevor die Oberflaeche bedienbar ist —
              // sonst entstuende ein Beleg, der in der ersten Sekunde nach dem
              // Anmelden angelegt wird, mit den eingebauten Standardwerten.
              const rechnungsEinstellungen = await loadCompanyInvoiceConfig(data as UserProfile);
              if (!giltNoch()) return false;
              setCompanyInvoiceConfig(rechnungsEinstellungen);
              const projektKatalog = await loadCompanyProjectCatalog(data as UserProfile);
              if (!giltNoch()) return false;
              setCompanyProjectCatalog(projektKatalog);
              // Maengelkatalog (Firmen-Overlay + Vorladen der Suche): best effort und
              // bewusst NICHT abgewartet — er darf die Projektliste nicht verzoegern.
              void ladeMangelKatalog(data as UserProfile);
              // Plotter-Vorlagen des SiGe-Plans (Breiten, Standardbreite und
              // -schrift der Firma): ebenso best effort und nicht abgewartet —
              // bis sie da sind, gilt die Werkseinstellung.
              void ladeSigePlanVorlagen(data as UserProfile);
              // Mit Liste im Geraet steht sie, sobald sie gemalt ist — der
              // Abgleich laeuft dahinter weiter (starteListe).
              if (mitProjekten) await starteListe(userId, runde);
              return true;
          }
          return false;
      } catch (e) { console.error("Profile load error", e); return false; }
      finally { setLoading(false); }
  };

  // Kennungen, die ueber den Realtime-Kanal in die Liste kamen bzw. daraus
  // entfernt wurden, jeweils mit Zeitpunkt. Gelesen beim Zusammenfuehren mit
  // einem Voll-Load: nur die Zeit entscheidet, ob die Serverantwort oder das
  // Ereignis das juengere Wissen ist.
  const realtimeEingefuegtRef = useRef<Map<string, number>>(new Map());
  const realtimeGeloeschtRef = useRef<Map<string, number>>(new Map());
  // Beginn des letzten ERFOLGREICHEN Voll-Loads — der Takt des Nachholens
  // (authRefreshPolicy.erzeugeNachholer).
  const letzterVollLoadRef = useRef<number | null>(null);
  // Der Realtime-Sammler der laufenden Sitzung (realtimeProjectSync): ein
  // Voll-Load erledigt seine aelteren Vormerkungen.
  const projektSammlerRef = useRef<ProjektSammler | null>(null);

  /**
   * Voller Load der Projektliste. Liefert seinen Beginn, `null` bei einem
   * Fehler oder wenn die Sitzungsrunde nicht mehr gilt (`runde` wie bei
   * fetchProfile).
   */
  const fetchProjects = async (_silent = false, runde = sitzungsRundeRef.current.runde): Promise<number | null> => {
      if (!rundeGilt(runde, sitzungsRundeRef.current)) return null;
      try {
          // ─── Vorlauf der Signale (Nr. 175) ──────────────────────────────
          // VOR dem Load die juengsten Signale: das juengste ist danach der
          // Wasserstand der Liste; alle werden jetzt gesichtet (Marke kleiner
          // als die des Loads) und gelten danach als abgedeckt. Zugleich die
          // Antwort, ob es `projekt_signale` gibt.
          let vorlauf: { signale: Signal[]; wasserstand: string | null } | null = null;
          let vorlaufFertigMs = 0;
          let vorlaufNetzfehler = false;
          if (signalModusRef.current !== 'aus') {
              try {
                  vorlauf = await leseSignalVorlauf();
                  vorlaufFertigMs = Date.now();
                  if (!rundeGilt(runde, sitzungsRundeRef.current)) return null;
                  for (const s of vorlauf.signale) sichte(sichtungsbuchRef.current, s);
                  if (signalModusRef.current !== 'an') setzeMitRef(signalModusRef, setSignalModusState, 'an');
              } catch (e) {
                  if (!rundeGilt(runde, sitzungsRundeRef.current)) return null;
                  if (istFehlendeSignalTabelle(e)) {
                      // Nr. 175 fehlt: wie bisher, ohne Geraetespeicher.
                      setzeMitRef(signalModusRef, setSignalModusState, 'aus');
                      void projektListeSpeicher.alleLoeschen();
                  } else {
                      vorlaufNetzfehler = true;
                  }
              }
          }
          // Die Marke dieses Loads (logische Uhr): was davor gesichtet wurde,
          // deckt er ab. Teilt er sich einen Load, den ein anderer Aufruf
          // begonnen hat, gilt dessen Marke; ohne sie (ein Load, den nicht
          // fetchProjects begann) wird erst er abgewartet und dann selbst
          // geladen.
          const aufrufMs = Date.now();
          let vollMarke = naechsteMarke(sichtungsbuchRef.current);
          let load = loadProjectsFromCloudMitBeginn();
          if (load.startedAt < aufrufMs) {
              const geteilt = vollLoadMarkenRef.current.get(load.startedAt);
              if (geteilt !== undefined) {
                  vollMarke = geteilt;
              } else {
                  await load.promise.catch(() => undefined);
                  if (!rundeGilt(runde, sitzungsRundeRef.current)) return null;
                  vollMarke = naechsteMarke(sichtungsbuchRef.current);
                  load = loadProjectsFromCloudMitBeginn();
              }
          }
          vollLoadMarkenRef.current.set(load.startedAt, vollMarke);
          if (vollLoadMarkenRef.current.size > 8) {
              const aeltester = Math.min(...vollLoadMarkenRef.current.keys());
              vollLoadMarkenRef.current.delete(aeltester);
          }
          const { promise, startedAt } = load;
          const data = await promise;
          // Inzwischen ab- oder umgemeldet: die Liste gehoert der alten Sitzung.
          if (!rundeGilt(runde, sitzungsRundeRef.current)) return null;
          // ZUSAMMENFUEHREN statt ersetzen. Der Voll-Load ist eine
          // Momentaufnahme ab `startedAt`. Was der Realtime-Kanal NACH diesem
          // Beginn gemeldet hat, ist juenger als die Antwort:
          //   - eine Anlage nach dem Beginn fehlt in der Antwort -> behalten
          //     (sonst warf setProjects(data) sie hinaus, bis zum naechsten
          //     Voll-Load; Review 2026-09-07, das Rennen gab es schon beim
          //     sequentiellen Lader);
          //   - eine Loeschung nach dem Beginn steht evtl. noch drin ->
          //     herausfiltern (sonst setzte ein veralteter Load sie wieder ein);
          //   - ein Eintrag aus einem Abruf, der NACH diesem Load begann, ist
          //     juenger als seine Zeile -> behalten (Nr. 175, Codex-Review
          //     21.10.2026: sonst stuende V1 da, und die Marke von V2 sagte,
          //     die Signale bis V2 seien abgedeckt — fuer immer falsch).
          // Was VOR dem Beginn gemeldet wurde, hat der Server gesehen: fehlt
          // die Zeile trotzdem, ist sie geloescht oder entzogen, und die
          // Antwort gilt. Vermerke, die aelter sind als der Beginn, sind damit
          // erledigt und werden geraeumt; die Karten wachsen nicht.
          const eingefuegt = realtimeEingefuegtRef.current;
          const geloescht = realtimeGeloeschtRef.current;
          // Einfuegungen: `<=`, Grabsteine: `<`. In derselben Millisekunde
          // wie der Beginn ist nicht zu ordnen, was zuerst war. Eine
          // Einfuegung im Zweifel verwerfen kostet nur, dass ein neues
          // Projekt bis zum naechsten Load fehlt; ein entzogenes Projekt
          // weiter zu zeigen waere der schlechtere Fehler. Ein Grabstein im
          // Zweifel anwenden filtert hoechstens eine Zeile, die ohnehin
          // geloescht ist.
          for (const [id, seit] of eingefuegt) if (seit <= startedAt) eingefuegt.delete(id);
          for (const [id, seit] of geloescht) if (seit < startedAt) geloescht.delete(id);
          // Die Marke haengt am OBJEKT — mit genau diesem Eintrag gelangt sie
          // in die Liste (oder gar nicht).
          const marken = abrufMarkenRef.current;
          for (const p of data) if (p && typeof p === 'object') marken.set(p, vollMarke);
          const markeVonEintrag = (e: unknown) => markeVon(marken, e);
          setProjects(prev => {
              const frisch = behalteJuengere(data.filter((p) => !geloescht.has(p.id)), prev, markeVonEintrag, vollMarke, (id) => geloescht.has(id));
              const bekannt = new Set(frisch.map((p) => p.id));
              const waehrenddessen = prev.filter((p) => !bekannt.has(p.id) && eingefuegt.has(p.id));
              return waehrenddessen.length ? [...waehrenddessen, ...frisch] : frisch;
          });
          // Der Vorlauf zaehlt fuer den Wasserstand nur, wenn er fertig war,
          // BEVOR dieser Load begann — wer sich einen laufenden Load teilt,
          // der frueher begonnen hat, hat seine Signale nach dessen Lesen
          // gesehen (deren Marken sind dann groesser als die des Loads, also
          // ohnehin nicht abgedeckt).
          // Der Beginn dieses Loads ist der juengste VOLL-Load — mit der Liste
          // festgeschrieben (Geraetespeicher) und Massstab fuer den naechsten
          // Pflicht-Voll-Load nach 24 Stunden (Codex-Review 21.10.2026, Runde 2).
          const vorlaufStand = vorlauf && vorlaufFertigMs <= startedAt ? vorlauf.wasserstand : null;
          aendereMitRef(listenStandRef, setListenStand, (s: ListenStand) => ({
              wasserstand: juengererStand(s.wasserstand, vorlaufStand),
              vollLoadAmMs: naechsterVollLoadAmMs(s.vollLoadAmMs, startedAt),
          }));
          if (!vorlauf && vorlaufNetzfehler && signalModusRef.current === 'unbekannt') {
              // Der Vorlauf scheiterte am Netz, der Load nicht: ob es Signale
              // gibt, im Hintergrund klaeren — sonst hoerte die Liste bis zum
              // naechsten Nachholen auf gar nichts.
              void leseSignalVorlauf(1).then(
                  () => { if (rundeGilt(runde, sitzungsRundeRef.current) && signalModusRef.current === 'unbekannt') setzeMitRef(signalModusRef, setSignalModusState, 'an'); },
                  (e) => { if (rundeGilt(runde, sitzungsRundeRef.current) && istFehlendeSignalTabelle(e)) setzeMitRef(signalModusRef, setSignalModusState, 'aus'); },
              );
          }
          letzterGanzerAbgleichRef.current = Math.max(letzterGanzerAbgleichRef.current ?? 0, vollMarke);
          lichteSichtungsbuch(sichtungsbuchRef.current, listenStandRef.current.wasserstand);
          listeGeladenInRundeRef.current = runde;
          setzeMitRef(listeAusCacheRef, setListeAusCacheState, false);
          // Der Load gilt ab seinem Beginn: Takt des Nachholens, und was der
          // Realtime-Sammler davor vorgemerkt hat, steht schon drin.
          letzterVollLoadRef.current = Math.max(letzterVollLoadRef.current ?? 0, startedAt);
          projektSammlerRef.current?.nachVollLoad(startedAt);
          return startedAt;
      } catch (error) { console.error("Error loading projects", error); return null; }
  };

  // ─── Die Liste aus dem Geraet und der Abgleich (Nr. 175) ─────────────────
  //
  // Kaltstart: die Liste aus der IndexedDB malen (projektListeCache.ts), dann
  // nur abgleichen, was sich seit ihrem Wasserstand geaendert hat
  // (projektListeDelta.ts). Voll geladen wird ohne Geraetespeicher, wenn er
  // zu alt ist oder nicht passt, wenn zu viel geaendert ist (> 150 Projekte
  // oder eine volle Seite Signale) oder `projekt_signale` fehlt (dann wie bis
  // 10/2026). Das Nachholen und der Stau des Realtime-Sammlers gleichen
  // ebenfalls nur ab.

  /**
   * Malt die Liste aus dem Geraetespeicher — einmal je Runde, und nur, wenn
   * in dieser Runde noch nichts vom Server kam. Liefert, ob gemalt wurde.
   */
  const maleAusGeraet = async (nutzerId: string, runde: number): Promise<boolean> => {
      const roh = await projektListeSpeicher.lesen(nutzerId);
      if (!rundeGilt(runde, sitzungsRundeRef.current) || listeGeladenInRundeRef.current === runde) return false;
      const pruefung = pruefeCache(roh, { nutzerId, jetztMs: Date.now() });
      if ('verworfen' in pruefung) {
          if (pruefung.verworfen !== 'keiner') console.info('[Projektliste] Geraetespeicher nicht benutzt:', pruefung.verworfen);
          return false;
      }
      // Jeder Eintrag als Listenauszug gekennzeichnet (nie als Projekt
      // speichern, listenAuszug.ts), Marken an genau diesen Objekten,
      // Sichtungsbuch und Uhr laufen weiter; ein Eintrag ohne Marke war beim
      // Speichern lokal veraendert.
      const hergestellt = stelleCacheHer(pruefung.stand);
      abrufMarkenRef.current = hergestellt.marken;
      sichtungsbuchRef.current = hergestellt.sichtungsbuch;
      const gelesenerStand: ListenStand = hergestellt.listenStand;
      aendereListenStand(() => gelesenerStand);
      cacheGeschriebenRef.current = { eintraege: hergestellt.eintraege, listenStand: gelesenerStand };
      zuletztGemerkterWasserstandRef.current = gelesenerStand.wasserstand;
      setzeSignalModus('an');
      setzeListeAusCache(true);
      setProjects(hergestellt.eintraege);
      return true;
  };

  /**
   * Der Abgleich: Signale ab Wasserstand minus zehn Minuten, die sichtbaren
   * Kennungen, dann nur die Projekte, deren Signale die Liste noch nicht
   * abdeckt (projektListeDelta, logische Uhr), in Listenform. Liefert den
   * Beginn (wie ein Voll-Load), `null` bei einem Netzfehler oder einer
   * ungueltigen Runde, `'voll'`, wenn ein Voll-Load der Weg ist.
   */
  const fuehreAbgleichDurch = async (runde: number): Promise<number | null | 'voll'> => {
      const giltNoch = () => rundeGilt(runde, sitzungsRundeRef.current);
      const beginn = Date.now();
      const buch = sichtungsbuchRef.current;
      const marken = abrufMarkenRef.current;
      // Ab hier gilt ein Entfernen: ein Eintrag aus einem spaeter begonnenen
      // Abruf ist juenger als die Kennungsliste dieses Abgleichs.
      const startMarke = naechsteMarke(buch);
      try {
          const abruf: SignalAbruf = await leseSignaleSeit(lesenAb(listenStandRef.current.wasserstand));
          if (!giltNoch()) return null;
          if (abruf.voll) return 'voll';
          for (const s of abruf.signale) sichte(buch, s);
          const sichtbar = await leseSichtbareKennungen();
          if (!giltNoch()) return null;
          const liste = projekteRef.current;
          const jeId = new Map<string, unknown>();
          for (const p of liste) jeId.set(p.id, p);
          const plan = planeDelta({
              liste: jeId.keys(),
              marke: (id) => markeVon(marken, jeId.get(id)),
              signale: abruf.signale,
              sichtung: (zeile) => buch.zeilen.get(zeile),
              sichtbar,
          });
          if (plan.voll) return 'voll';
          // Die Marke des Ladens: alles, was bis hierher gesichtet wurde, deckt es ab.
          const ladeMarke = naechsteMarke(buch);
          const geladen = await ladeListenEintraege(plan.zuLaden, {
              nameAusListe: (id) => projekteRef.current.find((p) => p.id === id)?.name,
          });
          if (!giltNoch()) return null;
          for (const e of geladen) marken.set(e, ladeMarke);
          const geliefert = new Set(geladen.map((e) => e.id));
          // Angefragt, aber nicht geliefert: inzwischen geloescht oder entzogen.
          const entfernen = new Set<string>([...plan.entfernen, ...plan.zuLaden.filter((id) => !geliefert.has(id))]);
          // Wie beim Voll-Load: was vor dem Beginn per Realtime kam, kennt
          // der Abgleich — diese Vermerke sind erledigt.
          for (const [id, seit] of realtimeEingefuegtRef.current) if (seit <= beginn) realtimeEingefuegtRef.current.delete(id);
          for (const [id, seit] of realtimeGeloeschtRef.current) if (seit < beginn) realtimeGeloeschtRef.current.delete(id);
          const kontext = {
              markeVon: (e: unknown) => markeVon(marken, e),
              ladeMarke,
              entfernenMarke: startMarke,
              letzterGanzerAbgleich: letzterGanzerAbgleichRef.current,
              eingefuegtSeit: (id: string) => realtimeEingefuegtRef.current.get(id),
              geloeschtSeit: (id: string) => realtimeGeloeschtRef.current.get(id),
              abfrageBeginnMs: beginn,
          };
          setProjects((prev) => fuehreZusammen(prev, geladen, entfernen, kontext));
          aendereListenStand((s) => {
              const wasserstand = juengererStand(s.wasserstand, plan.wasserstand);
              return wasserstand === s.wasserstand ? s : { ...s, wasserstand };
          });
          letzterGanzerAbgleichRef.current = Math.max(letzterGanzerAbgleichRef.current ?? 0, startMarke);
          lichteSichtungsbuch(buch, listenStandRef.current.wasserstand);
          listeGeladenInRundeRef.current = runde;
          setzeListeAusCache(false);
          letzterVollLoadRef.current = Math.max(letzterVollLoadRef.current ?? 0, beginn);
          projektSammlerRef.current?.nachVollLoad(beginn);
          console.info(`[Projektliste] Abgleich: ${abruf.signale.length} Signale, ${plan.zuLaden.length} geladen, ${entfernen.size} entfernt`);
          return beginn;
      } catch (e) {
          if (!giltNoch()) return null;
          if (istFehlendeSignalTabelle(e)) {
              // Nr. 175 fehlt (oder ist zurueckgenommen): wie bisher.
              setzeSignalModus('aus');
              void projektListeSpeicher.alleLoeschen();
              return 'voll';
          }
          if (istNetzFehler(e, geraetOnline())) {
              // Kein Netz: auch ein Voll-Load kaeme nicht durch. Spaeter
              // nachholen (online, Rueckkehr, Takt).
              console.warn('[Projektliste] Abgleich ohne Netz — spaeter', e);
              return null;
          }
          // Der Server antwortet, aber anders als erwartet: der bisherige Weg.
          console.warn('[Projektliste] Abgleich gescheitert — voller Load', e);
          return 'voll';
      }
  };

  /**
   * Die Liste auf den Stand des Servers bringen — per Abgleich, wenn es
   * Signale und einen Wasserstand gibt, sonst voll. Je Runde laeuft hoechstens
   * einer; wer waehrenddessen ruft, bekommt denselben Lauf. Liefert den
   * Beginn, `null` bei einem Fehlschlag (wie fetchProjects).
   */
  const aktualisiereListe = (runde: number = sitzungsRundeRef.current.runde): Promise<number | null> => {
      const laufend = listenAbgleichRef.current;
      if (laufend && laufend.runde === runde) return laufend.lauf;
      const lauf: Promise<number | null> = (async () => {
          if (!rundeGilt(runde, sitzungsRundeRef.current)) return null;
          // Abgleich nur mit Signalen, Wasserstand und einem Voll-Load der
          // letzten 24 Stunden — sonst voll (ein verlorenes Signal heilt nur der).
          if (signalModusRef.current === 'an' && listenStandRef.current.wasserstand
              && !brauchtVollLoad(listenStandRef.current.vollLoadAmMs, Date.now())) {
              const ergebnis = await fuehreAbgleichDurch(runde);
              if (ergebnis !== 'voll') return ergebnis;
          }
          return fetchProjects(true, runde);
      })();
      const eintrag = { runde, lauf };
      listenAbgleichRef.current = eintrag;
      const fertig = () => {
          if (listenAbgleichRef.current === eintrag) listenAbgleichRef.current = null;
          // Gescheitert und die Liste noch aus dem Geraet: wer darauf wartet
          // (Oeffnen, Verschieben), erfaehrt es jetzt und nicht erst nach
          // seiner Zeitgrenze. Gelungen weckt der Effekt nach dem Festschreiben.
          if (listeAusCacheRef.current && !listenAbgleichRef.current) weckeFrischWartende(false);
      };
      lauf.then(fertig, fertig);
      return lauf;
  };

  /**
   * Der Start der Liste einer Sitzung: erst aus dem Geraet malen (dann loest
   * das Versprechen auf, der Abgleich laeuft dahinter), sonst voll laden (dann
   * erst nach dem Load). Geteilt je Runde; ein gescheiterter Start darf beim
   * naechsten Anlass (erneutes Auth-Ereignis) wieder.
   */
  const starteListe = (nutzerId: string, runde: number): Promise<void> => {
      const vorher = listenStartRef.current;
      if (vorher && vorher.runde === runde && (vorher.laeuft || vorher.erfolgreich)) return vorher.bereit;
      const ersterVersuch = !vorher || vorher.runde !== runde;
      const start: { runde: number; laeuft: boolean; erfolgreich: boolean; bereit: Promise<void> } =
          { runde, laeuft: true, erfolgreich: false, bereit: Promise.resolve() };
      const abgeschlossen = (beginn: number | null) => {
          start.laeuft = false;
          start.erfolgreich = beginn !== null;
      };
      start.bereit = (async () => {
          if (!rundeGilt(runde, sitzungsRundeRef.current)) { abgeschlossen(null); return; }
          if (ersterVersuch) {
              // Listen anderer Nutzer gehoeren nicht auf dieses Geraet.
              void projektListeSpeicher.andereLoeschen(nutzerId);
              const gemalt = await maleAusGeraet(nutzerId, runde);
              if (!rundeGilt(runde, sitzungsRundeRef.current)) { abgeschlossen(null); return; }
              if (gemalt) {
                  aktualisiereListe(runde).then(abgeschlossen, () => abgeschlossen(null));
                  return;
              }
          }
          abgeschlossen(await aktualisiereListe(runde).catch(() => null));
      })();
      listenStartRef.current = start;
      return start.bereit;
  };

  // ─── Projektliste aktuell halten ──────────────────────────────────────────
  //
  // Drei Wege, und nur diese (authRefreshPolicy.ts, realtimeProjectSync.ts,
  // projektListeDelta.ts):
  //   1. Anmeldung bzw. Nutzerwechsel: die Liste aus dem Geraet, dann der
  //      Abgleich — ohne Geraetespeicher voller Load (Effekt oben).
  //   2. Realtime: einzelne Eintraege, gesammelt, in Listenform. Mit Nr. 175
  //      auf `projekt_signale` (nur Kennung und Zeitpunkt; bekannte Staende
  //      werden uebersprungen, nach jedem Beitritt wird die Luecke aus der
  //      Tabelle nachgezogen), ohne Nr. 175 wie bisher auf `projects` (das
  //      eigene Echo und bekannte Staende werden uebersprungen).
  //   3. Nachholen: zehn Minuten weg, `online`, Kanal nach Abriss wieder
  //      da — alle zusammen hoechstens einmal je zehn Minuten; ein
  //      gebremster Bedarf wird gemerkt und spaeter eingeloest. Mit Signalen
  //      ein Abgleich statt eines Voll-Loads.
  // Die Rueckkehr aus der Kamera laedt nichts. Bis 10/2026 lud jede davon
  // Profil und volle Liste (etwa 1,4 MB) und baute diesen Kanal neu auf.
  //
  // Der Effekt baut sich neu auf, wenn sich `signalModus` klaert (einmal je
  // Sitzung: beim Malen aus dem Geraet oder mit dem ersten Vorlauf).
  //
  // Hier unter fetchProjects und ueber den fruehen Returns: der Effekt
  // schliesst fetchProfile und fetchProjects, beide stehen darueber und
  // lesen nur Setter und Refs (TDZ-Lehre vom 13.09.2026).
  useEffect(() => {
      if (!sitzungsNutzerId) return;
      const nutzerId = sitzungsNutzerId;
      // Die Runde dieser Sitzung (sitzungsRundeRef): das Abmelden zaehlt sie
      // sofort im Auth-Rueckruf weiter, noch bevor dieser Effekt abgebaut
      // ist. Jeder Ladeweg aus diesem Effekt gibt sie AUSDRUECKLICH mit —
      // ein Zeitgeber, der im Fenster dazwischen feuert, griffe sonst die
      // Runde des neuen Nutzers ab.
      const runde = sitzungsRundeRef.current.runde;
      const versteckt = () => document.visibilityState === 'hidden';
      let verstecktSeit: number | null = versteckt() ? Date.now() : null;
      // Worauf die Liste in diesem Aufbau hoert (siehe oben).
      const signaleAn = signalModus === 'an';
      let abgebaut = false;

      // Ein gebremster Bedarf wird gemerkt und zum ersten erlaubten
      // Zeitpunkt eingeloest, verborgen beim Sichtbarwerden.
      const nachholer = erzeugeNachholer({
          istVersteckt: versteckt,
          letzterVollLoadMs: () => letzterVollLoadRef.current,
          laden: () => {
              // Profil (Rechte, Firma, Kataloge) daneben, nicht davor — die
              // Liste wartet nicht auf die Einstellungen.
              void fetchProfile(nutzerId, false, runde);
              // Mit Signalen ein Abgleich, sonst voll (aktualisiereListe).
              return aktualisiereListe(runde);
          },
      });

      /**
       * Kennt die Liste diese Signalzeile schon? Ja, wenn der Eintrag da ist
       * und aus einem Abruf stammt, der begann, NACHDEM die Zeile gesehen
       * wurde (projektListeDelta, logische Uhr).
       */
      const signalSchonBekannt = (id: string, zeile: number): boolean => {
          const eintrag = projekteRef.current.find((p) => p.id === id);
          return eintrag !== undefined
              && istAbgedeckt(sichtungsbuchRef.current.zeilen.get(zeile), markeVon(abrufMarkenRef.current, eintrag));
      };
      // Die Marke des laufenden Abrufs des Sammlers — er laedt nie zweimal
      // zugleich, und `uebernehmen` folgt immer auf das `laden` desselben Laufs.
      let sammlerMarke = 0;

      const sammler = erzeugeProjektSammler({
          istVersteckt: versteckt,
          // Signalmodus: die logische Uhr (projektListeDelta). Sonst der
          // Listeneintrag, nicht der Anker: den setzt auch das blosse
          // Oeffnen eines Projekts (realtimeProjectSync, Regel 1).
          istBekannt: signaleAn
              ? (id, neu) => {
                  const zeile = zeileDerVormerkung(neu);
                  return zeile !== null && signalSchonBekannt(id, zeile);
              }
              : (id, neu) => istBekannterStand(neu, projekteRef.current.find((p) => p.id === id)),
          // Das eigene Speichern des offenen Projekts abwarten: erst seine
          // Antwort landet im Listeneintrag, an dem das Echo erkannt wird. Ein
          // Ref — der fruehere Riegel las `saveStatus` aus einer Closure, die
          // beim Aufbau des Kanals eingefroren war, und griff praktisch nie.
          istBeschaeftigt: (id) => id === currentProjectIdRef.current && saveStatusRef.current === 'saving',
          laden: (vormerkungen) => {
              // Alles, was bis jetzt gesichtet wurde, deckt dieser Abruf ab.
              sammlerMarke = naechsteMarke(sichtungsbuchRef.current);
              return ladeRealtimeEintraege(vormerkungen, {
                  nameAusListe: (id) => projekteRef.current.find((p) => p.id === id)?.name,
              });
          },
          // Nur solange der Sammler lebt — nach Abmelden oder Nutzerwechsel
          // ruft er das nicht mehr.
          uebernehmen: (eintraege, abfrageBeginnMs, vormerkungen) => {
              if (!rundeGilt(runde, sitzungsRundeRef.current)) return;
              // Waehrend der Abfrage geloescht? Dann ist die Antwort veraltet.
              const frisch = eintraege.filter((e) => {
                  const geloeschtSeit = realtimeGeloeschtRef.current.get(String(e.id));
                  return geloeschtSeit === undefined || geloeschtSeit < abfrageBeginnMs;
              });
              // Neu in der Liste: merken, WANN die Zeile ueber den Kanal kam —
              // ein Voll-Load, der frueher begonnen hat, kennt sie noch nicht
              // und darf sie beim Zusammenfuehren nicht hinauswerfen.
              const alsEingefuegt = (id: string) => { realtimeEingefuegtRef.current.set(id, Date.now()); };
              if (!signaleAn) {
                  if (frisch.length === 0) return;
                  // Ein Voll-Load, der waehrend der Abfrage lief, weiss es besser.
                  const kontext = { abfrageBeginnMs, vollLoadMs: letzterVollLoadRef.current };
                  setProjects((prev) => fuegeRealtimeEintraegeEin(prev, frisch, alsEingefuegt, kontext));
                  return;
              }
              // Signalmodus: die Marke dieses Abrufs an die Objekte; was
              // angefragt, aber nicht geliefert wurde, ist geloescht oder
              // entzogen. Ueber Ersetzen, Einfuegen und Entfernen entscheidet
              // die logische Uhr (projektListeDelta.fuehreZusammen).
              const marke = sammlerMarke;
              const marken = abrufMarkenRef.current;
              for (const e of frisch) marken.set(e, marke);
              const geliefert = new Set(frisch.map((e) => e.id));
              const weg = new Set([...vormerkungen.keys()].filter((id) => !geliefert.has(id)));
              // Grabstein mit Zeit: ein Abruf, der vorher begann, setzt das
              // Projekt nicht wieder ein.
              for (const id of weg) realtimeGeloeschtRef.current.set(id, Date.now());
              if (frisch.length === 0 && weg.size === 0) return;
              // `unknown`: `e.id` der Antwort ist ungetypt, der Lader liefert
              // (noch) `Zeile` statt `ListProject` (realtimeProjektLader.ts).
              const vorhanden = new Set<unknown>(projekteRef.current.map((p) => p.id));
              for (const e of frisch) if (!vorhanden.has(e.id)) alsEingefuegt(String(e.id));
              const kontext = {
                  markeVon: (e: unknown) => markeVon(marken, e),
                  ladeMarke: marke,
                  entfernenMarke: marke,
                  letzterGanzerAbgleich: letzterGanzerAbgleichRef.current,
                  eingefuegtSeit: (id: string) => realtimeEingefuegtRef.current.get(id),
                  geloeschtSeit: (id: string) => realtimeGeloeschtRef.current.get(id),
                  abfrageBeginnMs,
              };
              setProjects((prev) => fuehreZusammen(prev, frisch, weg, kontext));
          },
          vollLaden: () => nachholer.melde('stau', false),
      });
      projektSammlerRef.current = sammler;

      const beiProjektAenderung = (payload: unknown) => {
          const aenderung = leseProjektAenderung(payload);
          if (aenderung.art === 'loeschen') {
              // Grabstein mit Zeit: ein Voll-Load oder eine Einzelabfrage, die
              // VOR dieser Loeschung begonnen hat, kann die Zeile noch liefern
              // und darf sie nicht wieder einsetzen.
              realtimeGeloeschtRef.current.set(aenderung.id, Date.now());
              realtimeEingefuegtRef.current.delete(aenderung.id);
              sammler.vergiss(aenderung.id);
              setProjects(prev => prev.filter(p => p.id !== aenderung.id));
              return;
          }
          if (aenderung.art === 'merken') {
              sammler.melde(aenderung.id, aenderung.neu, aenderung.fehler);
              return;
          }
          // Ohne Kennung ist nichts einzeln nachzuziehen — gedrosselt voll.
          void nachholer.melde('stau');
      };

      // ─── Signalmodus (Nr. 175): eine Signalzeile — Kennung und Zeitpunkt
      const verarbeiteSignal = (signal: Signal) => {
          if (abgebaut || !rundeGilt(runde, sitzungsRundeRef.current)) return;
          sichte(sichtungsbuchRef.current, signal);
          kanalWasserstandRef.current = juengererStand(kanalWasserstandRef.current, signal.stand);
          const id = signal.id;
          if (signal.geloescht) {
              sammler.vergiss(id);
              // Grabstein mit Zeit — wie das DELETE auf `projects`: ein Abruf,
              // der vorher begann, setzt die Zeile nicht wieder ein.
              realtimeGeloeschtRef.current.set(id, Date.now());
              realtimeEingefuegtRef.current.delete(id);
              // Die Liste nur anfassen, wenn der Eintrag drinsteht: das
              // Nachziehen liefert auch alte Grabsteine, und jedes
              // `setProjects` kostet einen Render.
              if (!projekteRef.current.some((p) => p.id === id)) return;
              setProjects((prev) => (prev.some((p) => p.id === id) ? prev.filter((p) => p.id !== id) : prev));
              return;
          }
          if (signalSchonBekannt(id, signal.zeile)) return;
          sammler.melde(id, signalVormerkung(signal));
      };
      const beiSignal = (payload: unknown) => {
          if (abgebaut || !rundeGilt(runde, sitzungsRundeRef.current)) return;
          const aenderung = leseSignalAenderung(payload);
          if (aenderung.art === 'ignorieren') return;
          if (aenderung.art === 'unbekannt') { void nachholer.melde('stau'); return; }
          verarbeiteSignal(aenderung.signal);
      };

      /**
       * Nach jedem Beitritt des Kanals: was zwischen dem letzten Abruf und dem
       * Beitritt kam, aus der Tabelle nachziehen — ab dem juengeren von
       * Wasserstand und letztem Kanal-Signal, minus Ueberlappung. Ein kleiner
       * Abruf; schon abgedeckte Zeilen fallen heraus. Erst nach einem
       * laufenden Abgleich (er setzt Wasserstand und Marken).
       */
      let ziehtNach = false;
      const signaleNachziehen = async () => {
          if (ziehtNach) return;
          ziehtNach = true;
          try {
              const laufend = listenAbgleichRef.current;
              if (laufend) await laufend.lauf.catch(() => null);
              if (abgebaut || !rundeGilt(runde, sitzungsRundeRef.current)) return;
              const basis = juengererStand(listenStandRef.current.wasserstand, kanalWasserstandRef.current);
              // Ohne Stand gibt es nichts nachzuziehen: der erste Abgleich bzw.
              // Voll-Load deckt die Zeit bis jetzt.
              if (!basis) return;
              const abruf = await leseSignaleSeit(lesenAb(basis));
              if (abgebaut || !rundeGilt(runde, sitzungsRundeRef.current)) return;
              if (abruf.voll) { void nachholer.melde('stau'); return; }
              for (const s of abruf.signale) verarbeiteSignal(s);
          } catch (e) {
              if (istFehlendeSignalTabelle(e) && rundeGilt(runde, sitzungsRundeRef.current)) {
                  setzeSignalModus('aus');
                  void projektListeSpeicher.alleLoeschen();
              }
              // Sonst (kein Netz): beim naechsten Beitritt oder Nachholen.
          } finally {
              ziehtNach = false;
          }
      };

      // Selbstheilend: nach Standby lehnt Realtime das abgelaufene JWT ab
      // (InvalidJWTToken "exp") — Recovery refresht und baut den Channel neu.
      // Steht er nach einem Abriss wieder, fehlt, was in der Luecke kam —
      // im Signalmodus zieht `signaleNachziehen` es aus der Tabelle, sonst
      // das gedrosselte Nachholen.
      // Solange unklar ist, ob es Signale gibt, hoert die Liste auf nichts
      // (der erste Abgleich bzw. Voll-Load laeuft ohnehin gerade). Auf die
      // Signale nur INSERT: das Ausduennen alter Zeilen (DELETE) geht den
      // Client nichts an und kaeme sonst bei jedem Abonnenten an.
      // `profiles` steht nicht in der Publikation (Stand 10/2026); die
      // Bindung bleibt, frischt aber nur noch das EIGENE Profil auf.
      const handle = subscribeWithAuthRecovery(supabase, () => {
          let kanal = supabase.channel('global-app-changes');
          if (signaleAn) {
              kanal = kanal.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'projekt_signale' }, beiSignal);
          } else if (signalModus === 'aus') {
              kanal = kanal.on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, beiProjektAenderung);
          }
          return kanal.on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, (payload: unknown) => {
              const zeile = (payload as { new?: { id?: unknown } } | null)?.new;
              if (zeile?.id === nutzerId) void fetchProfile(nutzerId, false, runde);
          });
      }, signaleAn
          ? { onSubscribed: () => { void signaleNachziehen(); } }
          : { onResubscribed: () => { void nachholer.melde('kanal'); } });
      realtimeChannelRef.current = handle;

      // Sichtbarkeit selbst mitschreiben: wie lange die App weg war,
      // entscheidet, ob die Rueckkehr nachholt (Kamera: nein).
      const beiSichtbarkeit = () => {
          if (versteckt()) {
              if (verstecktSeit === null) verstecktSeit = Date.now();
              return;
          }
          const rueckkehr = bildeRueckkehr(verstecktSeit, Date.now());
          verstecktSeit = null;
          // Erst das Nachholen: laedt es die ganze Liste, sind die
          // Vormerkungen damit erledigt (fetchProjects → nachVollLoad).
          const lauf = nachholer.sichtbar(rueckkehr);
          if (lauf) void lauf.then((beginn) => { if (beginn === null) sammler.anstossen(); });
          else sammler.anstossen();
      };
      const beiOnline = () => {
          const lauf = nachholer.melde('online');
          if (lauf) void lauf.then((beginn) => { if (beginn === null) sammler.anstossen(); });
          else sammler.anstossen();
      };
      document.addEventListener('visibilitychange', beiSichtbarkeit);
      window.addEventListener('online', beiOnline);
      return () => {
          abgebaut = true;
          document.removeEventListener('visibilitychange', beiSichtbarkeit);
          window.removeEventListener('online', beiOnline);
          handle.close();
          nachholer.abbauen();
          sammler.abbauen();
          if (projektSammlerRef.current === sammler) projektSammlerRef.current = null;
      };
  }, [sitzungsNutzerId, kanalRunde, signalModus]);

  // ─── Die Liste ins Geraet (Nr. 175, projektListeCache.ts) ────────────────
  //
  // Gespeichert wird der zuletzt FESTGESCHRIEBENE Stand — Liste, Wasserstand
  // und Staende aus demselben Render (Schnappschuss unten), nie ein Wasserstand,
  // dessen Eintraege noch nicht in der Liste stehen. Nach einem Abgleich bald,
  // nach Realtime oder eigenen Aenderungen gesammelt, beim Verbergen des Tabs
  // sofort (danach stirbt die Seite auf Android oft). Nur im Signalmodus, nie
  // solange die Liste selbst noch aus dem Geraet kommt.
  useEffect(() => {
      if (!sitzungsNutzerId) return;
      const nutzerId = sitzungsNutzerId;
      const runde = sitzungsRundeRef.current.runde;
      const speicherung = erzeugeCacheSpeicherung({
          schreiben: async () => {
              // Abgemeldet, anderer Nutzer, keine Signale: nichts schreiben.
              if (!rundeGilt(runde, sitzungsRundeRef.current) || signalModusRef.current !== 'an') return;
              const schnapp = cacheSchnappschussRef.current;
              if (!schnapp || schnapp.ausCache) return;
              const geschrieben = cacheGeschriebenRef.current;
              if (geschrieben && geschrieben.eintraege === schnapp.eintraege && geschrieben.listenStand === schnapp.listenStand) return;
              const stand = baueCacheStand({
                  nutzerId,
                  jetztMs: Date.now(),
                  wasserstand: schnapp.listenStand.wasserstand,
                  vollLoadAmMs: schnapp.listenStand.vollLoadAmMs,
                  eintraege: schnapp.eintraege,
                  // Die Marken haengen an genau den Objekten des Schnappschusses;
                  // das Sichtungsbuch darf voraus sein — eine Sichtung ohne
                  // passenden Abruf heisst nur: nachladen.
                  markeVon: (e) => markeVon(abrufMarkenRef.current, e),
                  sichtungsbuch: sichtungsbuchRef.current,
              });
              if (stand && await projektListeSpeicher.schreiben(stand)) {
                  cacheGeschriebenRef.current = { eintraege: schnapp.eintraege, listenStand: schnapp.listenStand };
              }
          },
      });
      cacheSpeicherungRef.current = speicherung;
      const beiVerbergen = () => { if (document.visibilityState === 'hidden') speicherung.sofort(); };
      const beiVerlassen = () => speicherung.sofort();
      document.addEventListener('visibilitychange', beiVerbergen);
      window.addEventListener('pagehide', beiVerlassen);
      return () => {
          document.removeEventListener('visibilitychange', beiVerbergen);
          window.removeEventListener('pagehide', beiVerlassen);
          speicherung.abbauen();
          if (cacheSpeicherungRef.current === speicherung) cacheSpeicherungRef.current = null;
      };
  }, [sitzungsNutzerId]);

  useEffect(() => {
      cacheSchnappschussRef.current = { eintraege: projects, listenStand, ausCache: listeAusCache };
      if (signalModus !== 'an' || !listenStand.wasserstand || listeAusCache) return;
      // Der Wasserstand ist gestiegen (Abgleich, Voll-Load): bald speichern.
      const dringend = listenStand.wasserstand !== zuletztGemerkterWasserstandRef.current;
      zuletztGemerkterWasserstandRef.current = listenStand.wasserstand;
      cacheSpeicherungRef.current?.merke(dringend);
  }, [projects, listenStand, listeAusCache, signalModus]);
  
  const handleLogout = async () => {
      // ─── Reihenfolge ─────────────────────────────────────────────────────
      // VOR dem Abmelden steht nur, was ein gueltiges Token braucht oder zum
      // Verlassen des Projekts gehoert: Sperre freigeben, Schliessen erfassen,
      // Autosave anhalten, Bericht und Ansicht zuruecksetzen. Alles, was erst
      // im abgemeldeten Zustand Sinn ergibt (Projektliste, Navigationsspur,
      // Ansichtsgedaechtnis), steht UNTEN hinter dem Erfolg — scheitert
      // `signOut`, lebt die Sitzung weiter, und eine geleerte Projektliste
      // bliebe leer: der Ladeeffekt haengt an `session` und laeuft ohne
      // Sitzungswechsel nicht erneut.
      if (realtimeChannelRef.current) realtimeChannelRef.current.close();
      // Abmelden verlaesst das Projekt ebenfalls: ohne Reset zeigt die
      // Seitenleiste nach dem naechsten Anmelden weiter "Nicht gespeichert
      // (Nur-Ansicht)" — displayedSaveStatus haengt allein an der Flagge.
      setProjectReadOnly(false);
      setExplicitReadOnly(false);
      // Abmelden heisst Projekt verlassen — die Sperre muss mit, sonst blockiert
      // ein Feierabend-Logout das Projekt noch drei Minuten fuer alle anderen.
      // Vor signOut(), solange das Token noch gilt.
      //
      // Aus demselben Grund steht das Schliessen der Aktivitaetserfassung
      // hier oben und nicht am Ende: nach dem Abmelden gaebe es kein Token
      // mehr, mit dem die Zeile durch die RLS-Pruefung kaeme. Hat der
      // Anwender das Projekt vorher schon ueber den Zurueck-Knopf verlassen,
      // passiert nichts — die Wache in der Erfassung schreibt je Oeffnen
      // hoechstens ein Schliessen.
      recordProjectClose({ projectId: currentProjectId, reason: 'logout' });
      releaseHeldLock();
      setLockBlocked(false);
      resetForeignAccess();
      // Mitteilungen des bisherigen Nutzers nicht fuer den naechsten stehen lassen.
      resetNotifications();
      setProjectLock(null);
      // ─── Und das Projekt selbst muss weg ──────────────────────────────────
      //
      // Die App wird beim Abmelden NICHT demontiert (`!session` liefert nur den
      // Anmeldebildschirm zurueck, der Zustand darunter bleibt stehen). Wer
      // sich ohne Neuladen als naechster anmeldet, sah deshalb bisher den
      // Bericht seines Vorgaengers: `view` stand noch auf 'project-hub',
      // `report` war gefuellt — und der Anwesenheits-Effekt forderte fuer den
      // neuen Nutzer prompt die Sperre auf dem fremden Projekt an. Also alles
      // abraeumen, was am Projekt haengt; die Navigationsspur im
      // sessionStorage ist oben schon geloescht.
      cancelPendingAutosave();
      setReport(null);
      setCurrentProjectId(null);
      // Auch die hinterlegten Buchungsarten: sie gehoeren zum Projekt des
      // Vorgaengers, und der naechste Anwender oeffnet seines von vorn.
      leereBuchungsartenRegister();
      setActiveSection('items');
      setView('dashboard');
      setPreviousView('dashboard');
      setAccountingFocusId(null);
      setCrmFocusCustomerId(null);
      setPendingStatusChange(null);
      setSaveStatus('idle');
      leftProjectViewAtRef.current = null;
      // Ueber den Wrapper: er setzt das Merkzeichen, meldet ab und nimmt es im
      // `finally` wieder zurueck. Das gleich folgende SIGNED_OUT ist kein
      // Widerruf von aussen, sondern dieser Knopf — der Tab darf deshalb NICHT
      // neu laden (sessionRevocation.ts).
      const { error: abmeldeFehler } = await abmelden(() => supabase.auth.signOut());
      if (abmeldeFehler) {
          // Kein `setSession(null)`: `signOut` gibt bei einem Netzfehler
          // `{ error }` zurueck, BEVOR es die Sitzung lokal abbaut — es gab
          // kein SIGNED_OUT, das Token lebt. Wuerde die Anmeldemaske jetzt
          // trotzdem erscheinen, glaubte der Anwender, er sei abgemeldet,
          // waehrend seine Sitzung weiterlaeuft.
          //
          // Was vor `signOut` schon abgeraeumt wurde (Projekt geschlossen,
          // Sperre freigegeben, Bericht und Ansicht zurueckgesetzt), bleibt
          // abgeraeumt. Das ist tragbar: der Anwender landet in der
          // Projektliste — dieselbe Lage wie nach dem Zurueck-Knopf, nur dass
          // er angemeldet bleibt. Das Gegenteil (Sperre halten, ohne es zu
          // wissen) waere schlechter. Die Projektliste selbst steht noch, sie
          // wird erst unten geleert.
          //
          // Der Realtime-Kanal oben ist schon zu. Er haengt nur noch an der
          // Nutzerkennung ("Projektliste aktuell halten"), und die bleibt
          // dieselbe — also ausdruecklich neu aufbauen.
          setKanalRunde((n) => n + 1);
          console.warn('[auth] Abmelden fehlgeschlagen', abmeldeFehler);
          // Nach VORN: die Sitzung laeuft weiter, obwohl der Anwender sie
          // beenden wollte. Das muss er sofort sehen und nicht erst, wenn er
          // sich durch die aelteren Hinweise geklickt hat.
          ergaenzeQueueHinweis({ message: ABMELDEN_FEHLGESCHLAGEN }, { vorne: true });
          return;
      }
      // ─── Ab hier ist die Abmeldung durch ────────────────────────────────
      setSession(null);
      setUserProfile(null);
      // Die naechste Anmeldung laedt wieder alles (authRefreshPolicy).
      profilGeladenFuerRef.current = null;
      geladenesProfilRef.current = null;
      // Die Projektliste gehoert zur alten Sitzung; der naechste Login laedt
      // sie ueber den Session-Effekt neu.
      setProjects([]);
      // Ebenso die Liste im Geraet und was der Abgleich sich gemerkt hat
      // (Nr. 175). Das SIGNED_OUT hat beides schon geleert — doppelt haelt
      // besser, falls es ausblieb; beides ist harmlos wiederholbar.
      vergissListenAbgleich();
      void projektListeSpeicher.alleLoeschen();
      clearNavState(window.sessionStorage);
      // Reiter, Filter, Bildlaufpositionen des Vorgaengers gehen den
      // naechsten Nutzer an diesem Tab nichts an.
      forgetViewMemory();
      // Der Restore laeuft je Sitzung genau einmal — nach dem naechsten Login
      // darf er wieder greifen. Zusammen mit den beiden Zeilen darueber: zu
      // zweit ergeben sie nur im abgemeldeten Zustand ein stimmiges Bild —
      // stuende der Merker allein schon vor `signOut` auf false, fuehre der
      // Restore bei einem Fehlschlag in das eben verlassene Projekt zurueck.
      restoredNavRef.current = false;
      // Gehoert zur alten Sitzung: ohne Reset traege die naechste Anmeldung
      // im selben Tab bis zum ersten Profil-Laden die Einstellungen der
      // vorigen Firma.
      setCompanyInvoiceConfig(undefined);
      setCompanyProjectCatalog(undefined);
      clearCompanyProjectCatalog();
      leereMangelKatalog();
      leereSigePlanVorlagen();
  };

  // Stabile Referenzen fuer die Rechnungsansicht: beide haengen als
  // Abhaengigkeiten am Vorschau-Effekt des Rechnungseditors. Frisch erzeugt
  // rendert jeder App-Render das PDF neu.
  // Der Block der FIRMA, nicht der des angemeldeten Nutzers: eingetragen wird
  // er im Einstellungsdialog des Admins, gelten soll er fuer jeden, der eine
  // Rechnung schreibt (companyInvoiceConfig.ts). Bis er geladen ist, gilt der
  // eigene Block — das ist beim Admin ohnehin derselbe.
  const invoiceConfigMemo = useMemo(
      () => resolveInvoiceConfig(companyInvoiceConfig ?? (userProfile as any)?.settings?.invoiceConfig),
      [companyInvoiceConfig, userProfile],
  );

  // Globaler Briefkopf (settings.letterheadConfig) in die QuoteConfig-Form
  // gemischt, die alle PDF-Pfade lesen. Einzige Lesestelle — Views und
  // Editoren bekommen nur noch das Ergebnis.
  const quoteConfigMemo = useMemo(
      () =>
          applyLetterheadConfig(
              (userProfile as any)?.settings?.quoteConfig,
              (userProfile as any)?.settings?.letterheadConfig,
          ),
      [userProfile],
  );

  const invoiceCompany = useMemo(
      () => ({
          name: (userProfile as any)?.company_name,
          address: (userProfile as any)?.company_address,
          logo: (userProfile as any)?.company_logo,
          // resolveFooterText statt ||: ein bewusst geleertes Feld ('') muss
          // leer bleiben duerfen. Nur ein nie gepflegtes Feld faellt auf die
          // Anschrift zurueck.
          footerText: resolveFooterText(
              (userProfile as any)?.settings?.footerText,
              (userProfile as any)?.company_address,
          ),
          senderLine: (userProfile as any)?.settings?.senderLine,
      }),
      [userProfile],
  );

  const incomingTransfers = useMemo(() => {
      // Aus dem Geraetespeicher (Nr. 175) keine Uebertragungs-Abfrage: eine
      // Uebertragung von gestern ist vielleicht laengst beantwortet.
      if (!userProfile || listeAusCache) return [];
      return projects.filter(p => p.transfer_target_id === userProfile.id);
  }, [projects, userProfile, listeAusCache]);

  useEffect(() => {
      const fetchSenders = async () => {
          if (incomingTransfers.length === 0) return;
          const uniqueIds = Array.from(new Set(incomingTransfers.map(p => p.user_id))) as string[];
          const { data } = await supabase.from('profiles').select('id, first_name, last_name, company_name').in('id', uniqueIds);
          setSenderNames(prev => {
              const next = { ...prev };
              uniqueIds.forEach(id => { if (!next[id]) next[id] = 'Unbekannter Absender'; });
              if (data) {
                  data.forEach((u: any) => {
                      const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                      const company = u.company_name ? `(${u.company_name})` : '';
                      next[u.id] = `${name} ${company}`.trim() || 'Namenslos';
                  });
              }
              return next;
          });
      };
      fetchSenders();
  }, [incomingTransfers]);


  // `openReason` ist reine Erfassung und aendert am Oeffnen selbst nichts. Der
  // Vorgabewert 'normal' gilt fuer JEDEN Aufruf aus der Oberflaeche — Klick in
  // der Projektliste, im Dashboard, in der Karte, aus einer Rechnung heraus.
  // 'resume' setzt nur der automatische Wiederherstellungsweg nach einem
  // Neuladen der Seite (siehe den Restore-Effekt weiter oben): dort hat
  // niemand ein Projekt geoeffnet, der Anwender sass schon darin.
  const handleOpenProject = async (project: ZuOeffnendesProjekt, isReadOnly = false, openReason: UserActivityOpenReason = 'normal') => {
      // ─── Beendet/Archiv: für Mitarbeiter zu ───────────────────────────────
      //
      // Vor ALLEM anderen: keine Sperre freigeben oder nehmen, nichts laden,
      // nichts schreiben. Der Anwender bleibt, wo er war. Gilt auch für den
      // Restore nach F5 und den Lesemodus — gesperrt heißt nicht „nur
      // ansehen". Regel: services/projectOpenGate.ts.
      //
      // Kennen weder Aufrufer noch Liste den Status (`{ id }` vom Angebot,
      // Zeile noch nicht in der Liste), wird die Zeile LESEND nachgeladen —
      // ein beendetes Projekt käme sonst ungeprüft durch. Admins brauchen
      // das nicht. Scheitert auch das, geht es weiter wie bisher: ohne Status
      // lässt sich nichts sperren, und das Laden gleich darauf scheitert
      // dann in aller Regel ebenso.
      //
      // Kommt die Liste noch aus dem Geraetespeicher (Kaltstart, Nr. 175),
      // kann die Zeile von gestern sein — Status und Zustaendigkeit darf sie
      // nicht entscheiden. Erst den Abgleich abwarten (hoechstens acht
      // Sekunden) und mit der FRISCHEN Zeile weiter; steht die Liste danach
      // noch aus dem Speicher (offline), geht es weiter wie mit `{ id }`: die
      // Zeile wird einzeln nachgeladen.
      let listenZeile = projects.find((p) => p.id === project?.id);
      if (listeAusCacheRef.current && project?.id) {
          const rundeBeimOeffnen = sitzungsRundeRef.current.runde;
          const frisch = await warteAufFrischeListe(8000);
          // Waehrend des Wartens ab- oder umgemeldet: dieses Oeffnen gehoert
          // der alten Sitzung (Codex-Review 21.10.2026) — nichts sperren,
          // nichts laden, nichts schreiben.
          if (!rundeGilt(rundeBeimOeffnen, sitzungsRundeRef.current)) return false;
          const frischeZeile = frisch ? projekteRef.current.find((p) => p.id === project.id) : undefined;
          project = frischeZeile ?? { id: project.id };
          listenZeile = frischeZeile;
      }
      let nachgeladeneZeile: Awaited<ReturnType<typeof loadSingleProjectOverview>> = null;
      // Beendete Projekte öffnen Admins und wer Projekte archivieren darf
      // (Buchhaltung, Disposition): Schlussrechnung und Wiederherstellen.
      const darfBeendeteOeffnen = isAdminRole(userProfile?.role) || rechte.hat('projekte.archivieren');
      if (project?.id && !darfBeendeteOeffnen && !hatProjektStatus(project) && !hatProjektStatus(listenZeile)) {
          nachgeladeneZeile = await loadSingleProjectOverview(project.id).catch(() => null);
      }
      if (!darfBeendeteOeffnen && istProjektGesperrtFuer(userProfile?.role, project, listenZeile, nachgeladeneZeile)) {
          setGesperrtesProjekt(project?.name || listenZeile?.name || nachgeladeneZeile?.name || 'Dieses Projekt');
          return false;
      }
      // ─── Sperre nehmen, BEVOR geladen wird ────────────────────────────────
      //
      // Reihenfolge ist Absicht: loadProjectById heilt Bilder selbst und
      // schreibt dabei in das Projekt (`skipMigration`). Erst nach dem Laden zu
      // fragen hiesse, in einem fremden, gerade bearbeiteten Projekt zu
      // schreiben, nur um danach festzustellen, dass man es nur ansehen darf.
      //
      // Wer ausdruecklich schreibgeschuetzt oeffnet (AdminOpenPositionsView),
      // fordert die Sperre gar nicht erst an — sonst sperrte ein blosser Blick
      // in die offenen Positionen das Projekt fuer den, der wirklich arbeiten
      // will.
      //
      // Die eigene Sperre am bisherigen Projekt faellt hier weg: wer Projekt B
      // oeffnet, ist in A nicht mehr drin.
      // Scheitert das Laden unten, bleibt der Anwender im BISHERIGEN Projekt
      // stehen. Dessen Lesemodus-Zustand wird hier gemerkt und im Fehlerzweig
      // zurueckgesetzt — sonst stuende ein fremdes, unbegruendetes Projekt mit
      // den Flaggen des gescheiterten Ziels (z. B. „beschreibbar") da.
      const vorher = {
          readOnly: isProjectReadOnly,
          explicit: explicitReadOnly,
          lockBlocked,
          lock: projectLock,
          foreignPending: foreignPendingRef.current,
          foreignGrant,
      };
      releaseHeldLock();
      let lock: ProjectLock | null = null;
      let blocked = false;
      let foreign = false;
      let grantFound: ForeignAccessGrant | null = null;
      // Riegel ueber den GANZEN Oeffnen-Vorgang: bis das neue Projekt im State
      // steht, zeigen `currentProjectId` und heldLockRef auf verschiedene
      // Projekte. Ohne den Riegel forderte die Anwesenheits-Entscheidung in
      // diesem Fenster die Sperre des eben verlassenen Projekts neu an.
      lockOpRef.current = true;
      if (!isReadOnly) {
          // ─── Fremdes Projekt? Dann erst ansehen ──────────────────────────
          //
          // Die Zustaendigkeit steht in der Zeile der Projektliste; manche
          // Aufrufer reichen nur `{ id }` herein (Angebot → Projekt), und
          // daran liesse sich der Eigentuemer nicht erkennen.
          //
          // Liegt fuer heute schon eine Begruendung vor (frueher am Tag, in
          // einem anderen Tab, vor F5), geht es ganz normal weiter — die
          // Datenbank laesst dann Sperre und Journal zu. Sonst: KEINE Sperre
          // anfordern und nur lesen. Die Begruendung fragt erst die erste
          // Begehung oder Buchung ab (ForeignAccessContext).
          //
          // Fehlt die Zeile in der Liste (eben angelegt, `{ id }`-Aufruf aus
          // dem Angebot), wird sie einzeln nachgeladen — sonst galte ein
          // eigenes Projekt als fremd und der Eigentuemer bekaeme eine
          // Mitteilung ueber sich selbst. Laesst sie sich nicht laden, gilt
          // fuer Nicht-Admins die sichere Richtung: erst ansehen.
          let accessRow: (ProjectAccessRow & { name?: string | null }) | null = listenZeile ?? null;
          if (!accessRow) {
              accessRow = project?.user_id && project?.data?.metadata
                  ? project
                  : nachgeladeneZeile ?? await loadSingleProjectOverview(project.id).catch(() => null);
          }
          const fremd = accessRow
              ? isForeignProjectFor(accessRow, userProfile)
              : !!userProfile && !isAdminRole(userProfile.role) && userProfile.role !== 'freelancer';
          if (fremd) {
              const grantUserId = userProfile?.id ?? lockUserId;
              let grant: ForeignAccessGrant | null = null;
              // Recht „Alle Projekte sehen" (Nr. 154): keine Begruendung, die
              // Freigabe kommt still. Fehlt die RPC, geht es weiter wie bisher
              // (Nachschlagen, sonst Nur-Ansicht mit Begruendung). Lehnt der
              // Server ab, geht das Projekt NICHT auf — der Anwender bleibt,
              // wo er war, und liest warum. Die eigene Sperre am bisherigen
              // Projekt holt der naechste Herzschlag zurueck, wie nach einem
              // misslungenen Laden.
              if (ohneBegruendung) {
                  const r = await grantForeignAccessByRight(project.id, grantUserId ?? '');
                  if (r.outcome === 'error') {
                      // Recht inzwischen entzogen: frisch fragen, damit der
                      // naechste Versuch den Weg mit Begruendung nimmt.
                      if (r.kind === 'forbidden') void aktualisiereMeineRechte();
                      setZugriffHinweis({
                          titel: project?.name || listenZeile?.name || accessRow?.name || 'Projekt nicht geöffnet',
                          text: r.message,
                      });
                      lockOpRef.current = false;
                      return false;
                  }
                  if (r.outcome === 'granted') grant = r.grant;
              }
              if (!grant && grantUserId) grant = await fetchActiveForeignGrant(project.id, grantUserId);
              foreign = grant === null;
              grantFound = grant;
          }
      }
      if (!isReadOnly && !foreign) {
          const res = await acquireProjectLockWithin(project.id, lockLabel);
          lock = res.lock;
          // Nur eine LEBENDE fremde Sperre bremst. Ein `acquired: false` mit
          // verwaister Zeile — theoretisch moeglich, wenn zwischen RPC und
          // Auswertung die Uhr springt — soll niemanden aussperren.
          blocked = !res.acquired && isLockHeldByOther(res.lock, lockUserId, Date.now());
          if (!blocked) heldLockRef.current = project.id;
      }
      const effectiveReadOnly = isReadOnly || blocked || foreign;
      setProjectLock(lock);
      setLockBlocked(blocked);
      setProjectReadOnly(effectiveReadOnly);
      setExplicitReadOnly(isReadOnly);
      // Ein offener Begruendungsdialog gehoert zum bisherigen Projekt.
      settleForeignRequest(false);
      setForeignPending(foreign);
      setForeignGrant(grantFound);
      // Gemerkt wird nur eine Ansicht AUSSERHALB eines Projekts. Wer aus einem
      // Modul heraus ein anderes Projekt oeffnet („+" im Archiv), landete beim
      // Verlassen sonst in einem Modul ohne Bericht (leer) — und Zurueck
      // pendelte zwischen Hub und Modul. So geht es dorthin, wo das erste
      // Projekt geoeffnet wurde.
      if (!isProjectView(view)) {
          setPreviousView(view);
      }

      let reportData = project.data as Report;

      const needsFullLoad = true; // Always load fresh data when opening a project

      if (needsFullLoad) { 
          setLoading(true);
          try {
              // Schreibgeschuetzt geoeffnet heisst: nur lesen. Die Bild-
              // Selbstheilung wuerde in den Speicherordner eines fremden
              // Nutzers hochladen und dessen Zeilen aktualisieren. Der Wert
              // steht hier schon fest — er kommt als Parameter herein bzw. aus
              // der eben abgefragten Sperre oder Fremdfreigabe, nicht erst aus
              // dem State.
              const fullProject = await loadProjectById(project.id, (cur, total) =>
                  setLoadingMessage(`Bilder werden optimiert … (${cur}/${total})`),
                  { skipMigration: effectiveReadOnly });
              if (fullProject && fullProject.data) {
                  reportData = fullProject.data;
                  // Der Ausgangsstand ist IMMER der Serverstand, auch wenn
                  // gleich ein Queue-Eintrag den angezeigten Bericht ersetzt:
                  // er sagt aus, wovon die Änderungen ausgehen, nicht was
                  // angezeigt wird. (Ein bestehender Eintrag bringt seinen
                  // eigenen, älteren Ausgangsstand ohnehin mit.)
                  projectBaseRef.current = {
                      projectId: project.id,
                      report: fullProject.data as Report,
                      updatedAt: fullProject.updated_at ?? null,
                  };
                  // Offline-Queue: ein lokal gesicherter, noch nicht synchronisierter
                  // Entwurf darf nicht verloren gehen — aber er ersetzt den
                  // Serverstand nicht mehr, er wird OBENDRAUF gelegt. Bis zum
                  // 08.09.2026 zeigte das Oeffnen die alte Kopie, und alles,
                  // was seit dem Einreihen auf dem Server passiert war, blieb
                  // unsichtbar: mobil vorbereitete Berichte, Projektwechsel,
                  // abends am PC weiter (Meyer, 26051016) — der naechste
                  // Autosave schrieb dann den alten Stand und der DB-Riegel
                  // holte Zeile fuer Zeile zurueck. Deshalb: Server zuerst,
                  // Entwurf obendrauf. Bei Widerstreit im selben Feld gewinnt
                  // der eigene Wert; Listen mit `id` behalten die fremden
                  // Elemente (das ist genau mergeReports). Ein Alteintrag ohne
                  // Ausgangsstand wird geborgen — Protokoll, Positionen und
                  // Notizen des Servers kommen in den Entwurf
                  // (entwurfAufServerstand).
                  // Ausnahme: ein zu alter Eintrag, angehalten oder nicht (geht
                  // nie mehr raus). Fluesse der in den State, ginge er mit dem
                  // naechsten Tastendruck durch die Hintertuer doch hoch —
                  // genau die alten Zaehler vom 07.09.2026 und die geloeschten
                  // Positionen von 6185 (useOfflineSync.lokalenEntwurfHolen).
                  const queued = await lokalenEntwurfHolenRef.current(project.id);
                  if (queued) {
                      const entwurf = entwurfAufServerstand(queued, fullProject.data as Report);
                      reportData = entwurf.merged;
                      if (entwurf.conflicts.length > 0 || entwurf.keptForeignLogEntries > 0 || entwurf.uebernommeneServerZeilen > 0) {
                          console.info('[offlineSync] Entwurf auf den Serverstand gelegt', project.id, {
                              mode: entwurf.mode,
                              conflicts: entwurf.conflicts.length,
                              kept: entwurf.keptForeignLogEntries,
                              removed: entwurf.removedLogEntries,
                              serverZeilen: entwurf.uebernommeneServerZeilen,
                          });
                      }
                  }
              } else {
                  throw new Error("Projektdaten unvollständig oder nicht ladbar. Bitte erneut versuchen.");
              }
          } catch(e: any) {
              console.error("Project load error", e);
              alert("Fehler beim Laden: " + e.message);
              setLoadingMessage(null);
              setLoading(false);
              // Das Projekt geht nicht auf — die eben genommene Sperre wieder
              // her. Ohne das blockierte ein misslungener Ladeversuch das
              // Projekt fuer alle anderen, bis sie nach 180 s verfaellt.
              releaseHeldLock();
              setProjectLock(vorher.lockBlocked ? vorher.lock : null);
              setLockBlocked(vorher.lockBlocked);
              setProjectReadOnly(vorher.readOnly);
              setExplicitReadOnly(vorher.explicit);
              setForeignPending(vorher.foreignPending);
              setForeignGrant(vorher.foreignGrant);
              lockOpRef.current = false;
              return false;
          }
          setLoadingMessage(null);
          setLoading(false);
      }

      reportData = withProfileSettings(reportData);

      setReport(reportData);
      setCurrentProjectId(project.id);
      // Erst HIER, nicht oben beim Sperrenholen: der Ladefehler-Zweig kehrt
      // mit `return false` zurueck und laesst den Anwender im BISHERIGEN
      // Projekt stehen — ein dort gemeldetes Oeffnen waere schlicht falsch.
      // Das `switch`-Schliessen des vorigen Projekts loest die Erfassung
      // selbst aus (sie merkt sich das offene Projekt), und zwar genau in
      // diesem Augenblick statt schon beim misslungenen Versuch.
      // Schreibgeschuetzt geoeffnete Projekte zaehlen mit: Ansehen ist Nutzung.
      recordProjectOpen({
          projectId: project.id,
          projectNumber: reportData?.metadata?.projectNumber ?? null,
          projectName: reportData?.metadata?.projectName ?? null,
          companyId: userProfile?.company_id ?? null,
      }, openReason);
      setActiveSection('items');
      setView('project-hub');
      setShowMobileProjectSelector(false);
      // Wir sind wieder drin: der Verlasszeitpunkt des vorigen Projekts darf
      // die Karenzzeit hier nicht anrechnen.
      leftProjectViewAtRef.current = null;
      lockOpRef.current = false;
      return true;
  };
  
  const handleModuleNavigation = (module: any) => {
      // Schreibgeschuetzt geoeffnet: gar nicht erst abfangen. Der Dialog
      // "Projektstatus ändern?" schreibt status, startDate und
      // nextScheduledDate dauerhaft in ein fremdes Projekt — und sein
      // "Abbrechen" wuerde auch das reine Ansehen abbrechen (Sackgasse).
      // Im Lesemodus geht die Navigation deshalb direkt durch, der
      // Statuswechsel entfaellt ersatzlos: Ansehen ohne jeden Schreibzugriff.
      if (!isProjectReadOnly && ['module-sigeko-inspection', 'editor', 'project-map'].includes(module)) {
          const currentStatus = report?.metadata?.globalData?.status;
          if (currentStatus === 'planning' || currentStatus === 'on_hold') {
              setPendingStatusChange({ targetModule: module, currentStatus });
              return;
          }
      }
      executeModuleNavigation(module);
  };

  const executeModuleNavigation = (module: any) => {
      if (module === 'editor' || module === 'module-sigeko-inspection' || module === 'project-map') {
          setActiveSection(module === 'project-map' ? 'project-map' : 'items');
          setView('module-sigeko-inspection');
      } else if (module === 'vorank') {
          setActiveSection('editor');
          setView('module-vorank');
      } else if (module === 'unterlage') {
          setActiveSection('project');
          setView('module-unterlage');
      } else if (module === 'sigeplan') {
          setActiveSection('project');
          setView('module-sige-plan');
      } else if (module === 'einweisung') {
          setActiveSection('project');
          setView('module-sige-einweisung');
      // SiFa-Raster (SifaModuleGrid): Unterweisungen per Kundenlink und
      // Mitarbeiterstamm. Eigene Module, keine Reiter — sie haengen an eigenen
      // Tabellen (sifa_*), nicht am Projekt-JSON.
      } else if (module === 'sifa-unterweisung') {
          setActiveSection('project');
          setView(VIEW_SIFA_UNTERWEISUNG);
      } else if (module === 'sifa-mitarbeiter') {
          setActiveSection('project');
          setView(VIEW_SIFA_MITARBEITER);
      // Nr. 92: Faelligkeiten & Vorsorge und Taetigkeitsnachweis — ebenfalls
      // eigene Tabellen (sifa_fristen, sifa_taetigkeiten), kein Projekt-JSON.
      } else if (module === 'sifa-fristen') {
          setActiveSection('project');
          setView(VIEW_SIFA_FRISTEN);
      } else if (module === 'sifa-taetigkeiten') {
          setActiveSection('project');
          setView(VIEW_SIFA_TAETIGKEITEN);
      // Gefaehrdungsbeurteilungen (docs/sifa-gbu-plan.md §11): eigene Tabellen
      // sifa_gbu* und Kataster, kein Projekt-JSON ausser der Zeitbuchung.
      } else if (module === 'sifa-gbu') {
          setActiveSection('project');
          setView(VIEW_SIFA_GBU);
      } else if (module === 'dokumente') {
          setActiveSection('project');
          setView('module-dokumente');
      } else if (module === 'planung') {
          setActiveSection('project');
          setView('module-planung');
      // 'handbuch' und 'koordinierung' oeffnen keine eigenen Ansichten mehr,
      // sondern die SiGe-Plan-Huelle am jeweiligen Reiter. Die Modulschluessel
      // bleiben trotzdem: das Handbuch springt darueber auf die
      // Gewerke-Ueberschneidung, und aeltere gemerkte Ansichten tragen sie noch.
      } else if (module === 'handbuch') {
          setActiveSection('project');
          setView('module-handbuch');
      } else if (module === 'koordinierung') {
          setActiveSection('project');
          setView('module-koordinierung');
      } else if (module === 'asplan') {
          setActiveSection('project');
          setView('module-asplan');
      // Die Baustellenordnung steht bewusst NICHT in der Liste von
      // handleModuleNavigation, die den Dialog „Projektstatus ändern?"
      // ausloest: sie ist Schreibtischarbeit und kein Anlass, ein Projekt
      // aktiv zu setzen.
      } else if (module === 'bauordnung') {
          setActiveSection('project');
          setView('module-bauordnung');
      } else if (module === 'module-archive') {
          setView('module-archive');
      } else if (module === 'module-sent-reports') {
          setView('project-hub');
          setTimeout(() => window.postMessage({ type: 'NAVIGATE', module: 'module-sent-reports' }, '*'), 100);
      }
  };

  const handleConfirmStatusChange = async () => {
      if (!pendingStatusChange || !report || !currentProjectId) return;

      // Zweite Reihe: der Dialog erscheint im Lesemodus nicht mehr
      // (handleModuleNavigation faengt dort gar nicht erst ab). Sollte er doch
      // je auftauchen, wird hier nichts geschrieben — aber trotzdem navigiert,
      // damit das Ansehen nicht in einer Sackgasse endet.
      if (isProjectReadOnly) {
          alert(readOnlyMessage('Der Projektstatus wurde nicht geändert; die Ansicht wird nur geöffnet.', readOnlyHolder()));
          executeModuleNavigation(pendingStatusChange.targetModule);
          setPendingStatusChange(null);
          return;
      }

      const todayStr = new Date().toISOString().split('T')[0];
      const newGlobalData = { ...report.metadata.globalData, status: 'active' as const };
      
      const newIntervalConfig = { 
          ...(report.metadata.intervalConfig || { type: 'weekly' }) 
      };

      if (pendingStatusChange.currentStatus === 'planning') {
          newGlobalData.startDate = todayStr;
          newIntervalConfig.nextScheduledDate = todayStr;
      }

      if (pendingStatusChange.currentStatus === 'on_hold') newGlobalData.onHoldUntil = undefined;
      
      const updatedReport = { 
          ...report, 
          metadata: { 
              ...report.metadata, 
              globalData: newGlobalData,
              intervalConfig: newIntervalConfig 
          } 
      };
      
      setReport(updatedReport);

      setProjects(prev => prev.map(p => 
          p.id === currentProjectId 
          ? { ...p, status: 'active', data: updatedReport, next_scheduled_date: newIntervalConfig.nextScheduledDate } 
          : p
      ));
      
      handleManualSaveStatusUpdate('saving');
      
      try {
          const updatedRow = await saveProjectToCloud(updatedReport, currentProjectId);
          
          if (updatedRow?.data) {
              setReport(prev => ({ ...prev!, metadata: { ...prev!.metadata, ...updatedRow.data.metadata } }));
          }
          
          handleManualSaveStatusUpdate('saved');
          executeModuleNavigation(pendingStatusChange.targetModule);
          setPendingStatusChange(null);
      } catch(e) { 
          console.error(e);
          handleManualSaveStatusUpdate('error'); 
          // Rechtefehler (Typwechsel ohne Recht, Nr. 154) im Klartext statt
          // des Rats, die Verbindung zu prüfen.
          alert(speicherRechtHinweis(e) ?? "Status-Update fehlgeschlagen. Bitte Verbindung prüfen.");
      }
  };

  /**
   * Verschieben und Ueberspringen aus Dashboard, Projektliste und Kanban —
   * EIN Speichern, hier und nur hier (ProjectOverview speicherte bis zum
   * 21.10.2026 zusaetzlich selbst).
   *
   * `project` ist ein Eintrag der Projektliste, also ein AUSZUG: sein `data`
   * traegt nur `metadata`. Ihn als Projekt zu speichern hat am 21.10.2026
   * Unterlage, Vorankuendigung, Einstellungen und alles andere ausserhalb
   * der Metadaten geloescht (listenAuszug.ts; saveProjectToCloud weist ihn
   * inzwischen ab). Gespeichert wird deshalb der VOLL geladene Stand mit
   * genau einer Aenderung, dem Folgetermin (folgeterminService.ts).
   *
   * Die Auftraege eines Projekts laufen nacheinander, und jeder rechnet sein
   * Ziel aus dem frisch geladenen Stand — zwei schnelle „+7" ergeben +14.
   * Der Listeneintrag zeigt die Vorschau sofort (aus dem Listeneintrag
   * gerechnet; er bleibt dabei ein Auszug). Am Ende steht dort der
   * gespeicherte Wert, nach einem Fehler der letzte bestaetigte — beides nur,
   * wenn seitdem keine neuere Aktion an diesem Projekt kam
   * (`erstelleFolgeterminAnzeige`). Liefert `true`, wenn gespeichert wurde —
   * ProjectOverview meldet sonst den Fehler.
   */
  const [folgeterminAnzeige] = useState(erstelleFolgeterminAnzeige);
  const setzeListenTermin = (projectId: string, termin: ListenTermin) =>
      setProjects(prev => prev.map(p => p.id === projectId ? mitListenTermin(p, termin) : p));

  const speichereFolgeterminAusListe = async (
      project: { id: string; next_scheduled_date?: string | null; data?: { metadata?: Partial<ReportMetadata> } },
      vorschau: string,
      auftrag: () => Promise<FolgeterminErgebnis>,
  ): Promise<boolean> => {
      const projectId = project.id;
      const vorher: ListenTermin = { datum: project.next_scheduled_date ?? null, intervall: project.data?.metadata?.intervalConfig };
      const anzeige = folgeterminAnzeige;
      const nr = anzeige.beginne(projectId, vorher);
      if (vorschau) setzeListenTermin(projectId, { datum: vorschau, intervall: { ...vorher.intervall, nextScheduledDate: vorschau } as IntervalConfig });
      handleManualSaveStatusUpdate('saving');
      try {
          const ergebnis = await auftrag();
          // Auch ohne Aenderung: die Liste auf den eben geladenen Stand — sie
          // kann einen Turnus zeigen, den jemand inzwischen entfernt hat.
          const zeigen = anzeige.erfolg(projectId, nr, { datum: ergebnis.nextScheduledDate, intervall: ergebnis.intervalConfig });
          if (zeigen) setzeListenTermin(projectId, zeigen);
          if (ergebnis.geaendert) {
              handleManualSaveStatusUpdate('saved');
          } else {
              // Nichts gespeichert — kein „Gespeichert" vortaeuschen, sondern sagen, warum.
              handleManualSaveStatusUpdate('idle');
              setZugriffHinweis({
                  titel: 'Termin nicht verschoben',
                  text: 'Für dieses Projekt ist kein automatischer Begehungsturnus (mehr) hinterlegt — bei „manuell" oder ohne Turnus gibt es keinen nächsten Turnustermin. Der Termin bleibt unverändert.',
              });
          }
          return true;
      } catch (e) {
          console.error('[Folgetermin] Speichern fehlgeschlagen', projectId, e);
          // Zurueck auf den letzten bestaetigten Stand — ohne Netz kommt das
          // Neuladen darunter nicht durch, und die nie gespeicherte Vorschau
          // waere die Basis des naechsten Klicks.
          const zeigen = anzeige.fehler(projectId, nr);
          if (zeigen) setzeListenTermin(projectId, zeigen);
          handleManualSaveStatusUpdate('error');
          fetchProjects(true);
          return false;
      }
  };

  const handlePostpone = async (project: Pick<ListProject, 'id' | 'next_scheduled_date' | 'data'>, days = 7): Promise<boolean> => {
      const meta = project.data.metadata;
      const vorschau = zielNachTagen(folgeterminBasis(project.next_scheduled_date, meta), days);
      return speichereFolgeterminAusListe(project, vorschau, () => verschiebeUmTage(project.id, days));
  };

  const handleSkip = async (project: Pick<ListProject, 'id' | 'next_scheduled_date' | 'data'>): Promise<boolean> => {
      const meta = project.data.metadata;
      // Ohne Turnus gibt es keinen naechsten Termin — nichts zu tun, kein Fehler.
      if (!meta.intervalConfig) return true;
      const vorschau = zielUebersprungen(folgeterminBasis(project.next_scheduled_date, meta, true), meta.intervalConfig);
      return speichereFolgeterminAusListe(project, vorschau, () => ueberspringeTurnus(project.id));
  };

  const handleManualSaveStatusUpdate = (status: SaveStatus) => {
      setSaveStatus(status);
      if (clearSaveStatusTimeoutRef.current) clearTimeout(clearSaveStatusTimeoutRef.current);
      if (status === 'saved') {
          clearSaveStatusTimeoutRef.current = setTimeout(() => { setSaveStatus('idle'); }, 3000);
      }
  };


  /**
   * Der Serverstand, auf dem die laufende Bearbeitung aufsetzt — Ausgangspunkt
   * für jeden Warteschlangeneintrag dieses Projekts.
   *
   * Ein Ref, kein State: kein Render hängt daran, und der Wert muss beim
   * nächsten `enqueue` ohne Umweg zur Verfügung stehen. Gesetzt wird er beim
   * Öffnen eines Projekts und nach jedem bestätigten Flush — danach ist der
   * eben geschriebene Stand der neue Ausgangspunkt, denn der Eintrag, der von
   * ihm ausging, ist aus der Warteschlange verschwunden.
   */
  const projectBaseRef = useRef<{ projectId: string; report: Report; updatedAt: string | null } | null>(null);

  const handleQueueFlushed = (projectId: string, updatedRow: any, savedReport: Report) => {
      // Die Zweitschrift ins Buchungsjournal (S4b). Sie haengt bewusst HIER
      // und nicht an der Buchungsstelle: die Module rufen nur `onUpdate`, und
      // ob das Speichern gelingt, entscheidet sich erst in der Queue. Erst
      // nach dem bestaetigten Flush darf das Journal die Zeilen sehen — eine
      // Journalzeile ohne JSON-Zeile waere in der Uebergangsphase nicht mehr
      // zurueckzunehmen. Vor dem fruehen Ausstieg unten, denn eine fehlende
      // Server-Zeile betrifft nur den State-Merge, nicht das Journal.
      // Mit dem GESCHRIEBENEN Protokoll: nach einer Zusammenfuehrung ist es
      // nicht der eingereihte Stand, und ins Journal geht nur, was darin steht.
      void flushJournalMirror(projectId, getServiceLog(savedReport));
      // Der Ausgangsstand des eben geflushten Eintrags — festhalten, BEVOR er
      // unten durch den geschriebenen Stand ersetzt wird. Er ist der `base`,
      // gegen den unten entschieden wird, was der Server geaendert hat und
      // was ich. Bei einem Projektwechsel gehoert er zu einem anderen
      // Projekt und geht nicht mit.
      const alteBasis = projectBaseRef.current?.projectId === projectId
          ? projectBaseRef.current.report
          : undefined;
      // Neuer Ausgangsstand — mit dem `updated_at`, das der Server bestätigt
      // hat, nie mit einem selbst gebildeten Zeitstempel.
      if (updatedRow?.updated_at) {
          projectBaseRef.current = {
              projectId,
              report: savedReport,
              updatedAt: updatedRow.updated_at,
          };
      }
      if (!updatedRow?.data) return;
      // Was der Server nach dem Flush tatsaechlich haelt: das Protokoll NACH
      // den Triggern (inkl. geretteter Zeilen), die Zaehler des geschriebenen
      // Reports — nach einer Zusammenfuehrung also die des Servers.
      const theirs = updatedRow.data as Report;
      const serverNextDate = theirs.metadata?.intervalConfig?.nextScheduledDate;
      // Nur bei ECHTER Abweichung ein neues Objekt. Bis zum 30.09.2026 baute
      // jeder Flush hier einen frischen Bericht, auch wenn der Termin schon
      // stimmte — `setReport` bekam damit nie `prev` zurueck, und jedes
      // Speichern kostete einen vollen Render des geoeffneten Moduls (bei
      // 6051 mit 639 Positionen spuerbar). Stimmt der Termin, bleibt der
      // Bericht derselbe; `uebernimmGeschriebenenStand` gibt ohne Aenderung
      // ebenfalls `prev` zurueck, und React ueberspringt den Render.
      const mitServerTermin = (r: Report): Report => (
          serverNextDate && r.metadata?.intervalConfig?.nextScheduledDate !== serverNextDate
      ) ? {
          ...r,
          metadata: {
              ...r.metadata,
              intervalConfig: { ...r.metadata.intervalConfig, nextScheduledDate: serverNextDate },
          },
      } : r;
      // Report-State ERGAENZEN, nie mit dem gespeicherten Stand ueberschreiben
      // — neuere Edits liegen bereits koalesziert in der Queue. Ergaenzt
      // werden fremde Protokollzeilen und die Zaehler, die der Server haelt;
      // neuere lokale Arbeit gewinnt (uebernimmGeschriebenenStand). Bis zum
      // 08.09.2026 kam hier nur der naechste Termin an: nach einer
      // Zusammenfuehrung trug der geschriebene Stand die fremde Zeile und den
      // Serverzaehler, der State nicht — der naechste Autosave reihte den
      // State mit treffendem Anker ein, der Normalpfad schrieb ihn als
      // Vollblob, und der DB-Riegel holte Zeile und Zaehler zurueck. Projekt
      // 26051016: 43 Rettungen derselben Zeile 6bb1abd5 in 23 Minuten.
      // Kein Hinweis auf dem Bildschirm — das ist Normalbetrieb.
      if (projectId === currentProjectId) {
          setReport(prev => {
              if (!prev) return prev;
              const u = uebernimmGeschriebenenStand(alteBasis, prev, theirs);
              if (u.merged !== prev) {
                  console.info('[offlineSync] Geschriebener Stand übernommen', projectId, {
                      kept: u.keptForeignLogEntries,
                      zaehler: u.zaehlerUebernommen,
                  });
              }
              return mitServerTermin(u.merged);
          });
      }
      // Die Projektliste zeigt dieselben Zaehler wie der State.
      const mergedReport = mitServerTermin(uebernimmGeschriebenenStand(alteBasis, savedReport, theirs).merged);
      setProjects(prev => prev.map(p =>
          p.id === projectId
          ? { ...p, ...updatedRow, data: mergedReport, project_services: mapServicesToDb(mergedReport.metadata.globalData?.services || []) }
          : p
      ));
  };

  /**
   * Meldung, dass ein lokal gesicherter Stand noch nicht hochgeladen werden
   * konnte. Ein Hinweis, kein Dialog: der Benutzer entscheidet nichts, die
   * Warteschlange versucht es von selbst weiter.
   *
   * Eine WARTESCHLANGE und keine einzelne Variable: `flushAll` geht die ganze
   * Offline-Queue durch und kann mehrere angehaltene Einträge nacheinander
   * melden. Lägen sie alle in derselben Variable, überschriebe jeder den
   * vorigen — sichtbar wäre nur der letzte, und die übrigen hätte nie jemand
   * gelesen. Angezeigt wird immer das erste Element, das nächste rückt beim
   * Schließen nach.
   *
   * `kennung` steht nur am Altersriegel: seine Absage geht nie mehr von
   * selbst raus. Nur dort gibt es etwas zu quittieren (`acknowledgeTooOld`
   * beim Schließen — erst das schreibt den dauerhaften Merker, damit ein
   * überschriebener oder nie gesehener Hinweis nach dem nächsten Start
   * wiederkommt) und etwas zu verwerfen. Sie benennt den STAND, über den
   * dieser Balken spricht, nicht das Projekt: zwischen Meldung und Klick kann
   * unter demselben Schlüssel längst ein anderer Eintrag liegen, und ein
   * Klick darf nie etwas treffen, das niemand gesehen hat.
   *
   * Jedes Element trägt eine eigene `id`. Nach einem `await` ist die Position
   * in der Liste nichts mehr wert — inzwischen kann ein Hinweis
   * dazugekommen oder weggeklickt worden sein; adressiert wird deshalb nur
   * über die `id`.
   */
  interface QueueHinweis {
      id: number;
      message: string;
      kennung?: ZuAltKennung;
      /**
       * Nur am Rechte-Hinweis (Speichern von der Datenbank abgewiesen, etwa
       * Typwechsel ohne Recht, Nr. 154): das Projekt, dessen nächstes
       * gelungenes Speichern den Hinweis wieder wegnimmt.
       */
      rechtProjektId?: string;
  }
  const [queueBlockedNotices, setQueueBlockedNotices] = useState<QueueHinweis[]>([]);
  const queueHinweisZaehlerRef = useRef(0);

  /**
   * Welcher Verwerfen-Knopf schon scharf ist (Hinweis-`id`) — die zweite
   * Stufe statt eines `window.confirm`. Verworfen wird erst auf den zweiten
   * Klick.
   */
  const [verwerfenArmiertFuer, setVerwerfenArmiertFuer] = useState<number | null>(null);
  /** Welcher Hinweis gerade verworfen wird — solange sind seine Knöpfe still. */
  const [verwerfenLaeuftFuer, setVerwerfenLaeuftFuer] = useState<number | null>(null);

  /**
   * Benutzerwechsel: die Hinweise gehören der alten Sitzung.
   *
   * Ein Hook, also VOR den frühen Returns — die Regel aus dem Bootcrash vom
   * 13.09.2026. Stehen bleiben dürfen sie nicht: ihre Kennungen zeigen auf
   * Einträge eines anderen Benutzers, und der neue Anwender könnte an einem
   * Balken hantieren, dessen Stand ihn nichts angeht.
   */
  const angemeldeterNutzerId = session?.user?.id ?? null;
  useEffect(() => {
      setQueueBlockedNotices([]);
      setVerwerfenArmiertFuer(null);
      setVerwerfenLaeuftFuer(null);
  }, [angemeldeterNutzerId]);

  /**
   * Hinweis anhängen — denselben nicht zweimal: `flushAll` läuft alle 45 s,
   * und ein angehaltener Eintrag meldet sich nach einem Neustart erneut,
   * solange er nicht quittiert ist. „Derselbe" heißt gleicher Text UND
   * derselbe Stand (Kennung); ein Hinweis ohne Kennung zählt allein über den
   * Text.
   *
   * `vorne` stellt einen Hinweis an den Anfang statt ans Ende. Für den
   * Regelfall falsch — die Warteschlange soll der Reihe nach abgearbeitet
   * werden —, für etwas, das den Anwender JETZT angeht, richtig.
   *
   * `einmalJeProjekt`: steht für `rechtProjektId` schon ein Hinweis, kommt
   * keiner dazu — gleich mit welchem Wortlaut.
   */
  const ergaenzeQueueHinweis = (
      hinweis: { message: string; kennung?: ZuAltKennung; rechtProjektId?: string },
      optionen?: { vorne?: boolean; einmalJeProjekt?: boolean },
  ) => {
      setQueueBlockedNotices(prev => {
          const schonDa = (optionen?.einmalJeProjekt && hinweis.rechtProjektId
              && prev.some(h => h.rechtProjektId === hinweis.rechtProjektId))
              || prev.some(h =>
              h.message === hinweis.message
              && h.kennung?.projectId === hinweis.kennung?.projectId
              && h.kennung?.userId === hinweis.kennung?.userId
              && h.kennung?.rev === hinweis.kennung?.rev
              && h.kennung?.updatedAt === hinweis.kennung?.updatedAt);
          if (schonDa) return prev;
          const neu = { ...hinweis, id: ++queueHinweisZaehlerRef.current };
          return optionen?.vorne ? [neu, ...prev] : [...prev, neu];
      });
  };

  /** Genau diesen Hinweis wegnehmen — der nächste rückt nach. */
  const entferneQueueHinweis = (id: number) => {
      setVerwerfenArmiertFuer(a => a === id ? null : a);
      setQueueBlockedNotices(prev => prev.filter(h => h.id !== id));
  };

  /**
   * Torwaechter der Offline-Queue.
   *
   * Zwei Gruende, einen wartenden Eintrag NICHT hochzuladen:
   *
   *  1. Die aktuelle Sitzung ist fuer dieses Projekt im Lesemodus. Dann darf
   *     auch ein Alteintrag aus der Zeit davor nicht raus — ein geblockter
   *     Zuschauer schriebe sonst genau das, was der Balken ihm gerade abspricht.
   *  2. Ein anderer haelt eine LEBENDE Sperre. Der klassische Fall ist der
   *     laengere Netzausfall: nach drei Minuten verfaellt die eigene Sperre,
   *     ein Kollege uebernimmt — und die Queue schoebe beim Wiederanmelden
   *     ungefragt den alten Stand darueber.
   *
   * Alles andere laedt hoch. `fetchProjectLock` wirft nicht und liefert bei
   * jedem Fehler null (offline, Migration Nr. 33 nicht gefahren ⇒ PGRST202) —
   * daraus wird in decideQueueUpload ein „hochladen". Der Torwaechter darf
   * Arbeit bremsen, aber niemals dauerhaft festhalten.
   */
  // Bauphase zu (useBauphaseGate.uploadsBlocked) — als Ref, damit der
  // Torwaechter stabil bleibt und useOfflineSync nicht neu verdrahtet wird.
  const bauphaseLockedRef = useRef(false);
  const canUploadQueueEntry = useCallback(async (projectId: string): Promise<QueueUploadDecision> => {
      // ERST der Versionscheck, DANN das Hochladen. Beide haengen am selben
      // visibilitychange; vor dem 07.09.2026 war der Upload laengst unterwegs,
      // wenn der Check das neue Deployment sah und den Reload ausloeste. Der
      // Check teilt seinen laufenden Fetch (versionGate.checkNow), das Warten
      // kostet also hoechstens einen Roundtrip; innerhalb von 5 Minuten gar
      // keinen. Ein veraltetes Bundle laedt nicht hoch — der Eintrag bleibt
      // pending, der neue Build nimmt ihn nach dem Reload mit.
      await versionGateRef.current?.checkNow();
      if (istBuildVeraltet()) return { upload: false, reason: 'build-veraltet' };
      // Bauphase zu: liegen lassen, keine Meldung — der Server wiese den Save
      // ohnehin mit 'BAUPHASE' ab; nach dem Neuladen geht der Eintrag raus.
      if (bauphaseLockedRef.current) return { upload: false, reason: 'bauphase' };
      const readOnly = projectId === currentProjectIdRef.current && readOnlyRef.current;
      const lock = readOnly ? null : await fetchProjectLock(projectId);
      return decideQueueUpload({ readOnly, lock, currentUserId: lockUserId, nowMs: Date.now() });
  }, [lockUserId]);

  const offlineSync = useOfflineSync({
      userId: session?.user?.id ?? null,
      // Der Anker kommt aus dem Eintrag selbst und wird durchgereicht: die
      // Warteschlange schreibt gegen den Stand, auf dem sie aufsetzt, nicht
      // gegen „irgendwas, was gerade dasteht".
      //
      // Weist die Datenbank das Speichern mit einem Rechtefehler ab (Typwechsel
      // ohne Anlegen-Recht, Nr. 154), hält die Warteschlange das für
      // wiederholbar und zeigt nur „Wartet auf Sync" — jeder weitere Versuch
      // scheitert genauso, solange der lokale Stand den Typ trägt. Deshalb hier
      // der Balken im Klartext; der Fehler geht unverändert weiter. Das nächste
      // gelungene Speichern desselben Projekts nimmt den Balken wieder weg.
      saveReport: async (entry, options) => {
          try {
              const row = await saveProjectToCloud(entry.report, entry.projectId, undefined, undefined, undefined, options);
              setQueueBlockedNotices(prev => ohneRechtHinweisFuer(prev, entry.projectId));
              return row;
          } catch (e) {
              const hinweis = speicherRechtHinweis(e, queueEntryProjectLabel(entry));
              if (hinweis) ergaenzeQueueHinweis({ message: hinweis, rechtProjektId: entry.projectId }, { vorne: true });
              throw e;
          }
      },
      loadServerReport: async (projectId) => {
          const fresh = await loadProjectById(projectId);
          if (!fresh?.data) return null;
          return { report: fresh.data as Report, updatedAt: fresh.updated_at ?? null };
      },
      onMerged: (projectId, info) => {
          // Nur ins Protokoll — die Zusammenführung ist der Vorgang, den man
          // hinterher nachvollziehen können muss. Auf den Bildschirm kommt
          // sie nicht mehr: mobil vorbereiten, am PC weiterarbeiten ist
          // Normalbetrieb, und ein Balken dafür wäre nach der dritten
          // folgenlosen Meldung ohnehin unsichtbar (Entscheidung des
          // Betreibers, 08.09.2026). Die Leiste bleibt für die Sperre durch
          // andere und den Altersriegel (onUploadBlocked).
          console.warn('[offlineSync] Überholter Stand zusammengeführt', projectId, info);
      },
      onStatus: handleManualSaveStatusUpdate,
      onFlushed: handleQueueFlushed,
      canUpload: canUploadQueueEntry,
      // Nur der Altersriegel ('zu-alt') bringt eine Kennung mit: seine Absage
      // geht nie mehr von selbst raus, nur sie will quittiert werden und nur
      // sie darf verworfen werden. Eine Sperrabsage ('sperre') loest sich von
      // selbst — ein Verwerfen-Knopf loeschte dort Arbeit, die gleich noch
      // hochgeht.
      //
      // 'abgewiesen' (Datenbank lehnt den Inhalt ab) haengt am Projekt wie
      // der Rechte-Hinweis: das naechste gelungene Speichern nimmt ihn weg
      // (ohneRechtHinweisFuer oben). Ein Balken je Projekt — eine Rechte-
      // Absage (42501) hat ihren eigenen, genaueren Satz schon von saveReport
      // bekommen, und jeder weitere Versuch wuerde sonst einen neuen
      // Wortlaut anhaengen.
      onUploadBlocked: (projectId, message, grund, kennung) => {
          if (grund === 'abgewiesen') {
              ergaenzeQueueHinweis({ message, rechtProjektId: projectId }, { einmalJeProjekt: true });
              return;
          }
          ergaenzeQueueHinweis({
              message,
              kennung: grund === 'zu-alt' ? kennung : undefined,
          });
      },
      // Der Server hat den Upload mit dem Build-Riegel abgewiesen — derselbe
      // Pfad wie ein erkanntes neues Deployment.
      onBuildOutdated: () => behandleVeraltetenBuild('riegel'),
  });
  // Auskunft nach oben durchreichen: der Auth-Effekt steht ueber dieser Zeile
  // und darf `offlineSync` nicht schliessen (TDZ). Zuweisung im Render wie bei
  // `bauphaseLockedRef` weiter unten — die Funktion ist stabil (useCallback).
  queueBeschaeftigtRef.current = offlineSync.istBeschaeftigt;
  lokalenEntwurfHolenRef.current = offlineSync.lokalenEntwurfHolen;

  // ─── Bauphase (Admin-Wartung) ─────────────────────────────────────────────
  // Ein Hook, also VOR den fruehen returns (loading/session) — die Weiche zur
  // Wartungsseite steht weiter unten vor dem Freelancer-Zweig. Die Callbacks
  // sind schlichte Closures; useBauphaseGate haelt sie im Ref.
  const bauphase = useBauphaseGate({
      enabled: !!session && !!userProfile,
      role: userProfile?.role,
      currentProjectId,
      flushAllNow: async () => {
          cancelPendingAutosave();
          const id = currentProjectIdRef.current;
          if (id) await offlineSync.flushProject(id);
          await offlineSync.flushAll();
          await retryJournalMirrors(session?.user?.id ?? null).catch(() => undefined);
      },
      isSaved: () => saveStatusRef.current === 'saved' || saveStatusRef.current === 'idle',
      closeProject: closeProjectForMaintenance,
      checkVersionNow: async () => { await versionGateRef.current?.checkNow(); },
      buildTime: aktuellerBuildStempel,
      // `maintenance_status()` meldet einen Mindest-Build ueber dem eigenen —
      // derselbe Pfad wie ein erkanntes neues Deployment.
      onBuildTooOld: () => behandleVeraltetenBuild('riegel'),
  });
  bauphaseLockedRef.current = bauphase.uploadsBlocked;

  // ─── Lernzentrum: geführte Tour (Paket B) ─────────────────────────────────
  // Ein Hook, also VOR den frühen returns (Bauphase, Freelancer-Portal). Das
  // Overlay selbst steht ganz unten im Baum und wird in diesen beiden Zweigen
  // gar nicht erst gerendert.
  const istMobil = useIsMobile();
  // Ohne Profil steht die Rolle noch nicht fest. `['K']` ist der engste
  // sinnvolle Satz — die Koordinatoren-Kapitel sieht jede Rolle außer dem
  // Freelancer, also kann daraus kein Kapitel aufspringen, das dem Nutzer
  // nicht zusteht. Sobald das Profil da ist, gilt `rollenAusProfil` — mit den
  // fachlichen Rollen (Nr. 173), solange die noch laden mit der Systemrolle.
  const tourRollen: TutorialRolle[] = useLernRollen(userProfile) ?? ['K'];
  /**
   * Darf die Willkommens-Tour von selbst anlaufen?
   *
   * Nur auf einer fertig aufgebauten, unverdeckten Oberfläche: die Tour sucht
   * ihre Anker im DOM, und was hinter einem Dialog liegt, findet sie zwar,
   * zeigt aber auf etwas, das niemand anfassen kann. Mobil bleibt sie ganz
   * aus (das „?" und das Overlay sind Desktop-Sachen, siehe MOBIL_GRENZE in
   * tourLogic) — der manuelle Start aus dem Lernzentrum bleibt überall.
   */
  const willkommensTourErlaubt =
      !!userProfile
      && !istMobil
      && !bauphase.showScreen
      && userProfile.role !== 'freelancer'
      && !isProfileOpen
      && !isHolidayPlannerOpen
      && !showSetupWizard
      && !mobileMenuOpen
      && !importPreview.isOpen
      && !pendingStatusChange
      && incomingTransfers.length === 0;
  const tour = useTour({
      view,
      rollen: tourRollen,
      willkommenErlaubt: willkommensTourErlaubt,
      // Führend ist die Datenbank, nicht der Browser (Migration Nr. 102).
      // Ohne Profil bleibt der Wert `undefined` — dasselbe Signal wie „Spalte
      // gibt es noch nicht", und dann entscheidet der Browser-Merker. Anlaufen
      // kann die Tour ohne Profil ohnehin nicht (`willkommensTourErlaubt`).
      // `?? null` ist nötig: fehlt die Spalte in dieser Datenbank, liefert das
      // Profil `undefined`, und das soll auch `undefined` bleiben.
      ersteinweisungAm: userProfile ? userProfile.ersteinweisung_am : undefined,
      merkeErsteinweisung: () => {
          void (async () => {
              const gesetzt = await merkeErsteinweisung();
              // Das Profil im Zustand nachziehen, sonst steht `ersteinweisungAm`
              // für den Rest des Sitzungslaufs auf `null` und der nächste
              // Ansichtswechsel wertet die Bedingung erneut aus. Schlug das
              // Schreiben fehl (Spalte fehlt noch), bleibt das Profil unberührt
              // — dann trägt der Browser-Merker die Sitzung.
              if (gesetzt) setUserProfile(p => (p ? { ...p, ersteinweisung_am: gesetzt } : p));
          })();
      },
      // Direkt auf den Dienst statt über useLernzentrum: der Hook lädt
      // Kapitelinhalte und Fortschritt und gehört in die Lernzentrums-Ansicht,
      // nicht in die App-Schale. Ein zweiter Aufruf hier wäre ein zweiter
      // Ladelauf bei jedem Start, nur um zwei Schreibfunktionen zu bekommen.
      markiereAbgeschlossen: (id) => { void merkeTourAbschluss(id); },
      merkeAbbruch: (id, schrittIndex) => { void markiereTourAbgebrochen(id, schrittIndex); },
  });

  const handleImportConfirm = async (action: 'create_new' | 'update', data: Report) => {
      setImportPreview({ isOpen: false, data: null, existingProject: null });
      // Neu anlegen nur mit dem Recht fuer den Projekttyp des Imports (fehlt
      // der Typ: SiGeKo). Vor dem Hochladen, nicht erst an der Absage der
      // Datenbank (Nr. 154). Ein Update legt nichts an.
      const verweigert = action === 'create_new' ? projektAnlegenVerweigert(rechte, data?.metadata?.projectType) : null;
      if (verweigert) {
          setZugriffHinweis({ titel: 'Import nicht möglich', text: verweigert });
          return;
      }
      setLoading(true);
      try {
          if (action === 'create_new') {
              const neu = await saveProjectToCloud(data, undefined);
              // Projekt-Kickoff (docs/projekt-kickoff-plan.md §4.4): die
              // Quittungszeile legt der DB-Trigger an; hier nur Mail und
              // Popup anstoßen — ohne zu warten, der Import ist fertig.
              if (neu?.id) {
                  const erstellerName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ').trim() || undefined;
                  void benachrichtigeKickoff([neu.id], { erstellerName })
                      .catch(err => console.warn('Kickoff nach JSON-Import nicht ausgelöst:', err))
                      .finally(() => window.dispatchEvent(new Event(KICKOFF_AKTUALISIEREN)));
              }
          }
          else if (action === 'update' && importPreview.existingProject) await saveProjectToCloud(data, importPreview.existingProject.id);
          alert("Import erfolgreich."); fetchProjects();
      } catch (e: any) { alert("Fehler beim Import: " + e.message); } 
      finally { setLoading(false); }
  };

  const handleTransferResponse = async (projectId: string, action: 'accept' | 'reject') => {
      setProcessingTransferId(projectId);
      try { await respondToTransfer(projectId, action); await fetchProjects(true); } 
      catch (e: any) { alert(e.message); } 
      finally { setProcessingTransferId(null); }
  };

  // ═══════════════════════════════════════════════════════════════
  // FIX: handleUpdateReport — improved nextScheduledDate recalculation
  // When the inspection date changes, ALWAYS recalculate the next date.
  // Old bug: If next=31.03 and inspection=30.03, old code said "31>30 = valid"
  // and didn't recalculate. But the correct next date should be 07.04 (next week).
  // ═══════════════════════════════════════════════════════════════
  const handleUpdateReport = (updatedReport: Report) => {
      const config = updatedReport.metadata.intervalConfig;
      const currentDate = updatedReport.metadata.date;
      
      if (config && config.type !== 'manual' && currentDate) {
          const prevDate = report?.metadata.date;
          const currentNext = config.nextScheduledDate;
          
          const dateChanged = prevDate !== currentDate;

          if (dateChanged && currentDate) {
              // Always recalculate from actual inspection date when it changes
              const autoNextDate = calculateNextDueDate(currentDate, config);
              if (autoNextDate) {
                  // Update if: no current next, or current next is on/before inspection date,
                  // or the calculated date is later (inspection happened, push schedule forward)
                  const shouldUpdate = !currentNext || currentNext <= currentDate || autoNextDate > currentNext;
                  if (shouldUpdate) {
                      updatedReport.metadata.intervalConfig = {
                          ...config,
                          nextScheduledDate: autoNextDate
                      };
                  }
              }
          }
      }

      setReport(updatedReport);

      if (currentProjectId) {
          setProjects(prev => prev.map(p => 
              p.id === currentProjectId 
              ? { 
                  ...p, 
                  data: updatedReport,
                  project_services: mapServicesToDb(updatedReport.metadata.globalData?.services || [])
                } 
              : p
          ));
      }

      if (isProjectReadOnly) {
          // Der lokale State oben ist bewusst schon aktualisiert (sonst waeren
          // die Formulare unbedienbar). Damit die Eingabe nicht wie gespeichert
          // aussieht, zeigt die Statusanzeige dauerhaft "Nicht gespeichert
          // (Nur-Ansicht)" — abgeleitet aus isProjectReadOnly, siehe
          // displayedSaveStatus. Deshalb hier KEIN setSaveStatus: ein flüchtiger
          // Wert wuerde vom naechsten Queue-Ereignis wieder ueberschrieben.
          return;
      }

      setSaveStatus('saving');
      // Lokal zuerst: in die Offline-Queue, der Debounce flusht zur Cloud.
      // Die lokale Sicherung ist seit dem 30.09.2026 selbst gebuendelt
      // (300 ms Ruhe, spaetestens 2 s — useOfflineSync.enqueue): bei 6051
      // kostete jeder Anschlag sonst ~4 MB Kopierarbeit auf dem Hauptthread.
      // Der Cloud-Flush unten liest die Warteschlange und schreibt dabei das
      // Wartende vorher fest (Barriere in offlineQueue.ts) — er laedt also
      // den juengsten Anschlag hoch.
      if (currentProjectId) {
          // Der Ausgangsstand geht mit — nur mit ihm lässt sich später
          // unterscheiden, ob eine fehlende Zeile entfernt oder nie gesehen
          // wurde. Gehört der gemerkte Stand zu einem anderen Projekt (Wechsel
          // während des Ladens), geht KEINER mit: ein falscher Ausgangsstand
          // wäre schlimmer als keiner.
          const base = projectBaseRef.current?.projectId === currentProjectId
              ? projectBaseRef.current
              : null;
          void offlineSync.enqueue(currentProjectId, updatedReport, base);
      }
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
          if (!currentProjectId) return;
          void offlineSync.flushProject(currentProjectId);
      }, 2000);
  };

  /**
   * Spaete Ergebnisse eines schon ausgehaengten Pruefmoduls (Foto-Upload,
   * Assistent nach „Fertig", Beweisbild) — nie auf den eingefrorenen Stand
   * des Moduls: dessen alter `onUpdate` (Closure seines letzten Renders)
   * setzte sonst den App-Bericht zurueck, nach einem Projektwechsel sogar auf
   * die Daten des anderen Projekts.
   *
   * Deshalb ein FUNKTIONALES setReport: React rechnet die Aenderung auf den
   * Stand, der beim Anwenden gilt. Eine noch nicht gerenderte Eingabe bleibt
   * erhalten; ist inzwischen ein anderes Projekt geladen (Wechsel noch nicht
   * gerendert), bleibt dessen Bericht unberuehrt. Gespeichert (Projektliste,
   * Warteschlange) wird erst der gerenderte Stand — im Effekt darunter, ueber
   * handleUpdateReport wie jede Eingabe. Faellt der spaete Stand in ein
   * Rennen mit einer gleichzeitigen Eingabe, gewinnt die Eingabe: das spaete
   * Ergebnis kann dann verloren gehen, falsch geschrieben wird nie.
   * false = sofort verworfen (Projekt nicht mehr offen).
   */
  const spaetErgebnisRef = useRef<Report | null>(null);
  const spaetAendern = useCallback((projectId: string, aendern: (r: Report) => Report): boolean => {
      if (projectId !== currentProjectIdRef.current) return false;
      setReport(prev => {
          if (!prev) return prev;
          const berichtProjekt = prev.metadata?.globalData?.projectId;
          if ((berichtProjekt || currentProjectIdRef.current) !== projectId) return prev;
          const neu = aendern(prev);
          if (neu !== prev) spaetErgebnisRef.current = neu;
          return neu;
      });
      return true;
  }, []);
  useEffect(() => {
      const neu = spaetErgebnisRef.current;
      if (!neu) return;
      spaetErgebnisRef.current = null;
      // Nur der gerenderte spaete Stand: kam eine Eingabe obendrauf, speichert sie selbst.
      if (neu === report) handleUpdateReport(neu);
  });

  const handleCreateNewVersion = (newReport: Report) => {
      handleUpdateReport(newReport);
      setActiveSection('items'); 
  };

  const handleSetupComplete = async (data: any) => {
      // Der Assistent bietet nur erlaubte Typen an; die Wache hier faengt
      // einen Rest ab (Rechte zwischendurch entzogen), bevor gespeichert wird.
      const verweigert = projektAnlegenVerweigert(rechte, data?.projectType);
      if (verweigert) {
          setZugriffHinweis({ titel: 'Projekt nicht angelegt', text: verweigert });
          return;
      }
      const { count } = await supabase.from('projects').select('*', { count: 'exact', head: true }).eq('project_number', data.projectNumber);
      if (count && count > 0) { alert(`Projekt-Nr. "${data.projectNumber}" existiert bereits.`); return; }
      setShowSetupWizard(false); setLoading(true);
      try {
          const assignedUser = data.assignedUser || userProfile;
          const coordName = assignedUser?.first_name ? `${assignedUser.first_name} ${assignedUser.last_name}` : (userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : '');
          // Verantwortliche Planungsphase (Assistent, nur Admins;
          // docs/projekt-kickoff-plan.md §2/§5): mehrere Personen möglich, nur
          // ANDERE als der Besitzer, ohne Dubletten. Ein älterer Assistent
          // liefert noch `planningUser` (eine Person) — planungsPersonenAusSetup
          // nimmt dann diese.
          const planningUsers = planungsPersonenAusSetup(data, assignedUser?.id);
          const planningNames = planningUsers.map(profilName);

          const assignmentNoteId = crypto.randomUUID();
          const assignmentNote = {
             id: assignmentNoteId,
             text: zuweisungsNotizText(coordName, planningNames),
             date: new Date().toISOString(),
             // Realer Name statt 'System': die Zuweisung macht ein Mensch (der
             // eingeloggte Nutzer legt das Projekt an), und note.author wird im
             // Protokoll wörtlich angezeigt.
             author: userProfile ? `${userProfile.first_name} ${userProfile.last_name}`.trim() : 'System',
             readBy: [],
             mentions: [assignedUser?.id, ...planningUsers.map(u => u.id)].filter(Boolean)
          };

          const newReport: Report = {
              metadata: { projectNumber: data.projectNumber, projectName: data.projectName, projectAddress: data.address, inspectionNumber: 0, 
              projectCategory: data.projectCategory,
              projectSubcategory: data.projectSubcategory,
              // 'sifa' schaltet den Hub auf das SiFa-Raster; ohne Angabe
              // bleibt es beim Altverhalten (kein Typ = SiGeKo).
              projectType: data.projectType,
              // Betriebsdaten der SiFa-Betreuung aus dem Wizard (nur bei 'sifa' gesetzt).
              sifa: data.sifa,
              category: data.projectCategory,
              subcategory: data.projectSubcategory,
              date: '', 
              time: '08:00', conductedBy: 'coordinator', coordinator: coordName, coordinatorId: assignedUser?.id, coordinatorEmail: assignedUser?.email || '', coordinatorPhone: assignedUser?.phone || '', clientName: data.clientName, clientAddress: data.clientDetails?.address, clientContactPerson: data.clientDetails?.contact, clientPhone: data.clientDetails?.phone, clientEmail: data.clientDetails?.email, customerName: data.customerName, customerAddress: data.customerDetails?.address, customerContactPerson: data.customerDetails?.contact, customerPhone: data.customerDetails?.phone, customerEmail: data.customerDetails?.email, distributionList: [], plans: [], intervalConfig: data.intervalConfig, 
              globalData: { 
                  projectId: 'temp-id', 
                  // Eine SiFa-Betreuung beginnt laufend (initialStatus 'active'),
                  // eine Baustelle in der Planung (docs/sifa-betreuung-plan.md §1).
                  status: data.initialStatus ?? 'planning', 
                  startDate: data.startDate, 
                  endDate: data.endDate, 
                  services: data.services, 
                  serviceLog: [], 
                  notes: [assignmentNote], 
                  locationCoordinates: data.coordinates 
              } },
              // footerText mitgeben: ohne ihn druckt der Pruefbericht-Fuss
              // die Firmenanschrift, obwohl zentral etwas anderes gepflegt ist.
              settings: { companyName: userProfile?.company_name, companyAddress: userProfile?.company_address, companyLogo: userProfile?.company_logo, appMode: userProfile?.app_mode || 'sigeko', emailSignatureTemplate: userProfile?.settings?.emailSignatureTemplate || '', footerText: (userProfile as any)?.settings?.footerText },
              items: [], checklists: [], mutualHazards: [], safetyPoints: []
          };

          // Planungspersonen in die Vertreterliste — VOR dem Speichern: der
          // DB-Trigger legt ihre Kickoff-Quittungen beim Insert aus genau dieser
          // Liste an, und ueber ihre id erhalten sie Projektzugriff
          // (representatives_access.sql). Position 0 bleibt der Besitzer
          // (Hauptverantwortlicher, Ausfuehrung); applyRepresentatives spiegelt
          // nur ihn in die Altfelder, die Planungseintraege stehen allein im Array.
          if (planningUsers.length > 0 && assignedUser) {
              newReport.metadata = applyRepresentatives(newReport.metadata, [
                  { id: assignedUser.id, name: coordName, email: assignedUser.email, phone: assignedUser.phone },
                  ...planungsEintraege(planningUsers),
              ]);
          }
          
          const newRow = await saveProjectToCloud(newReport, undefined, assignedUser?.id);

          if (newRow?.id) {
             // Projekt-Kickoff (docs/projekt-kickoff-plan.md §4.2): Mail mit
             // Kalenderdatei an Besitzer und Planungsperson, danach das Popup
             // neu laden. Bewusst ohne await — das Oeffnen des Projekts wartet
             // nicht auf den Mailversand.
             const erstellerName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ').trim() || undefined;
             void benachrichtigeKickoff([newRow.id], { erstellerName })
                 .catch(err => console.warn('Kickoff nach Projektanlage nicht ausgelöst:', err))
                 .finally(() => window.dispatchEvent(new Event(KICKOFF_AKTUALISIEREN)));
             if (data.crmCustomerId) {
                 // Nur die FK-Spalte setzen, nicht data/updated_at (Sync-Anker bleibt unberuehrt).
                 linkProjectsToCustomer(data.crmCustomerId, [newRow.id]).catch(err =>
                     console.warn('CRM-Kundenverknuepfung fehlgeschlagen:', err));
             }
             if (assignedUser) {
                 await createNotificationsForMentions(
                     [assignedUser.id], 
                     assignmentNoteId,
                     `Neues Projekt zugewiesen: ${data.projectName}`, 
                     newRow.id, 
                     data.projectName, 
                     userProfile?.id || 'system', 
                     userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : 'System'
                 );
             }
             // Die Planungspersonen erfahren es auf demselben Weg wie der
             // Besitzer — jede bekommt ihre eigene Glocken-Mitteilung.
             if (planningUsers.length > 0) {
                 await createNotificationsForMentions(
                     planningUsers.map(u => u.id),
                     assignmentNoteId,
                     `Neues Projekt zugewiesen (Planung): ${data.projectName}`,
                     newRow.id,
                     data.projectName,
                     userProfile?.id || 'system',
                     userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : 'System'
                 );
             }
             handleOpenProject(newRow);
          }
      } catch (e: any) { alert("Fehler beim Erstellen: " + rechtFehlerOder(e, f => (f as { message?: string } | null)?.message || String(f))); } 
      finally { setLoading(false); }
  };
  
  // --- MOBILE FAB HANDLER ---
  /**
   * „+" in der mobilen Leiste.
   *
   * Zuerst die offene Ansicht fragen (Vertrag V2): das Pruefmodul oeffnet dann
   * selbst den Mangel-Assistenten, und hier geschieht nichts weiter. Sonst den
   * Standort suchen — sichtbar und mit Frist — und das Ergebnis vorlegen,
   * statt ungefragt zu oeffnen: ein Projekt im Umkreis bestaetigen, mehrere
   * (Lose an einer Adresse) zur Wahl, keines oder kein Standort → die
   * Projektauswahl wie bisher, mit einem Satz, warum.
   *
   * Steht ueber den fruehen Returns und schliesst nur, was darueber
   * deklariert ist. Die Rueckrufe des Standorts pruefen die Suchnummer: eine
   * abgebrochene oder ueberholte Suche oeffnet nichts mehr.
   */
  const handleMobileFabClick = () => {
    if (meldeNeuerEintrag(window)) return;
    const suche = ++plusSucheRef.current;
    setPlusOeffnet(null);
    setPlusBlattHinweis(null);
    const zurProjektauswahl = (hinweis: string) => {
        setPlusBlatt(null);
        setPlusHinweis(hinweis);
        setShowMobileProjectSelector(true);
    };
    if (!("geolocation" in navigator)) {
        zurProjektauswahl(plusStandortFehler());
        return;
    }
    setPlusHinweis(null);
    setPlusBlatt({ art: 'suche' });
    navigator.geolocation.getCurrentPosition(
        (position) => {
            if (suche !== plusSucheRef.current) return;
            const schritt = plusSchrittNachStandort(projects, position.coords.latitude, position.coords.longitude);
            if (schritt.art === 'liste') zurProjektauswahl(schritt.hinweis);
            else setPlusBlatt(schritt);
        },
        (err) => {
            if (suche !== plusSucheRef.current) return;
            console.warn('[plus] Standort nicht ermittelt', err);
            zurProjektauswahl(plusStandortFehler(err?.code));
        },
        PLUS_STANDORT_OPTIONEN,
    );
  };

  /** Blatt schliessen und eine laufende Suche verwerfen (Kreuz, Android-Zurueck). */
  const schliessePlusBlatt = () => {
    plusSucheRef.current += 1;
    setPlusBlatt(null);
    setPlusOeffnet(null);
    setPlusBlattHinweis(null);
  };

  /** „Anderes Projekt" bzw. „Projekt selbst wählen": die bisherige Projektauswahl. */
  const plusZurProjektauswahl = () => {
    schliessePlusBlatt();
    setPlusHinweis(null);
    setShowMobileProjectSelector(true);
  };

  /**
   * Ein Projekt im Blatt oder in der Projektauswahl angetippt: das schon
   * geladene fuehrt in seinen Hub statt es neu zu laden, eine offene
   * Maske verbietet den Wechsel (plusOeffnenSchritt).
   */
  const plusSchrittFuer = (projekt: PlusProjekt) => plusOeffnenSchritt({
      trefferId: projekt.id,
      offenesProjektId: report ? currentProjectId : null,
      inBegehung: view === 'module-sigeko-inspection',
      eingabeOffen: isIdleExitHeld(),
  });

  /**
   * Treffer im Blatt. Beim Oeffnen bleibt das Blatt mit „Wird geöffnet …"
   * stehen, bis das Projekt da ist: Fremdpruefung und Sperre brauchen bei
   * schlechtem Netz Sekunden, und der Ladebildschirm kommt erst danach. Ein
   * inzwischen geschlossenes oder neues Blatt (Suchnummer) bleibt unberuehrt.
   */
  const oeffnePlusTreffer = async (projekt: ListProject) => {
    if (plusOeffnet !== null) return;
    const schritt = plusSchrittFuer(projekt);
    if (schritt === 'warten') { setPlusBlattHinweis(PLUS_OFFENE_EINGABE); return; }
    if (schritt === 'schliessen') { schliessePlusBlatt(); return; }
    if (schritt === 'hub') {
        schliessePlusBlatt();
        setView('project-hub');
        return;
    }
    const suche = plusSucheRef.current;
    setPlusBlattHinweis(null);
    setPlusOeffnet(projekt.id);
    try {
        await handleOpenProject(projekt);
    } finally {
        if (suche === plusSucheRef.current) schliessePlusBlatt();
    }
  };

  /** Zeile der Projektauswahl (Rueckfall des „+"): dieselbe Entscheidung wie im Blatt. */
  const waehleAusProjektauswahl = (projekt: ListProject) => {
    const schritt = plusSchrittFuer(projekt);
    if (schritt === 'oeffnen') { void handleOpenProject(projekt); return; }
    if (schritt === 'warten') { setPlusHinweis(PLUS_OFFENE_EINGABE); return; }
    setShowMobileProjectSelector(false);
    if (schritt === 'hub') setView('project-hub');
  };

  // Sperren fuer den mobilen Auswahldialog „Projekt wählen". Der Hook muss VOR
  // den fruehen Returns stehen (Regeln der Hooks); die Liste selbst entsteht
  // weiter unten als `filteredMobileProjects`. Vorfilter und Sortierung sind
  // hier bewusst dieselben, damit die Obergrenze in `pickLockQueryIds` bei sehr
  // vielen Projekten die zuletzt benutzten behaelt und nicht irgendwelche.
  // `enabled` haengt am Dialog: ist er zu, laeuft weder Abo noch Zeitgeber.
  // Das „+"-Blatt zeigt dieselben Sperren an seinen Treffern.
  const mobileSelectorLockIds = useMemo(
      () => projects
          .filter((p) => (p.status || p.data?.metadata?.globalData?.status) !== 'finished')
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          .map((p) => p.id),
      [projects],
  );
  const { locks: mobileSelectorLocks, nowMs: mobileSelectorLockNow } =
      useProjectLocks(mobileSelectorLockIds, showMobileProjectSelector || plusBlatt !== null);

  // ─── Android-App: Zurueck-Taste, Download-Seite (docs/android-app-plan.md 4, P4)
  //
  // Alles hier steht VOR den fruehen Returns (Regeln der Hooks) und schliesst
  // nur, was schon darueber deklariert ist — die TDZ-Lehre vom 13.09.2026.

  /**
   * Zurueck-Taste der App: EIN Zuhoerer fuer alle Bildschirme, auch
   * Anmeldemaske, Ladeschirm, Bauphase und Freelancer-Portal — dort minimiert
   * er nur. Die volle Oberflaeche traegt ihren Rueckweg ueber
   * `AndroidZurueckTaste` in `oberflaechenZurueckRef` ein; so wirkt immer
   * genau einer. Die Closure kennt nur den Ref und Importe.
   */
  const oberflaechenZurueckRef = useRef<(() => boolean) | null>(null);
  useEffect(() => {
      if (!istNativeApp() || !hatPlugin('App')) return;
      let abgebaut = false;
      let handle: PluginListenerHandle | null = null;
      CapacitorApp.addListener('backButton', () => {
          const oberflaeche = oberflaechenZurueckRef.current;
          if (oberflaeche && oberflaeche()) return;
          void CapacitorApp.minimizeApp().catch(() => undefined);
      })
          .then((h) => {
              // Schon abgebaut, waehrend die Anmeldung lief: sofort wieder weg.
              if (abgebaut) void h.remove();
              else handle = h;
          })
          .catch((e) => console.warn('[App] Zurueck-Taste nicht angemeldet', e));
      return () => {
          abgebaut = true;
          if (handle) void handle.remove();
      };
  }, []);

  /**
   * Download-Seite `#/app` (Ziel des QR-Codes). Gilt auch im Browser. Ohne
   * Anmeldung steht zuerst die Anmeldemaske; der Hash bleibt dabei stehen, und
   * nach dem Anmelden erscheint die Seite.
   *
   * Die Adresse ist die einzige Quelle: `appSeiteOffen` folgt allein dem
   * `hashchange`. Oeffnen setzt den Hash, Schliessen nimmt ihn zurueck — so
   * rufen oeffneAppSeite/schliesseAppSeite nie selbst einen State-Setter und
   * duerfen auch aus einem Effekt heraus laufen.
   */
  const [appSeiteOffen, setAppSeiteOffen] = useState(() => istAppSeitenAdresse(window.location.hash));
  /** Selbst geoeffnet (eigener Verlaufseintrag) → mit history.back() schliessen. */
  const appSeiteSelbstRef = useRef(false);
  useEffect(() => {
      const beiHashWechsel = () => {
          const offen = istAppSeitenAdresse(window.location.hash);
          if (!offen) appSeiteSelbstRef.current = false;
          setAppSeiteOffen(offen);
      };
      window.addEventListener('hashchange', beiHashWechsel);
      return () => window.removeEventListener('hashchange', beiHashWechsel);
  }, []);
  const oeffneAppSeite = useCallback(() => {
      if (istAppSeitenAdresse(window.location.hash)) return;
      // Der Hash legt einen Verlaufseintrag: die Zurueck-Geste des Browsers
      // fuehrt von der Seite wieder weg (hashchange oben).
      appSeiteSelbstRef.current = true;
      window.location.hash = '#/app';
  }, []);
  const schliesseAppSeite = useCallback(() => {
      if (!istAppSeitenAdresse(window.location.hash)) return;
      if (appSeiteSelbstRef.current) {
          // Den eigenen Verlaufseintrag wieder abgeben statt einen zweiten,
          // gleichen darueberzulegen — sonst fuehrte das naechste „Zurueck"
          // im Browser ins Leere.
          appSeiteSelbstRef.current = false;
          window.history.back();
          return;
      }
      // Von aussen gekommen (QR, Lesezeichen): nur die Adresse zuruecksetzen,
      // ohne Neuladen. replaceState meldet kein hashchange — der Zustand folgt
      // der Adresse ueber dasselbe Ereignis wie sonst.
      try {
          window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
      } catch { return; /* Verlauf gesperrt — die Seite bleibt offen */ }
      window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);
  // Jeder Ansichtswechsel verlaesst die Seite — egal, wer ihn ausloest (Menue,
  // Leiste, Mitteilung, Tour). Der Merker steht beim Restore weiter oben.
  useEffect(() => {
      if (appSeiteViewRef.current === view) return;
      appSeiteViewRef.current = view;
      schliesseAppSeite();
  }, [view, schliesseAppSeite]);

  // ─── Handy-Browser: Zurueck-Geste ueber den Verlauf ───────────────────────
  //
  // Ohne App gibt es keine Zurueck-Taste, die die Anwendung abfangen koennte:
  // die Geste (Android-Browser, Wischen auf dem iPhone) ging im Verlauf
  // zurueck und verliess die Seite, auch mitten im Pruefmodul. Jetzt legt der
  // Verlaufswaechter (appUmfang.erzeugeZurueckWaechter) einen eigenen Eintrag,
  // solange eine Rueckkehr in der Anwendung moeglich ist; nimmt die Geste ihn
  // weg, laeuft dieselbe Entscheidung wie fuer die Android-Taste
  // (handleAndroidZurueck ueber oberflaechenZurueckRef).
  //
  // Nur im Browser, nur mobil, nur auf der vollen Oberflaeche (nur dort ist
  // oberflaechenZurueckRef gesetzt). In der App traegt die Zurueck-Taste,
  // Desktop und Tablet bleiben wie bisher. Nach einem Neuladen uebernimmt er
  // den Eintrag des vorigen Ladens; bewusste Grenzen stehen an
  // erzeugeZurueckWaechter. Steht VOR den fruehen Returns und schliesst nur,
  // was darueber deklariert ist (TDZ-Lehre vom 13.09.2026).

  /** Lage der Zurueck-Entscheidung — Android-Taste und Verlaufswaechter lesen dieselbe. */
  const zurueckLage = (): ZurueckLage => ({
      eingabeFokussiert: istEingabeFokussiert(document.activeElement),
      menueOffen: mobileMenuOpen,
      // Das „+"-Blatt ist eine Projektauswahl: Zurueck schliesst es.
      projektauswahlOffen: showMobileProjectSelector || plusBlatt !== null,
      statuswechselOffen: pendingStatusChange !== null,
      overlayImVerlauf: istOverlayVerlauf(window.history.state),
      registriert: hatRegistriertenZurueck(),
      dialogOffen: offenerDialogImDom(document),
      appSeiteOffen,
      aufStartseite: view === 'dashboard',
  });
  const zurueckWaechterRef = useRef<ZurueckWaechter | null>(null);
  const waechterOberflaecheRef = useRef<WaechterOberflaeche | null>(null);
  // Nach jedem Zeichnen: frische Lage fuer den Waechter, dann abgleichen.
  // Ohne volle Oberflaeche (Ladeschirm, Anmeldung, Bauphase, Freelancer) ist
  // oberflaechenZurueckRef leer — dann ruht er. AndroidZurueckTaste ist ein
  // Kind: ihr Effekt hat den Ref in diesem Durchlauf schon gesetzt.
  useEffect(() => {
      waechterOberflaecheRef.current = oberflaechenZurueckRef.current
          ? {
              aufStartseite: view === 'dashboard',
              lage: zurueckLage,
              zurueck: () => { oberflaechenZurueckRef.current?.(); },
          }
          : null;
      zurueckWaechterRef.current?.abgleichen();
  });
  useEffect(() => {
      if (istNativeApp() || !istMobil) return;
      const waechter = erzeugeZurueckWaechter({
          verlauf: window.history,
          adresse: () => window.location.href,
          hash: () => window.location.hash,
          jetzt: () => Date.now(),
          planen: (fn, ms) => window.setTimeout(fn, ms),
          abbrechen: (handle) => window.clearTimeout(handle),
          oberflaeche: () => waechterOberflaecheRef.current,
          kennung: ZURUECK_WAECHTER_KENNUNG,
      });
      zurueckWaechterRef.current = waechter;
      const beiPopstate = () => waechter.beiPopstate();
      // Beruehrungen zaehlen fuer den Browser als Nutzerhandlung — nur mit
      // einer frischen legt der Waechter einen neuen Eintrag an. Ob eine
      // zaehlt (echt, nicht Escape; ein a.click() im Code nicht), entscheidet
      // appUmfang.zaehltAlsBeruehrung.
      const beiBeruehrung = (e: Event) => waechter.beiBeruehrung(e);
      const optionen: AddEventListenerOptions = { capture: true, passive: true };
      const beruehrungen = ['pointerup', 'click', 'keydown'] as const;
      window.addEventListener('popstate', beiPopstate);
      for (const typ of beruehrungen) window.addEventListener(typ, beiBeruehrung, optionen);
      waechter.abgleichen();
      return () => {
          window.removeEventListener('popstate', beiPopstate);
          for (const typ of beruehrungen) window.removeEventListener(typ, beiBeruehrung, optionen);
          waechter.beenden();
          if (zurueckWaechterRef.current === waechter) zurueckWaechterRef.current = null;
      };
  }, [istMobil]);

  // Der Lesemodus gehoert zum geoeffneten Projekt. Zurueckgenommen wurde er
  // bisher nur im Zurueck-Weg (onBackToDashboard); wer das Projekt anders
  // verliess — Seitenleiste, mobile Navigation, "Buchhaltung dieses Projekts
  // öffnen" —, sah in Rechnungen, Kunden oder Dashboard weiter "Nicht
  // gespeichert (Nur-Ansicht)".
  //
  // Zurueckgesetzt wird ausschliesslich beim Sprung in eine projektfremde
  // Ansicht (isProjectView, einzige Quelle: VIEW_KIND in
  // navigationPersistence) — die Modulwechsel innerhalb des Projekts behalten
  // den Schutz. Und weil der Schutz faellt, wird das Projekt dabei auch
  // wirklich geschlossen: ein noch geladener Bericht ohne Schutzflagge waere
  // schlimmer als ein falsches Abzeichen.
  //
  // Der SCHREIBENDE Weg aus den Projektansichten schliesst das Projekt
  // dagegen NICHT — dort geht jemand kurz in die Rechnungen und kommt zurueck.
  // Die Sperre gibt der Anwesenheits-Effekt nach der Karenzzeit ab; hier
  // stehen zu lassen, was steht, ist Absicht.
  const navigateToView = (next: AppView) => {
      if (isProjectReadOnly && !isProjectView(next)) {
          // Hier wird das Projekt wirklich geschlossen (setReport(null) unten),
          // also gehoert das auch in die Erfassung. Ohne diese Zeile traege die
          // Wache das Projekt weiter und der spaetere `switch`-Close datierte
          // auf das naechste Oeffnen — die Verweildauer waere zu gross
          // gemeldet, und Zahlen sind der einzige Zweck dieser Statistik.
          recordProjectClose({ projectId: currentProjectId, reason: 'manual' });
          releaseHeldLock();
          setProjectReadOnly(false);
          setExplicitReadOnly(false);
          setLockBlocked(false);
          resetForeignAccess();
          setProjectLock(null);
          setReport(null);
          setCurrentProjectId(null);
      }
      // Menueklick auf einen Verwaltungsbereich oeffnet dessen ersten Reiter
      // (adminBereiche.ts) — jeder Bereich hat seit dem Menue-Umbau eigene
      // Reiter, ein gemerkter Reiter aus einem anderen Bereich passte nicht.
      // Nur hier — der Rueckweg aus einem Projekt (handleLeaveProject) laesst
      // lastAdminTab stehen und landet damit auf dem Reiter, von dem aus das
      // Projekt geoeffnet wurde.
      if (istAdminBereichView(next)) setLastAdminTab(ersterTab(next));
      // Die Download-Seite `#/app` liegt ueber jeder Ansicht; wer ueber Menue
      // oder Leiste wegnavigiert, verlaesst sie.
      if (appSeiteOffen) schliesseAppSeite();
      setView(next);
  };

  /**
   * Lernzentrum oeffnen, wahlweise direkt auf einem Kapitel (Kontext-"?").
   *
   * Stabil ueber alle Renders: der Rueckruf steckt im Wert von
   * `LernzentrumOeffnerContext` und im Kontext-"?" jeder Ansicht — waere er bei
   * jedem Render ein anderer, renderte jeder Verbraucher mit.
   *
   * Stabil bekommt man ihn nur ueber die Referenz: `navigateToView` wird bei
   * jedem Render neu gebaut und haengt an halb der Zustandswelt dieser Datei;
   * stuende es im Abhaengigkeitsblock, waere nichts gewonnen. Der Effekt ohne
   * Abhaengigkeiten schreibt nach jedem Render die frische Fassung in die
   * Referenz — der Klick eines Nutzers kommt immer danach.
   *
   * Beides steht hier oben, weil darunter die fruehen Returns (Ladebildschirm,
   * Anmeldung, Freelancer) folgen und Hooks in jedem Render in derselben
   * Reihenfolge laufen muessen. Und `navigateToView` steht unmittelbar DAVOR,
   * nicht dahinter: der Effekt laeuft auch nach einem Render, der frueh
   * aussteigt. Stuende die Deklaration hinter den Returns, griffe die Closure
   * sie in der temporalen Totzone ab — genau daran starb die Anmeldemaske.
   */
  const navigateToViewRef = useRef<(next: AppView) => void>(() => {});
  useEffect(() => {
      navigateToViewRef.current = navigateToView;
  });
  const handleOpenLernzentrum = useCallback((kapitelId?: TutorialKapitelId) => {
      setLernzentrumKapitelId(kapitelId ?? null);
      navigateToViewRef.current('lernzentrum');
  }, []);

  if (loading) return (
    <div className="app-shell h-screen flex flex-col items-center justify-center gap-6">
      {/* Der Startbildschirm zeigt die Marke, nicht nur einen Spinner. Er ist
          bei jedem Kaltstart die erste Flaeche, die jemand sieht — ein blosses
          Rad darauf verschenkt den Moment. */}
      <div className="relative">
        <div className="absolute -inset-10 rounded-full bg-blue-500/10 blur-2xl" />
        <h1 className="relative text-4xl font-black tracking-[-0.05em] text-slate-900">
          OS<span className="text-blue-600">:</span>IA
        </h1>
      </div>

      {/* Ein Balken, der von links einlaeuft — er zeigt Fortschritt an, wo ein
          rotierendes Rad nur Beschaeftigung zeigt. */}
      <div className="relative w-44 h-[3px] rounded-full bg-slate-900/[0.07] overflow-hidden">
        <span
          className="absolute inset-y-0 w-1/3 rounded-full bg-gradient-to-r from-transparent via-blue-600 to-transparent"
          style={{ animation: 'bar-sweep 1.4s cubic-bezier(0.65,0,0.35,1) infinite' }}
        />
      </div>

      <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500 animate-fade-in">
        {loadingMessage || 'Lade Arbeitsbereich'}
      </p>
    </div>
  );
  if (isPasswordRecovery) return <PasswordResetView />;
  if (!session) return <AuthScreen notification={null} />;

  // ── Freelancer-Portal (ERP/CRM-Plan Phase D3) ──────────────────────────────
  // Die Rolle `freelancer` ist die einzige EXTERNE Rolle. Sie bekommt NICHT die
  // gewohnte Oberflaeche mit einem reduzierten Menue, sondern eine eigene:
  // dieser Rueckgabewert steht VOR der Sidebar, dem Projektzustand und jeder
  // Ansicht. Kein CRM, keine Projektliste, keine Rechnungen, keine
  // Admin-Konsole — nicht ausgeblendet, sondern gar nicht erst gerendert.
  //
  // Warum so und nicht ueber Rollenabfragen in der Sidebar: die Sidebar
  // entscheidet ueber `isAdmin`/`isSuperAdmin` und zeigt allen uebrigen
  // Angemeldeten Dashboard, Projekte, Aufgaben und Projektkarte. Ein
  // Freelancer faellt dort in den Zweig „normaler Benutzer" und saehe vier
  // Eintraege, hinter denen die Datenbank ihn aussperrt
  // (supabase/freelancer_portal.sql). Eine zusaetzliche Bedingung je Eintrag
  // waere vier Gelegenheiten, es beim naechsten Menuepunkt zu vergessen.
  //
  // Die Absicherung liegt ohnehin in der Datenbank, nicht hier: dieser Zweig
  // ist Bequemlichkeit fuer den Freelancer, keine Zugriffskontrolle.
  // ── Bauphase (Wartungsseite) ───────────────────────────────────────────────
  // VOR dem Freelancer-Zweig, damit sie fuer alle Rollen gilt.
  if (bauphase.showScreen) {
      return <BauphaseScreen message={bauphase.state.message} sinceMs={bauphase.state.locksAtMs} expiresAtMs={bauphase.state.expiresAtMs} queuedHint={bauphase.queuedHint} adminBypass={bauphase.state.adminBypass} onAdminEnter={bauphase.enterAsAdmin} onEndMaintenance={() => { void bauphase.endNow(); }} />;
  }

  if (userProfile?.role === 'freelancer') {
      return (
          <FreelancerPortal
              onLogout={handleLogout}
              anzeigeName={[userProfile.first_name, userProfile.last_name].filter(Boolean).join(' ')}
              bauphase={{ state: bauphase.state }}
          />
      );
  }

  /**
   * Die drei Werkzeuge am SiGe-Plan liegen unter EINER Huelle mit Reitern
   * (SigePlanModul). Die beiden alten Ansichten bleiben trotzdem gueltige
   * Werte: gemerkte Ansichten aus dem sessionStorage zeigen darauf, ebenso die
   * Sprungziele aus dem Handbuch und spaeter der Assistent. Sie oeffnen jetzt
   * die Huelle am passenden Reiter — es gibt keinen toten View.
   */
  const SIGE_PLAN_REITER: Partial<Record<AppView, SigePlanReiter>> = {
      'module-sige-plan': 'plan',
      'module-koordinierung': 'koordinierung',
      'module-handbuch': 'handbuch',
  };
  const SIGE_PLAN_VIEW: Record<SigePlanReiter, AppView> = {
      plan: 'module-sige-plan',
      koordinierung: 'module-koordinierung',
      handbuch: 'module-handbuch',
  };
  const sigePlanReiter = SIGE_PLAN_REITER[view];

  // Modulansicht = eine Ansicht INNERHALB des geoeffneten Projekts. Sie
  // entscheidet, ob der Zurueck-Weg in den Projekt-Hub fuehrt (statt das
  // Projekt zu verlassen) und ob die Seitenleiste den Abschnitt markiert.
  // Handbuch, Koordinierung und A+S-Plan fehlten hier — sie verliessen beim
  // Zurueck das Projekt ganz.
  const isModuleView =['module-vorank', 'module-unterlage', 'module-sigeko-inspection', 'module-archive', 'module-sige-plan', 'module-sige-einweisung', 'module-dokumente', 'module-planung', 'module-handbuch', 'module-koordinierung', 'module-asplan', 'module-bauordnung', VIEW_SIFA_UNTERWEISUNG, VIEW_SIFA_MITARBEITER, VIEW_SIFA_FRISTEN, VIEW_SIFA_TAETIGKEITEN, VIEW_SIFA_GBU].includes(view);

  const filteredMobileProjects = projects
      .filter(p => {
           // Beendet und Archiv wie in der Projektliste (ProjectOverview): raus.
           if (isProjectFinished(p)) return false;
           if (mobileSearchTerm) {
               return (p.name ?? '').toLowerCase().includes(mobileSearchTerm.toLowerCase()) ||
                      (p.project_number || '').includes(mobileSearchTerm);
           }
           return true;
      })
      .sort((a,b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  // Im Lesemodus wird nie gespeichert — die Statusanzeige darf deshalb nicht
  // beim letzten echten Stand ("Gespeichert") stehen bleiben. Abgeleitet statt
  // gesetzt, damit der Hinweis dauerhaft steht und beim Verlassen des Projekts
  // von selbst wieder verschwindet.
  const displayedSaveStatus = isProjectReadOnly ? 'read-only' as const : saveStatus;

  /**
   * "Jetzt in der App zeigen" — der Weg aus einem Kapitel in die echte Ansicht.
   *
   * Das Tour-Overlay kommt in Paket B. Bis dahin wird hier nur navigiert und die
   * Kapitel-Id unter `lernzentrum.tour` im sessionStorage hinterlegt; das Overlay
   * liest sie spaeter genau dort und muss diesen Weg nicht noch einmal bauen.
   * sessionStorage und nicht localStorage: eine begonnene Tour gehoert zu DIESEM
   * Tab und soll morgen nicht wieder aufspringen.
   *
   * Projektansichten brauchen ein geladenes Projekt. Ist keines offen, geht es
   * in die Projektliste statt auf einen leeren Bildschirm — und DORT wird die
   * Markierung geloescht: die Zielansicht ist nicht erreicht worden, und ein
   * liegengebliebener Schluessel liesse die Tour spaeter aus dem Nichts
   * anspringen (die Verfallszeit in `lieseTourUebergabe` ist nur das Netz
   * darunter). Wer die Tour will, oeffnet das Projekt und startet sie erneut.
   *
   * Der Wechsel selbst steht in `wechsleZurKapitelAnsicht`: das Tour-Overlay
   * bietet denselben Weg noch einmal an (`onNavigiere`), wenn es einen Anker
   * nicht findet — und muss dabei genau dieselbe Vorsicht walten lassen.
   */
  const wechsleZurKapitelAnsicht = (ziel: AppView) => {
      if (isProjectView(ziel) && !report) {
          try { window.sessionStorage.removeItem(TOUR_SCHLUESSEL); } catch { /* Privater Modus: nichts zu raeumen */ }
          navigateToView('projects');
          return;
      }
      if (isProjectView(ziel)) { setActiveSection(ziel === 'module-sigeko-inspection' ? 'items' : 'project'); setView(ziel); return; }
      navigateToView(ziel);
  };

  const handleStarteTour = (kapitelId: TutorialKapitelId, ziel: AppView) => {
      // Mit Zeitstempel: erreicht die App die Zielansicht nicht, verfaellt die
      // Uebergabe von selbst (useTour), statt bei irgendeinem spaeteren
      // Ansichtswechsel anzuspringen.
      try { window.sessionStorage.setItem(TOUR_SCHLUESSEL, schreibeTourUebergabe(kapitelId, Date.now())); } catch { /* Privater Modus: ohne Merker, aber mit Navigation */ }
      wechsleZurKapitelAnsicht(ziel);
  };

  /**
   * Sprung in ein Uebungsprojekt (Uebungsplatz, Auftrag A4).
   *
   * Frisch angelegte Uebungsprojekte stehen noch nicht in `projects` — deshalb
   * wird die Liste einmal nachgeladen, bevor aufgegeben wird. Ohne das laefe
   * der erste Klick auf eine gerade erzeugte Vorlage ins Leere, und genau der
   * ist der haeufigste.
   */
  const handleOpenUebungsprojekt = async (projectId: string, ziel: AppView) => {
      let ziel_projekt = projects.find((p) => p.id === projectId);
      if (!ziel_projekt) {
          const frisch = await loadProjectsFromCloud();
          setProjects(frisch);
          ziel_projekt = frisch.find((p) => p.id === projectId);
      }
      if (!ziel_projekt) return;
      const offen = await handleOpenProject(ziel_projekt);
      if (!offen || ziel === 'project-hub') return;
      setActiveSection(ziel === 'module-sigeko-inspection' ? 'items' : 'project');
      setView(ziel);
  };

  /**
   * Endgueltiges Verlassen des Projekts (Zurueck-Knopf in Seitenleiste und
   * ProjektHub). Bisher stand dieser Rumpf zweimal wortgleich als Inline-
   * Closure im JSX; die Sperrfreigabe waere die dritte Kopie gewesen.
   */
  const handleLeaveProject = () => {
      if (!isProjectReadOnly && saveStatus === 'saving' && report) saveProjectToCloud(report, currentProjectId || undefined);
      if (isModuleView) { setView('project-hub'); return; }
      // Erst hinter dem Modul-Rueckweg: aus einem Modul in den ProjektHub zu
      // gehen ist kein Verlassen des Projekts, sondern eine Navigation darin.
      recordProjectClose({ projectId: currentProjectId, reason: 'manual' });
      releaseHeldLock();
      setProjectReadOnly(false);
      setExplicitReadOnly(false);
      setLockBlocked(false);
      resetForeignAccess();
      setProjectLock(null);
      setView(previousView);
      setReport(null);
      setCurrentProjectId(null);
  };

  // ─── Android-App: Umfang und Zurueck-Taste (docs/android-app-plan.md 1.4, P4)
  //
  // Hier unten, hinter den fruehen Returns, und ohne Hooks: beides braucht
  // handleLeaveProject und isModuleView. `nurImBrowser` ist nur in der
  // Handy-App je wahr (Tablet-App und Browser: voller Umfang, Vertrag E11);
  // die Zurueck-Taste gilt in beiden App-Varianten. Im Handy-Browser fuehrt
  // der Verlaufswaechter dieselbe Entscheidung bei der Zurueck-Geste aus.

  /** In der Handy-App ausgeblendete Ansicht → Platzhalter „Nur im Browser verfügbar". */
  const nurImBrowser = !ansichtInAppErlaubt(view, appVariante());

  /**
   * Ansichts-Wache nach Rechten (docs/rollensystem-plan.md §6.1): fehlt das
   * Recht, steht „Kein Zugriff" an der Stelle der Ansicht, und ihre Daten
   * werden gar nicht erst geladen — egal, ob Menue, Link, Mitteilung oder
   * gemerkter Zustand dorthin gefuehrt hat. Dieselbe Regel blendet die
   * Eintraege in Seitenleiste und mobiler Leiste aus (`darfAnsicht`).
   * Kein Hook: nur Ablesungen, sie duerfen hier unten stehen.
   */
  const ansichtsWache = ansichtWache(view, rechte, userProfile?.role, { istSifaFachkraft: istSifa, sifaGeladen: istSifaGeladen });
  const darfAnsicht = (v: AppView) => ansichtErlaubt(v, rechte, userProfile?.role, { istSifaFachkraft: istSifa });
  /** Einstieg „Neues Projekt": nur mit wenigstens einem der drei Anlegen-Rechte. */
  const darfProjektAnlegen = darfIrgendeinProjektAnlegen(rechte.hat);

  /**
   * Umschalter oben in „Karte" und „Auswertungen" (Menue-Umbau 19.10.2026):
   * die Seitenleiste hat je einen Eintrag, die Geschwister-Ansicht waehlt man
   * hier. Ein Eintrag steht nur da, wenn die Ansicht dahinter auch aufginge —
   * dieselbe Regel wie Wache und Seitenleiste (`darfAnsicht`) und der
   * Handy-Umfang; sonst fuehrte ein Knopf auf „Kein Zugriff" oder „Nur im
   * Browser". KPI & Cockpit ist frei in der Rechtetabelle (die AdminView
   * schuetzt sich selbst), deshalb hier ausdruecklich nur fuer Admins.
   * Kein Hook: nur Ablesungen, sie duerfen hier unten stehen.
   */
  const ansichtGehtAuf = (v: AppView) => darfAnsicht(v) && ansichtInAppErlaubt(v, appVariante());
  const umschalterEintraege: UmschalterEintrag[] =
      view === 'global-map' || view === 'crm-map'
          ? [
              { view: 'global-map', label: 'Projekte', icon: <FolderKanban className="w-4 h-4" /> },
              { view: 'crm-map', label: 'Kunden', icon: <Building2 className="w-4 h-4" /> },
          ]
          : view === 'project-management' || view === 'erp-cockpit'
              ? [
                  ...(isAdminRole(userProfile?.role)
                      ? [{ view: 'project-management' as const, label: 'KPI & Cockpit', icon: <BarChart3 className="w-4 h-4" /> }]
                      : []),
                  { view: 'erp-cockpit', label: 'Steuerung', icon: <Gauge className="w-4 h-4" /> },
              ]
              : [];
  const sichtbareUmschalterEintraege = umschalterEintraege.filter(e => ansichtGehtAuf(e.view));
  /**
   * Ansichten, die in der Umschalter-Huelle stehen (Rendern s. u.): die vier
   * mit Umschalter und, der Einfachheit halber, alle Verwaltungsbereiche —
   * die rendern dieselbe AdminView wie „Auswertungen".
   */
  const inUmschalterHuelle = istAdminBereichView(view)
      || view === 'global-map' || view === 'crm-map' || view === 'erp-cockpit';

  /**
   * Der vorhandene Rueckweg („Zurück landet wo man war"): aus einem Modul in
   * den Hub, aus dem Hub dorthin, wo das Projekt geoeffnet wurde
   * (handleLeaveProject → previousView), aus jeder anderen Ansicht aufs
   * Dashboard. Reiter, Filter und Bildlauf stellt das Ansichtsgedaechtnis
   * (viewMemory) beim Wiedereinhaengen selbst her.
   */
  const zurueckNavigieren = () => {
      if (isModuleView || view === 'project-hub') { handleLeaveProject(); return; }
      navigateToView('dashboard');
  };

  /**
   * Android-Zurueck-Taste. Entscheidung in appUmfang.entscheideZurueck —
   * Grundsatz: nie eine Eingabe verwerfen. Liefert false auf der Startseite
   * (dann minimiert AndroidZurueckTaste die App). Im Handy-Browser ruft der
   * Verlaufswaechter sie bei der Zurueck-Geste; false heisst dort: nichts
   * tun, die naechste Geste verlaesst die Seite.
   */
  const handleAndroidZurueck = (): boolean => {
      const schritt = entscheideZurueck(zurueckLage());
      switch (schritt) {
          case 'eingabe-verlassen':
              // Tastatur zu, Feld verlassen: Felder, die beim Verlassen
              // uebernehmen, uebernehmen so. Navigiert wird erst beim naechsten Druck.
              if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
              return true;
          case 'menue-schliessen': setMobileMenuOpen(false); return true;
          case 'projektauswahl-schliessen': setShowMobileProjectSelector(false); schliessePlusBlatt(); return true;
          case 'statuswechsel-abbrechen': setPendingStatusChange(null); return true;
          case 'verlauf-zurueck': window.history.back(); return true;
          case 'registriert': registriertenZurueckAusfuehren(); return true;
          case 'nichts': return true;
          case 'app-seite-schliessen': schliesseAppSeite(); return true;
          case 'zurueck': zurueckNavigieren(); return true;
          case 'minimieren': return false;
      }
  };

  /**
   * Sperre nehmen und das Projekt frisch laden — der gemeinsame Kern von
   * Uebernahme (handleTakeoverLock) und Fremdzugriff (handleForeignSubmit).
   *
   * Das Neuladen ist der eigentliche Zweck — und der einzige Schritt, der
   * scheitern kann, ohne dass es jemand merkt. Fliegt der Fehler ungefangen
   * heraus (offline), bliebe heldLockRef auf dem Projekt stehen, ohne dass je
   * ein Herzschlag laeuft: der Anwesenheits-Effekt liefert im Lesemodus
   * 'idle'. Ergebnis waeren eine verwaiste Sperre und ein unbehandeltes
   * Promise. Deshalb wird im Fehlerfall die eben genommene Sperre sofort
   * wieder hergegeben und der Lesemodus bleibt.
   *
   * @param showLoader Vollbild-Ladeschirm wie bisher bei der Uebernahme. Der
   *   Fremdzugriff laesst ihn weg: der Ladeschirm haengt den ganzen Baum aus,
   *   und der Begehungsdialog, der auf das Ergebnis wartet, waere danach weg.
   *   Dort zeigt der Begruendungsdialog den Fortschritt selbst.
   */
  const takeLockAndReload = async (
      projectId: string,
      take: () => Promise<ProjectLockResult>,
      showLoader: boolean,
  ): Promise<{ outcome: 'ok' | 'not-acquired' | 'reload-failed' | 'abandoned'; fresh: { data?: Report; updated_at?: string | null } | null }> => {
      const res = await take();
      // Waehrend der Anfrage wurde das Projekt gewechselt: das Ergebnis
      // gehoert zu keinem offenen Projekt mehr und darf dessen Zustand
      // (Sperrbalken, Lesemodus) nicht anfassen. Eine eben erhaltene Sperre
      // geht sofort zurueck.
      if (currentProjectIdRef.current !== projectId) {
          if (res.acquired) releaseProjectLock(projectId).catch(() => {});
          return { outcome: 'abandoned', fresh: null };
      }
      if (!res.acquired) {
          // Jemand war schneller — Balken auf den neuen Halter setzen,
          // Lesemodus bleibt.
          setProjectLock(res.lock);
          return { outcome: 'not-acquired', fresh: null };
      }
      heldLockRef.current = projectId;
      setProjectLock(res.lock);

      if (showLoader) setLoading(true);
      let fresh: any = null;
      try {
          fresh = await loadProjectById(projectId);
      } catch (e) {
          console.error('Uebernahme: Projekt konnte nicht neu geladen werden', e);
      } finally {
          if (showLoader) setLoading(false);
      }
      // Ohne Ladeschirm laesst sich waehrend des Ladens das Projekt wechseln.
      // Der frische Stand gehoert dann zu keinem offenen Projekt mehr, und die
      // eben genommene Sperre muss sofort wieder weg.
      if (currentProjectIdRef.current !== projectId) {
          if (heldLockRef.current === projectId) releaseHeldLock();
          return { outcome: 'abandoned', fresh: null };
      }
      // Auch eine Zeile OHNE `data` ist ein Fehlschlag. Den Lesemodus
      // trotzdem abzulegen hiesse, auf dem alten Stand weiterzuarbeiten —
      // also genau der Datenverlust, den die Uebernahme verhindern soll.
      if (!fresh?.data) {
          releaseHeldLock();
          setProjectLock(await fetchProjectLock(projectId));
          return { outcome: 'reload-failed', fresh: null };
      }
      setReport(withProfileSettings(fresh.data as Report));
      // Erst nach dem frischen Stand freigeben: sonst tippt jemand in der
      // Ladezeit in den alten Bericht und der naechste Autosave schickt ihn ab.
      setLockBlocked(false);
      setForeignPending(false);
      setProjectReadOnly(false);
      return { outcome: 'ok', fresh };
  };

  /**
   * Bearbeitung an sich ziehen.
   *
   * Zwei Wege hinter einem Knopf, die Unterscheidung trifft canTakeOverLock:
   *   * 'acquire' — die Sperre ist abgelaufen oder freigegeben. Jeder
   *     Berechtigte darf, ganz ohne Rueckfrage; der Server vergibt sie ohnehin.
   *   * 'force'   — lebende fremde Sperre, nur Administratoren, mit Rueckfrage
   *     unter Nennung des Halters.
   *
   * Danach wird das Projekt NEU GELADEN, und das ist der wichtigste Teil: es
   * gibt keinen Konflikt-Anker mehr. Wer auf dem Stand weiterarbeitete, den er
   * beim Zuschauen im Speicher hatte, wuerde beim ersten Speichern alles
   * ueberschreiben, was der Vorgaenger in der Zwischenzeit geschrieben hat.
   */
  const handleTakeoverLock = async () => {
      if (!currentProjectId || lockBusy) return;
      // Ein fremdes Projekt ohne gueltige Freigabe wird nie ueber die
      // Uebernahme beschreibbar: die Sperr-RPC antwortet dort mit 42501, und
      // der Sperrdienst liesse das fail-open durch. Stattdessen zurueck in den
      // Fremdmodus und die Begruendung anbieten.
      // Auch mit dem Recht „Alle Projekte sehen": fehlt die Freigabe (RPC
      // fehlte beim Oeffnen), bleibt nur der Weg ueber die Begruendung.
      const offenesProjekt = projects.find((p) => p.id === currentProjectId);
      if (offenesProjekt && isForeignProjectFor(offenesProjekt, userProfile)
          && !isForeignGrantActive(foreignGrant, Date.now())) {
          setLockBlocked(false);
          setProjectReadOnly(true);
          setForeignPending(true);
          void requestJustification();
          return;
      }
      const kind = canTakeOverLock(projectLock, lockUserId, userProfile?.role, Date.now());
      if (kind === 'none') return;
      if (kind === 'force' && !window.confirm(lockTakeoverConfirm(lockHolderName(projectLock)))) return;

      const projectId = currentProjectId;
      setLockBusy(true);
      try {
          const { outcome } = await takeLockAndReload(
              projectId,
              () => kind === 'force'
                  ? takeoverProjectLock(projectId, lockLabel)
                  : acquireProjectLock(projectId, lockLabel),
              true,
          );
          if (outcome === 'not-acquired') alert(LOCK_UEBERNAHME_FEHLGESCHLAGEN);
          else if (outcome === 'reload-failed') alert(LOCK_UEBERNAHME_OHNE_NEULADEN);
      } finally {
          setLockBusy(false);
      }
  };

  /**
   * Begruendung abschicken: Freigabe, dann Sperre, dann frisch laden.
   *
   * Reihenfolge ist Absicht. Erst die Freigabe, denn ohne sie laesst die
   * Datenbank weder Sperre noch Journalzeile zu. Dann die Sperre wie bei der
   * Uebernahme — haelt sie gerade jemand anderes, landet das Projekt im
   * gewoehnlichen Sperr-Lesemodus mit dessen Balken (die Begruendung gilt
   * trotzdem, spaeter genuegt „Bearbeitung übernehmen"). Zuletzt das
   * Neuladen: angesehen wurde ein Stand, der inzwischen alt sein kann.
   *
   * Scheitert die Freigabe, bleibt der Dialog mit der Servermeldung offen.
   * Scheitert nur das Neuladen, ebenfalls — ein zweiter Versuch kostet keine
   * zweite Mitteilung, die RPC gibt die Freigabe von heute zurueck.
   */
  const handleForeignSubmit = async (reason: string) => {
      const projectId = currentProjectIdRef.current;
      if (!projectId || foreignBusy) return;
      setForeignBusy(true);
      setForeignError(null);
      let grant: ForeignAccessGrant;
      try {
          grant = await grantForeignAccess(projectId, reason);
      } catch (e) {
          if (currentProjectIdRef.current !== projectId) { settleForeignRequest(false); return; }
          setForeignError(classifyForeignAccessError(e).message);
          setForeignBusy(false);
          return;
      }
      if (currentProjectIdRef.current !== projectId) { settleForeignRequest(false); return; }
      // Gilt ab jetzt, auch wenn gleich die Sperre bei jemand anderem liegt —
      // eine spaetere Uebernahme schreibt ebenfalls aus dieser Freigabe heraus.
      setForeignGrant(grant);

      const { outcome, fresh } = await takeLockAndReload(
          projectId,
          () => acquireProjectLock(projectId, lockLabel),
          false,
      );
      if (outcome === 'abandoned') { settleForeignRequest(false); return; }
      if (outcome === 'reload-failed') {
          setForeignError(FOREIGN_OHNE_NEULADEN);
          setForeignBusy(false);
          return;
      }
      if (outcome === 'not-acquired') {
          // Begruendet, aber ein anderer arbeitet gerade drin: ab hier ist es
          // der gewoehnliche Sperrfall. Der Balken nennt den Halter und bietet
          // die Uebernahme an, sobald er fertig ist.
          setLockBlocked(true);
          setForeignPending(false);
          settleForeignRequest(false);
          return;
      }
      // Der Ausgangsstand fuer die Warteschlange ist ab jetzt der frisch
      // geladene Serverstand — wie beim Oeffnen und beim Rueckweg.
      if (fresh?.data) {
          projectBaseRef.current = { projectId, report: fresh.data, updatedAt: fresh.updated_at ?? null };
      }
      setForeignReloadKey((k) => k + 1);
      settleForeignRequest(true);
  };

  /** „Nur ansehen" — waehrend der Server arbeitet, gibt es kein Zurueck. */
  const handleForeignCancel = () => {
      if (foreignBusy) return;
      settleForeignRequest(false);
  };

  /**
   * Der gelbe Balken fuer das offene Projekt. Als Funktion, weil das
   * Pruefmodul einen eigenen Wortlaut braucht (dort sind zusaetzlich Versand
   * und Abrechnung gesperrt) — und bewusst NICHT gemerkt: die Sperre laeuft
   * mit der Uhr ab, ein useMemo mit `Date.now()` in der Rechnung und nicht in
   * den Abhaengigkeiten haette den Balken eingefroren.
   */
  const buildProjectNotice = (readOnlyText: string) => describeProjectNotice({
      explicitReadOnly,
      foreignPending,
      lockBlocked,
      lock: projectLock,
      currentUserId: lockUserId,
      role: userProfile?.role,
      nowMs: Date.now(),
      readOnlyText,
  });

  /**
   * Das Kapitel, an dem die laufende Tour hängt — und ob es woanders zuhause
   * ist als wir gerade. Nur dann bekommt das Overlay einen Navigationsweg:
   * ein „Dorthin wechseln" auf die Ansicht, in der man ohnehin schon steht,
   * wäre ein Knopf ohne Wirkung.
   */
  const tourKapitelView = tour.aktiveTourId ? kapitelNachId(tour.aktiveTourId)?.view : undefined;

  /**
   * Der Hinweis, der gerade im Balken steht — immer der älteste noch offene.
   * Kein Hook, nur eine Ablesung: sie darf hier unten stehen.
   */
  const ersterQueueHinweis = queueBlockedNotices[0];

  /** Das geladene Projekt — im „+"-Blatt als „geöffnet" markiert. Nur eine Ablesung. */
  const plusOffenesProjektId = report ? currentProjectId : null;

  return (
    <LernzentrumOeffnerContext.Provider value={handleOpenLernzentrum}>
    <ForeignAccessContext.Provider value={foreignAccessContext}>
    <div className="app-shell flex h-screen overflow-hidden">
        <Sidebar view={view} setView={navigateToView} userProfile={userProfile} appMode={userProfile?.app_mode || 'sigeko'} onLogout={handleLogout} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onOpenProfile={() => setIsProfileOpen(true)} onOpenHolidayPlanner={() => { setHolidayRange({}); setIsHolidayPlannerOpen(true); }} activeSection={isModuleView ? activeSection : undefined} onSectionChange={setActiveSection} saveStatus={displayedSaveStatus} currentProjectName={report?.metadata?.projectName} incomingTransfersCount={incomingTransfers.length} leadsDueCount={leadsDueCount} onBackToDashboard={handleLeaveProject} onOpenAppSeite={oeffneAppSeite} appSeiteAktiv={appSeiteOffen} istSifa={istSifa} materialOffenCount={materialOffen.anzahl} talentpoolNeuCount={talentpoolNeu.anzahl} />
        {/* data-glass-window: das Fenster der Optik „Glas" (src/design/glass/base.css) — ohne den Modus wirkungslos. */}
        <main data-glass-window className="flex-1 flex flex-col overflow-hidden relative w-full pb-16 md:pb-0">
            {bauphase.state.phase !== 'none' && <BauphaseBanner state={bauphase.state} adminInside={bauphase.adminEntered} onEnd={() => { void bauphase.endNow(); }} />}
            {/*
              * Android-App (Vertrag E6): die Leisten stehen auf App-Ebene, oben
              * im Fluss von <main> wie der Bauphase-Balken — nie unten, dort
              * liegt die mobile Leiste. AppUpdateBanner rendert im Browser
              * nichts, AppDownloadHinweis nur im Android-Browser.
              */}
            <AppUpdateBanner />
            <AppDownloadHinweis onOpen={oeffneAppSeite} />
            <AndroidZurueckTaste onZurueck={handleAndroidZurueck} zurueckRef={oberflaechenZurueckRef} />
            
            {/* MOBILE PROJECT SELECTOR MODAL */}
            {showMobileProjectSelector && (
                <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
                    <div className="glass w-full max-w-md rounded-t-3xl sm:rounded-3xl h-[80vh] flex flex-col animate-slide-up shadow-2xl overflow-hidden border border-white/50">
                         <div className="p-4 border-b border-white/30 flex justify-between items-center bg-white/40">
                             <div>
                                 <h3 className="font-bold text-lg text-slate-800">Projekt wählen</h3>
                                 <p className="text-xs text-slate-600">Bericht starten für...</p>
                             </div>
                             <button onClick={() => setShowMobileProjectSelector(false)} className="p-2 bg-white/50 rounded-full text-slate-500 border border-white/60 hover:bg-white/80 transition-all"><X className="w-5 h-5"/></button>
                         </div>
                         {/* Vom „+" hierher zurueckgefallen: warum (kein Standort, nichts in der Naehe). */}
                         {plusHinweis && (
                             <div role="status" className="px-4 py-2.5 border-b border-white/30 bg-amber-50 text-amber-900 text-xs font-medium flex items-center gap-2">
                                 <MapPin className="w-3.5 h-3.5 shrink-0" />
                                 <span>{plusHinweis}</span>
                             </div>
                         )}
                         <div className="p-3 border-b border-white/30">
                             <div className="relative">
                                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                 <input 
                                    type="text" 
                                    placeholder="Suchen..." 
                                    value={mobileSearchTerm}
                                    onChange={(e) => setMobileSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 border border-white/60 rounded-xl bg-white/50 focus:ring-2 focus:ring-blue-500/50 outline-none backdrop-blur-sm transition-all"
                                 />
                             </div>
                         </div>
                         <div className="flex-1 overflow-y-auto p-3 space-y-2">
                             {filteredMobileProjects.length === 0 ? (
                                 <div className="text-center py-10 text-slate-500 italic">Keine Projekte gefunden</div>
                             ) : (
                                 filteredMobileProjects.map(p => (
                                     <div 
                                        key={p.id} 
                                        onClick={() => waehleAusProjektauswahl(p)}
                                        className="glass-panel p-4 rounded-2xl hover:bg-white/90 cursor-pointer active:scale-95 transition-all flex items-center justify-between"
                                     >
                                         <div>
                                             <h4 className="font-bold text-slate-800 text-sm">{p.name}</h4>
                                             <div className="flex items-center gap-2 mt-1 text-xs text-slate-600">
                                                 <MapPin className="w-3 h-3" />
                                                 <span className="truncate max-w-[200px]">{p.data.metadata?.projectAddress || 'Keine Adresse'}</span>
                                             </div>
                                         </div>
                                         <div className="flex items-center gap-2 shrink-0">
                                             <ProjectLockHint lock={mobileSelectorLocks.get(p.id)} currentUserId={lockUserId} nowMs={mobileSelectorLockNow} compact />
                                             <ArrowRight className="w-5 h-5 text-slate-400" />
                                         </div>
                                     </div>
                                 ))
                             )}
                         </div>
                    </div>
                </div>
            )}

            {/*
              * „+"-Blatt (services/plusStandort): erst „Standort wird gesucht …",
              * dann ein Treffer zum Bestaetigen oder mehrere zur Wahl. Kein
              * Treffer und kein Standort fuehren in die Projektauswahl darueber.
              * Die Zeilen wie „Jetzt · in der Nähe" im mobilen Dashboard.
              */}
            {plusBlatt && (
                <div className="fixed inset-0 z-[200] bg-black/40 flex items-end justify-center animate-fade-in">
                    <div role="dialog" aria-modal="true" aria-labelledby="plus-blatt-titel" className="w-full max-w-md max-h-[85vh] flex flex-col bg-white rounded-t-3xl border border-slate-200 shadow-2xl animate-slide-up">
                        <div className="pl-5 pr-2 py-2 border-b border-slate-100 flex items-center justify-between gap-2">
                            <h3 id="plus-blatt-titel" className="font-bold text-lg text-slate-800">Neu erfassen</h3>
                            <button type="button" onClick={schliessePlusBlatt} aria-label="Schließen" className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 active:bg-slate-100 transition-colors"><X className="w-5 h-5" /></button>
                        </div>
                        {plusBlatt.art === 'suche' && (
                            <div role="status" aria-live="polite" className="px-5 py-6 flex items-center gap-3 text-slate-700">
                                <Loader2 className="w-5 h-5 shrink-0 animate-spin text-blue-600" aria-hidden="true" />
                                <span className="text-sm font-medium">Standort wird gesucht …</span>
                            </div>
                        )}
                        {plusBlatt.art === 'bestaetigen' && (
                            <div className="px-5 pt-4">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> In der Nähe · {plusEntfernung(plusBlatt.treffer.km)}{plusBlatt.treffer.projekt.id === plusOffenesProjektId ? ' · geöffnet' : ''}
                                    <ProjectLockHint lock={mobileSelectorLocks.get(plusBlatt.treffer.projekt.id)} currentUserId={lockUserId} nowMs={mobileSelectorLockNow} compact className="ml-1" />
                                </p>
                                <p className="mt-1 text-2xl font-bold text-slate-900 break-words">{plusBlatt.treffer.name}</p>
                                {plusBlatt.treffer.adresse && <p className="mt-1 text-sm text-slate-600 break-words">{plusBlatt.treffer.adresse}</p>}
                                <button type="button" onClick={() => { void oeffnePlusTreffer(plusBlatt.treffer.projekt); }} disabled={plusOeffnet !== null} className="mt-5 w-full min-h-[48px] rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                                    {plusOeffnet === plusBlatt.treffer.projekt.id
                                        ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Wird geöffnet …</>
                                        : <>{plusBlatt.treffer.projekt.id === plusOffenesProjektId ? 'Zum Projekt' : 'Öffnen'} <ArrowRight className="w-4 h-4" /></>}
                                </button>
                            </div>
                        )}
                        {plusBlatt.art === 'auswaehlen' && (<>
                            <p className="px-5 pt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> In der Nähe · welches Projekt?
                            </p>
                            <ul className="flex-1 min-h-0 overflow-y-auto px-4 pt-2 space-y-2">
                                {plusBlatt.treffer.map(t => (
                                    <li key={t.projekt.id}>
                                        <button type="button" onClick={() => { void oeffnePlusTreffer(t.projekt); }} disabled={plusOeffnet !== null} className="w-full min-h-[56px] px-3.5 py-2.5 bg-white rounded-xl border-l-4 border border-slate-200 border-l-blue-600 flex items-center justify-between gap-3 active:bg-slate-50 disabled:opacity-60 text-left">
                                            <span className="min-w-0">
                                                <span className="block text-sm font-semibold text-slate-800 truncate">{t.name}</span>
                                                <span className="block text-[11px] text-slate-500 truncate">
                                                    {plusOeffnet === t.projekt.id
                                                        ? 'Wird geöffnet …'
                                                        : `${t.projekt.id === plusOffenesProjektId ? 'Geöffnet · ' : ''}${plusEntfernung(t.km)}${t.adresse ? ` · ${t.adresse}` : ''}`}
                                                </span>
                                            </span>
                                            <span className="flex items-center gap-2 shrink-0">
                                                <ProjectLockHint lock={mobileSelectorLocks.get(t.projekt.id)} currentUserId={lockUserId} nowMs={mobileSelectorLockNow} compact />
                                                {plusOeffnet === t.projekt.id
                                                    ? <Loader2 className="w-5 h-5 animate-spin text-blue-600" aria-hidden="true" />
                                                    : <ArrowRight className="w-5 h-5 text-slate-400" />}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>)}
                        {/* Offene Maske: nicht wechseln (plusOeffnenSchritt 'warten'). */}
                        {plusBlattHinweis && (
                            <p role="status" className="mx-5 mt-3 px-3 py-2.5 rounded-xl bg-amber-50 text-amber-900 text-xs font-medium">{plusBlattHinweis}</p>
                        )}
                        <div className="px-5 pt-3 pb-safe">
                            <button type="button" onClick={plusZurProjektauswahl} disabled={plusOeffnet !== null} className="w-full min-h-[48px] rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-60 text-sm font-semibold transition-colors">
                                {plusBlatt.art === 'suche' ? 'Projekt selbst wählen' : 'Anderes Projekt'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {importPreview.isOpen && <ImportPreviewModal isOpen={importPreview.isOpen} importData={importPreview.data!} existingProject={importPreview.existingProject} onConfirm={handleImportConfirm} onClose={() => setImportPreview({ isOpen: false, data: null, existingProject: null })} />}
            {incomingTransfers.length > 0 && <ProjectTransferModal transfers={incomingTransfers} senderNames={senderNames} onRespond={handleTransferResponse} processingId={processingTransferId} />}
            {/*
              * Projekt-Kickoff (docs/projekt-kickoff-plan.md §4.3): Quittung
              * fuer neu zugewiesene Projekte. Eigene Komponente mit eigenen
              * Hooks — App.tsx bekommt dafuer KEINEN Hook (TDZ-Bootcrash vom
              * 13.09.). Freelancer und Bauphase kommen hier nie an (fruehe
              * Returns oben); die Rolle geht trotzdem mit.
              */}
            <ProjectKickoffGate userId={userProfile?.id} role={userProfile?.role} />

            {pendingStatusChange && (
                <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-200">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative border border-slate-200">
                        <div className="bg-amber-500 p-6 flex justify-between items-start text-white"><div className="flex gap-3"><div className="bg-white/20 p-2 rounded-xl animate-pulse"><AlertTriangle className="w-6 h-6 text-white" /></div><div><h3 className="font-bold text-lg">Projektstatus ändern?</h3><p className="text-amber-100 text-xs font-medium mt-1">Ausführungs-Phase startet</p></div></div><button onClick={() => setPendingStatusChange(null)} className="hover:bg-white/10 p-1 rounded transition-colors"><X className="w-5 h-5"/></button></div>
                        <div className="p-6"><p className="text-slate-600 text-sm leading-relaxed mb-4">Sie öffnen den Begehungsbereich. Das Projekt wechselt von <span className="font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{pendingStatusChange.currentStatus === 'planning' ? 'Planung' : 'Ruhend'}</span> in die <span className="font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Ausführungsphase</span>.</p><p className="text-xs text-slate-400 italic">Der Status wird automatisch aktualisiert.</p></div>
                        <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3"><Button variant="ghost" onClick={() => setPendingStatusChange(null)}>Abbrechen</Button><Button variant="primary" onClick={handleConfirmStatusChange} className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200"><Play className="w-4 h-4 mr-2 fill-current" /> Bestätigen & Starten</Button></div>
                    </div>
                </div>
            )}
            {/*
              * Ein Balken fuer alle Projektansichten.
              *
              * Frueher hing er nur an Vorankuendigung und Unterlage (die haben
              * keinen eigenen Lesemodus, ihre Eingaben verpuffen still in
              * handleUpdateReport). Der Sperrhinweis muss aber ueberall stehen,
              * wo ein Projekt offen ist — auch im ProjektHub, wo man nach dem
              * Oeffnen zuerst landet, und im SiGe-Plan und im Archiv.
              *
              * Einzige Ausnahme ist das Pruefmodul: es baut seinen eigenen
              * Kopfbereich und setzt den Balken dort selbst unter die
              * Werkzeugleiste (gleiche Komponente, eigener Wortlaut).
              *
              * Er steht VOR jeder Ansicht, und das ist keine Kosmetik: <main>
              * ist eine Flex-Spalte mit overflow-hidden, der ProjektHub darin
              * traegt `h-full`. Stand der Balken hinter ihm, rutschte er an den
              * unteren Bildrand — ausgerechnet in der Ansicht, in der man nach
              * dem Oeffnen zuerst landet. Wer im Lesemodus keinen Hinweis
              * sieht, tippt ins Leere; das ist der schlimmste aller Faelle.
              */}
            {/*
              * Android-App: die Download-Seite `#/app` (auch im Browser) und
              * der Platzhalter fuer ausgeblendete Ansichten stehen an der Stelle
              * der Ansichten — egal, ob Menue, Link, Mitteilung oder gemerkter
              * Zustand dorthin gefuehrt hat. Im Browser ohne `#/app` greift
              * nur der letzte Zweig, und darin steht alles wie bisher.
              */}
            {appSeiteOffen && <AppDownloadView onBack={schliesseAppSeite} />}
            {!appSeiteOffen && nurImBrowser && <NurImBrowser onBack={zurueckNavigieren} />}
            {!appSeiteOffen && !nurImBrowser && ansichtsWache !== 'frei' && (
                <KeinZugriff text={keinZugriffText(view)} laedt={ansichtsWache === 'laedt'} onDashboard={() => navigateToView('dashboard')} />
            )}
            {!appSeiteOffen && !nurImBrowser && ansichtsWache === 'frei' && (<>
            {report && isProjectView(view) && view !== 'module-sigeko-inspection' && (
                <ProjectNoticeBanner notice={buildProjectNotice(READ_ONLY_BANNER)} onTakeover={handleTakeoverLock} onJustify={() => { void requestJustification(); }} busy={lockBusy} />
            )}
            {view === 'dashboard' && <DashboardView projects={projects} userProfile={userProfile} onOpenProject={handleOpenProject} onRefresh={() => fetchProjects()} onPostpone={handlePostpone} onSkip={handleSkip} onNavigate={navigateToView} />}
            {view === 'projects' && <ProjectOverview projects={projects} userProfile={userProfile} onOpenProject={handleOpenProject} onRefresh={() => fetchProjects()} onPostpone={handlePostpone} onSkip={handleSkip} />}
            {/*
              * Umschalter-Huelle (Menue-Umbau 19.10.2026): „Karte" (Projekte/
              * Kunden) und „Auswertungen" (KPI & Cockpit/Steuerung) haben oben
              * einen Umschalter. Die Ansichten stehen dafuer zusammen in EINER
              * Huelle, damit der Umschalter beim Wechsel stehen bleibt — sonst
              * hinge er mit jeder Ansicht neu ein, und wer per Tastatur
              * wechselt, verloere den Fokus.
              *
              * Die Huelle steht auch ohne Umschalter (unter zwei Eintraegen
              * rendert er nichts, also kein leerer Rand): kommen Profil oder
              * Rechte erst nach dem ersten Rendern, wechselte sonst die
              * Baumform, und die Karte baute sich neu auf. Aus demselben Grund
              * stehen ALLE Verwaltungsbereiche (adminBereiche.ts) darin, nicht
              * nur „Auswertungen": dieselbe AdminView, die nur die Reiter ihres
              * Bereichs zeigt, bleibt beim Wechsel zwischen den Bereichen
              * eingehaengt wie bisher.
              *
              * Die Ansichten tragen `h-full`; `flex-1 min-h-0` gibt ihnen die
              * Resthoehe unter der Leiste, AdminView und Steuerung scrollen
              * darin wie bisher in <main>.
              */}
            {inUmschalterHuelle && (
              <div className="h-full flex flex-col min-h-0">
                <BereichsUmschalter eintraege={sichtbareUmschalterEintraege} aktiv={view} onWechsel={navigateToView} />
                <div className="flex-1 min-h-0">
            {istAdminBereichView(view) && (
                <AdminView
                    view={view}
                    initialTab={lastAdminTab as any}
                    onTabChange={(t: string) => setLastAdminTab(t)}

                    onCreateProject={darfProjektAnlegen ? () => setShowSetupWizard(true) : undefined}
                    onSaveStatusChange={(s: SaveStatus) => {
                        handleManualSaveStatusUpdate(s);
                        // Einstellungen wirken sofort in allen Ansichten: das Profil
                        // traegt settings (Briefkopf, Rechnungs-/Angebotskonfiguration),
                        // und der Realtime-Kanal auf profiles ist als einziger
                        // Aktualisierungsweg nicht verlaesslich (Publication).
                        if (s === 'saved' && userProfile) void fetchProfile(userProfile.id);
                    }}
                    onRefresh={() => fetchProjects()}
                    projectsProp={projects}
                    onOpenProject={handleOpenProject}
                    onOeffneLernzentrum={handleOpenLernzentrum}
                />
            )}
            {view === 'crm-map' && <CrmMapView onOpenCustomer={(id) => { setCrmFocusCustomerId(id); setView('crm'); }} onOpenProject={(id) => { const p = projects.find(pr => pr.id === id); if (p) handleOpenProject(p); }} />}
            {view === 'erp-cockpit' && <ErpCockpit onOpenProject={(id) => { const p = projects.find(pr => pr.id === id); if (p) handleOpenProject(p); }} />}
            {view === 'global-map' && <GlobalMapDashboard projects={projects} userProfile={userProfile} onOpenProject={handleOpenProject} initialSearchTerm="" onRefresh={() => fetchProjects()} />}
                </div>
              </div>
            )}
            {view === 'crm' && <CrmView quoteConfig={quoteConfigMemo} initialCustomerId={crmFocusCustomerId || undefined} defaultPaymentTermsDays={invoiceConfigMemo.defaultPaymentTermsDays} datevConfig={invoiceConfigMemo.datev} onOpenQuote={(id) => { setQuoteFocusId(id); navigateToView('quotes'); }} onOpenInvoice={(id) => { setInvoiceFocusId(id); navigateToView('invoices'); }} onOpenProject={handleOpenProject} />}
            {view === 'leads' && <div className="h-full flex flex-col min-h-0"><div className="flex-1 min-h-0"><LeadsView onDueCountChange={setLeadsDueCount} /></div></div>}
            {view === 'freelancers' && <FreelancerView onOpenProject={(id) => { const p = projects.find(pr => pr.id === id); if (p) handleOpenProject(p); }} company={invoiceCompany} invoiceConfig={invoiceConfigMemo} quoteConfig={quoteConfigMemo} userName={[userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ')} />}
            {view === 'quotes' && <QuotesView quoteConfig={quoteConfigMemo} invoiceConfig={invoiceConfigMemo} company={{ name: (userProfile as any)?.company_name, address: (userProfile as any)?.company_address, logo: (userProfile as any)?.company_logo, footerText: resolveFooterText((userProfile as any)?.settings?.footerText, (userProfile as any)?.company_address), senderLine: (userProfile as any)?.settings?.senderLine }} userProfile={userProfile} settings={{ companyName: userProfile?.company_name, companyAddress: userProfile?.company_address, companyLogo: userProfile?.company_logo, appMode: userProfile?.app_mode || 'sigeko', emailSignatureTemplate: userProfile?.settings?.emailSignatureTemplate || '', footerText: (userProfile as any)?.settings?.footerText }} onOpenProject={handleOpenProject} initialQuoteId={quoteFocusId || undefined} onCloseInitialQuote={() => navigateToView('crm')} />}
            {view === 'invoices' && <InvoicesView invoiceConfig={invoiceConfigMemo} company={invoiceCompany} quoteConfig={quoteConfigMemo} userProfile={userProfile} onOpenProject={handleOpenProject} initialAccountingProjectId={accountingFocusId || undefined} onOeffneLernzentrum={handleOpenLernzentrum} initialInvoiceId={invoiceFocusId || undefined} onCloseInitialInvoice={() => navigateToView('crm')} />}
            {view === 'sifa-vorlagen' && <VorlagenView isAdmin={isAdminRole(userProfile?.role)} istSifa={istSifa} isMobile={istMobil} bueroName={userProfile?.company_name} />}
            {/* Materialverwaltung: hinter der Wache oben (Recht beschaffen ODER
                verwalten); welche Reiter und Knoepfe, entscheiden die beiden
                Rechte — der Admin hat beide. Die Huelle scrollt, <main> nicht. */}
            {view === 'material' && (
              <div className="h-full overflow-y-auto">
                <MaterialBereich darfVerwalten={rechte.hat('material.verwalten')} darfBeschaffen={rechte.hat('material.beschaffen')} onOffeneGeaendert={materialOffen.aktualisiere} />
              </div>
            )}
            {/* Talentpool: hinter der Wache oben (Recht talentpool.verwalten).
                Am Schreibtisch fuellen Liste und Karte die Hoehe und scrollen
                je fuer sich; schmal scrollt die Huelle. */}
            {view === 'talentpool' && (
              <div className="h-full overflow-y-auto lg:overflow-hidden">
                <TalentpoolBereich onNeuGeaendert={talentpoolNeu.aktualisiere} />
              </div>
            )}
            {view === 'tasks' && <TasksView projects={projects} userProfile={userProfile} onOpenProject={handleOpenProject} onRefresh={() => fetchProjects()} />}
            {view === 'lernzentrum' && <LernzentrumView userProfile={userProfile} onStarteTour={handleStarteTour} onOeffneProjekt={handleOpenUebungsprojekt} initialKapitelId={lernzentrumKapitelId || undefined} onDazuFragen={id => setHilfeChatKapitel(id)} />}
            {view === 'project-hub' && report && <ProjectHub key={currentProjectId || 'hub'} report={report} onUpdate={handleUpdateReport} onCreateVersion={handleCreateNewVersion} onNavigate={handleModuleNavigation} onBack={handleLeaveProject} userRole={userProfile?.role} onOpenAccounting={() => { if (currentProjectId) { setAccountingFocusId(currentProjectId); navigateToView('invoices'); } }} saveStatus={saveStatus} currentUser={userProfile} onOpenProject={handleOpenProject} onOpenQuote={(id) => { setQuoteFocusId(id); navigateToView('quotes'); }} isReadOnly={isProjectReadOnly} projectId={currentProjectId || undefined} />}
            {/*
              * Die Modulansichten haengen am Neulade-Schluessel des
              * Fremdzugriffs: nach einer Begruendung wird das Projekt frisch
              * geladen, und Module mit eigenem Berichtsstand (Pruefmodul)
              * muessen dann neu aufsetzen, statt mit dem Stand vom Ansehen
              * weiterzuschreiben. Der ProjektHub steht bewusst davor und
              * bleibt stehen — dort wartet der Begehungsdialog auf die
              * Begruendung.
              */}
            <React.Fragment key={`module-${foreignReloadKey}`}>
            {view === 'module-sigeko-inspection' && currentProjectId && report && <SiGeKoInspectionModule project={{ ...projects.find(p => p.id === currentProjectId), data: report }} userProfile={userProfile} onBack={() => setView('project-hub')} activeSection={activeSection} onSectionChange={setActiveSection} onUpdate={handleUpdateReport} saveStatus={displayedSaveStatus} isReadOnly={isProjectReadOnly} notice={buildProjectNotice(READ_ONLY_BANNER_MODUL)} onTakeover={handleTakeoverLock} onJustify={() => { void requestJustification(); }} takeoverBusy={lockBusy} onSpaetAendern={(aendern) => currentProjectId ? spaetAendern(currentProjectId, aendern) : false} />}
            {/* isReadOnly: der Behoerden-Reiter schreibt in die GLOBALE Behoerdentabelle
              * und ist damit die einzige Stelle hier, an der der gelbe Balken sonst
              * luegen wuerde ("Eingaben werden nicht gespeichert"). */}
            {view === 'module-vorank' && report && <VorankuendigungView report={report} onUpdate={handleUpdateReport} onBack={() => setView('project-hub')} activeSection={activeSection} onSectionChange={setActiveSection} isReadOnly={isProjectReadOnly} userProfile={userProfile} />}
            {view === 'module-unterlage' && report && <UnterlageView report={report} onUpdate={handleUpdateReport} onBack={() => setView('project-hub')} activeSection={activeSection} onSectionChange={setActiveSection} userProfile={userProfile} isReadOnly={isProjectReadOnly} />}
            {/* SiGe-Plan, Gewerke-Ueberschneidung und Handbuch stehen unter
              * einer Huelle mit Reitern. Alle drei Ansichten fuehren hierher —
              * `startReiter` entscheidet, welcher Reiter offen ist. Die Huelle
              * bleibt beim Reiterwechsel gemountet (dieselbe Stelle im Baum),
              * `onReiterChange` haelt nur `view` nach, damit Seitenleiste und
              * gemerkte Ansicht dasselbe sagen wie der Bildschirm.
              * onNavigate geht bewusst auf executeModuleNavigation und NICHT
              * auf handleModuleNavigation: dessen Statuswechsel-Schleuse gilt
              * fuer editor/module-sigeko-inspection/project-map und wuerde beim
              * Sprung aus einem Handbuchkapitel den Dialog "Projektstatus
              * ändern?" aufwerfen, den dort niemand bestellt hat. */}
            {sigePlanReiter && currentProjectId && report && <SigePlanModul projectId={currentProjectId} report={report} userProfile={userProfile} company={invoiceCompany} quoteConfig={quoteConfigMemo} invoiceConfig={invoiceConfigMemo} startReiter={sigePlanReiter} onReiterChange={(r) => setView(SIGE_PLAN_VIEW[r])} onBack={() => setView('project-hub')} onUpdate={handleUpdateReport} onNavigate={executeModuleNavigation} isReadOnly={isProjectReadOnly} />}
            {/* Die Einweisung braucht den Firmen-Briefkopf: der Generator setzt
              * Briefpapier, Kopf- und Fusszeile aus buildCompanyForPdf(company,
              * quoteConfig, invoiceConfig). Deshalb gehen alle drei Quellen
              * durch — ohne sie stuende das unterschriebene Blatt ohne Kopf da. */}
            {view === 'module-sige-einweisung' && currentProjectId && report && <EinweisungModul project={{ id: currentProjectId, data: report, name: report.metadata.projectName }} userProfile={userProfile} company={invoiceCompany} quoteConfig={quoteConfigMemo} invoiceConfig={invoiceConfigMemo} onBack={() => setView('project-hub')} onUpdate={handleUpdateReport} isReadOnly={isProjectReadOnly} />}
            {/* SiFa: Unterweisungen (Kurse, Kundenlink, Teilnahmen, Abrechnung)
              * und Mitarbeiterstamm. onUpdate nur bei den Unterweisungen — dort
              * wird nach markiereGebucht die Leistung ins Projekt gutgeschrieben;
              * der Mitarbeiterstamm schreibt ausschliesslich in sifa_mitarbeiter. */}
            {view === VIEW_SIFA_UNTERWEISUNG && currentProjectId && report && <UnterweisungModul project={{ id: currentProjectId, data: report, name: report.metadata.projectName }} userProfile={userProfile} onBack={() => setView('project-hub')} onUpdate={handleUpdateReport} isReadOnly={isProjectReadOnly} />}
            {view === VIEW_SIFA_MITARBEITER && currentProjectId && report && <MitarbeiterModul project={{ id: currentProjectId, data: report, name: report.metadata.projectName }} onBack={() => setView('project-hub')} isReadOnly={isProjectReadOnly} />}
            {/* Nr. 92: Fristenregister und Taetigkeitsnachweis schreiben nur in
              * sifa_fristen / sifa_taetigkeiten — kein onUpdate. Der Nachweis
              * braucht den Nutzernamen („durchgeführt von") und den Firmenkopf
              * fuers PDF. */}
            {view === VIEW_SIFA_FRISTEN && currentProjectId && report && <FristenModul project={{ id: currentProjectId, data: report, name: report.metadata.projectName }} onBack={() => setView('project-hub')} isReadOnly={isProjectReadOnly} />}
            {view === VIEW_SIFA_TAETIGKEITEN && currentProjectId && report && <TaetigkeitenModul project={{ id: currentProjectId, data: report, name: report.metadata.projectName }} userProfile={userProfile} company={invoiceCompany} onBack={() => setView('project-hub')} isReadOnly={isProjectReadOnly} />}
            {/* Gefaehrdungsbeurteilungen: Props wie bei den Unterweisungen.
              * onUpdate, weil „Zeit buchen" nach dem Riegel die Leistung ins
              * Projekt gutschreibt (docs/sifa-gbu-plan.md §10). */}
            {view === VIEW_SIFA_GBU && currentProjectId && report && <GbuModul project={{ id: currentProjectId, data: report, name: report.metadata.projectName }} userProfile={userProfile} onBack={() => setView('project-hub')} onUpdate={handleUpdateReport} isReadOnly={isProjectReadOnly} />}
            {/* Die Ablage kennt nur die Projekt-Id und den Anwender — sie
              * haengt an einer eigenen Tabelle und nicht am Projekt-JSON.
              * Deshalb geht hier bewusst KEIN onUpdate durch: nichts an diesem
              * Modul veraendert den Bericht. */}
            {view === 'module-dokumente' && currentProjectId && report && <DokumenteModul projectId={currentProjectId} projektName={report.metadata.projectName} userProfile={userProfile} onBack={() => setView('project-hub')} isReadOnly={isProjectReadOnly} istUebungsprojekt={istUebungsprojekt(report)} />}
            {/* Die Planungsphase haengt an eigenen Tabellen (planungsphasen /
              * planungsfragen / planungshinweise) und nicht am Projekt-JSON —
              * deshalb geht hier bewusst KEIN onUpdate durch. Firmenstammdaten
              * kommen mit, weil das Modul die Klaerungsliste im Hausbriefkopf
              * erzeugt. */}
            {view === 'module-planung' && currentProjectId && report && <PlanungModul projectId={currentProjectId} projektName={report.metadata.projectName} projektNummer={report.metadata.projectNumber} projektAdresse={report.metadata.projectAddress} projektDaten={report.metadata} userProfile={userProfile} company={invoiceCompany} quoteConfig={quoteConfigMemo} invoiceConfig={invoiceConfigMemo} onBack={() => setView('project-hub')} onNavigate={executeModuleNavigation} isReadOnly={isProjectReadOnly} />}
            {/* Handbuch und Koordinierung stehen jetzt in der SiGe-Plan-Huelle
              * weiter oben. Sie haengen wie die Planungsphase an eigenen
              * Tabellen. Das Handbuch bekommt von der Huelle seit Plan B5
              * Bericht und Schreibweg — nur fuer den Abschlussschritt nach der
              * Freigabe (Leistung buchen, Bestandteile). Die Koordinierung ist die einzige
              * Ausnahme in Wartestellung — ihre Uebernahme in die
              * Gefaehrdungsmatrix (report.mutualHazards) kommt erst mit AP6
              * und bekommt den Weg dann ausdruecklich, nicht vorsorglich. */}
            {/* A+S-Plan und Baustellenordnung bekommen Bericht und Schreibweg
              * (Plan Paketposition B5): beide bieten die Leistungsbuchung direkt
              * nach der Freigabe an und fragen die Bestandteile einer
              * Paketposition nach. Ohne `report`/`onUpdate` bliebe die Buchung
              * tot — so war es bei der Baustellenordnung bis hierhin. */}
            {view === 'module-asplan' && currentProjectId && report && <AsplanModul projectId={currentProjectId} projektName={report.metadata.projectName} projektNummer={report.metadata.projectNumber} projektAdresse={report.metadata.projectAddress} userProfile={userProfile} company={invoiceCompany} quoteConfig={quoteConfigMemo} invoiceConfig={invoiceConfigMemo} onBack={() => setView('project-hub')} isReadOnly={isProjectReadOnly} report={report} onUpdate={handleUpdateReport} />}
            {view === 'module-bauordnung' && currentProjectId && report && <BauordnungModul projectId={currentProjectId} projektName={report.metadata.projectName} projektNummer={report.metadata.projectNumber} projektAdresse={report.metadata.projectAddress} userProfile={userProfile} onBack={() => setView('project-hub')} isReadOnly={isProjectReadOnly} report={report} onUpdate={handleUpdateReport} />}
            {view === 'module-archive' && report && <ProjectArchiveView report={report} onBack={() => setView('project-hub')} projectId={currentProjectId || undefined} isReadOnly={isProjectReadOnly} />}
            </React.Fragment>
            </>)}
            {/*
              * Begruendung fuer ein fremdes Projekt (SQL Nr. 128). Nur mit
              * geladenem Bericht: Nummer, Name und Verantwortlicher kommen
              * daraus, und ohne offenes Projekt gibt es nichts zu begruenden.
              */}
            {foreignDialogOpen && report && (
                <ForeignAccessDialog
                    projectNumber={report.metadata?.projectNumber}
                    projectName={report.metadata?.projectName}
                    ownerName={report.metadata?.coordinator}
                    busy={foreignBusy}
                    error={foreignError}
                    onSubmit={(reason) => { void handleForeignSubmit(reason); }}
                    onCancel={handleForeignCancel}
                />
            )}
            {isProfileOpen && userProfile && <UserSettingsModal user={userProfile} onClose={() => setIsProfileOpen(false)} onUpdateProfile={async (updates) => { const { error } = await supabase.from('profiles').update(updates).eq('id', userProfile.id); if (error) throw new Error(error.message); fetchProfile(userProfile.id); }} onEinstellungenGespeichert={() => fetchProfile(userProfile.id, false)} onOpenHolidayPlanner={umfangBeschraenkt(appVariante()) ? undefined : (start, end) => { setHolidayRange({ start, end }); setIsProfileOpen(false); setIsHolidayPlannerOpen(true); }} />}
            {isHolidayPlannerOpen && userProfile && <HolidayPlanner projects={projects} userProfile={userProfile} onClose={() => setIsHolidayPlannerOpen(false)} initialStartDate={holidayRange.start} initialEndDate={holidayRange.end} onSave={async () => { const data = await loadProjectsFromCloud(); setProjects(data); }} />}
            {showSetupWizard && darfProjektAnlegen && <ProjectSetupWizard onComplete={handleSetupComplete} onCancel={() => setShowSetupWizard(false)} currentUser={userProfile} />}
            {gesperrtesProjekt && (
                <div className="fixed inset-0 z-[120] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="projekt-gesperrt-titel">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                        <h3 id="projekt-gesperrt-titel" className="font-bold text-lg text-slate-800">{gesperrtesProjekt}</h3>
                        <p className="text-sm text-slate-600 mt-2">{PROJEKT_GESPERRT_HINWEIS}</p>
                        <div className="mt-5 flex justify-end">
                            <Button variant="primary" onClick={() => setGesperrtesProjekt(null)}>OK</Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Über dem Projekt-Assistenten (z-[150]): dessen Absage steht sonst dahinter. */}
            {zugriffHinweis && (
                <div className="fixed inset-0 z-[260] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="zugriff-hinweis-titel">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                        <h3 id="zugriff-hinweis-titel" className="font-bold text-lg text-slate-800">{zugriffHinweis.titel}</h3>
                        <p className="text-sm text-slate-600 mt-2">{zugriffHinweis.text}</p>
                        <div className="mt-5 flex justify-end">
                            <Button variant="primary" onClick={() => setZugriffHinweis(null)}>OK</Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Pflicht-Popup fuer den ausgelosten Pruefer eines Loeschantrags.
              * Fragt selbst nach (Takt, Tab-Rueckkehr) und rendert nichts,
              * solange nichts auf diesen Nutzer wartet. Unter dem
              * Zugriffshinweis (z-[260]), ueber allem anderen. */}
            <LoeschantragPruefDialog userProfile={userProfile} />

            {/*
              * Die Warteschlange kommt nicht durch, weil ein anderer das
              * Projekt bearbeitet. Bewusst ein Hinweis am Rand und kein Dialog:
              * es gibt nichts zu entscheiden — die Arbeit ist lokal sicher und
              * geht von selbst raus, sobald das Projekt frei ist. Der Text
              * erscheint je Halter nur einmal (siehe useOfflineSync), damit er
              * nicht alle 45 Sekunden neu aufpoppt.
              *
              * Derselbe Balken traegt auch die Absage des Altersriegels: eine
              * lokale Aenderung, die aelter als 24 h war, wurde NICHT
              * hochgeladen (07.09.2026). Der Text nennt Projekt und Alter.
              *
              * Gezeigt wird immer der AELTESTE offene Hinweis; das Kreuz nimmt
              * ihn weg und laesst den naechsten nachruecken. Angefasst wird er
              * dabei ueber seine `id` und seine Kennung, nie ueber die
              * Position in der Liste oder ueber das Projekt: waehrend des
              * Wartens auf die IndexedDB kann ein Hinweis dazukommen, und in
              * der Warteschlange kann laengst ein anderer Stand liegen.
              *
              * Beim Altersriegel quittiert das Kreuz ihn zugleich
              * (`acknowledgeTooOld`) — erst
              * diese Quittung macht ihn ueber Neustarts hinweg still, und
              * genau deshalb darf sie nicht schon beim Melden fallen: sonst
              * verstummten mehrere alte Eintraege auf einen Schlag, obwohl nur
              * einer von ihnen sichtbar war.
              *
              * NUR diese Absage bekommt den Knopf „Lokalen Stand verwerfen":
              * sie geht nie von selbst raus. Zweistufig und ohne
              * Browser-Dialog — der erste Klick schaerft, der zweite loescht.
              * Verworfen wird ausschliesslich der lokale Beleg in der
              * Warteschlange dieses Geraets; der Serverstand, also das, was
              * die App anzeigt, bleibt unberuehrt.
              */}
            {ersterQueueHinweis && (
                <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-[120] bg-amber-100 border border-amber-300 text-amber-900 rounded-xl shadow-2xl px-4 py-3 flex items-start gap-2 flex-wrap text-sm max-w-[92vw]">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="min-w-0 flex-1">{ersterQueueHinweis.message}</span>
                    {ersterQueueHinweis.kennung && (
                        <button
                            type="button"
                            disabled={verwerfenLaeuftFuer === ersterQueueHinweis.id}
                            onClick={async () => {
                                const { id, kennung } = ersterQueueHinweis;
                                if (!kennung) return;
                                // Erster Klick: nur scharf machen.
                                if (verwerfenArmiertFuer !== id) { setVerwerfenArmiertFuer(id); return; }
                                setVerwerfenLaeuftFuer(id);
                                const verworfen = await offlineSync.discardTooOldEntry(kennung);
                                // Nur zuruecknehmen, was DIESER Klick gesetzt
                                // hat: waehrend des Wartens kann ein anderer
                                // Hinweis scharf gemacht oder gestartet worden
                                // sein, und der darf hier nicht entwaffnet
                                // werden.
                                setVerwerfenLaeuftFuer(cur => cur === id ? null : cur);
                                setVerwerfenArmiertFuer(cur => cur === id ? null : cur);
                                if (!verworfen) {
                                    // Der Hinweis bleibt stehen — samt Kennung,
                                    // damit ein zweiter Versuch moeglich ist.
                                    // Adressiert ueber die id: waehrend des
                                    // Wartens kann er weitergerueckt sein.
                                    setQueueBlockedNotices(prev => prev.map(h => h.id === id
                                        ? { ...h, message: 'Der lokale Stand konnte nicht verworfen werden. Bitte erneut versuchen.' }
                                        : h));
                                    return;
                                }
                                setQueueBlockedNotices(prev => prev.filter(h => h.id !== id));
                            }}
                            className="ml-auto shrink-0 px-2 py-1 rounded-lg border border-amber-400 font-medium hover:bg-amber-200/70 transition-colors disabled:opacity-50"
                        >
                            {verwerfenArmiertFuer === ersterQueueHinweis.id
                                ? 'Wirklich verwerfen?'
                                : 'Lokalen Stand verwerfen'}
                        </button>
                    )}
                    <button
                        type="button"
                        disabled={verwerfenLaeuftFuer === ersterQueueHinweis.id}
                        onClick={() => {
                            const { id, kennung } = ersterQueueHinweis;
                            if (kennung) void offlineSync.acknowledgeTooOld(kennung);
                            entferneQueueHinweis(id);
                        }}
                        aria-label="Hinweis schließen"
                        className="shrink-0 p-1 rounded-lg hover:bg-amber-200/70 transition-colors disabled:opacity-50"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/*
              * Vorwarnung vor dem Leerlauf-Rueckzug — steht nur in den letzten
              * zwei Minuten und nur, wenn der Rueckzug wirklich faellt (dieselbe
              * Bedingung wie er selbst, siehe describeIdleCountdown).
              *
              * Ueber den anderen Hinweisen und ohne Schliessen-Kreuz: hier gibt
              * es etwas zu entscheiden, und die einzige Antwort darauf ist der
              * Knopf. Wegklicken hiesse „ich bin da, aber schliesst ruhig" —
              * das ergibt keinen Sinn.
              *
              * Vorgelesen wird der Satz einmal beim Erscheinen; die laufende
              * Zahl bleibt fuer Screenreader aussen vor, sonst spraeche das
              * Geraet jede Sekunde dazwischen.
              */}
            {idleCountdownMs !== null && (
                <div
                    role="status"
                    className="fixed bottom-36 md:bottom-20 left-1/2 -translate-x-1/2 z-[130] bg-amber-100 border border-amber-400 text-amber-900 rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 text-sm max-w-[92vw]"
                >
                    <Clock className="w-4 h-4 shrink-0" />
                    <span aria-hidden="true">{idleWarningNotice(formatIdleCountdown(idleCountdownMs))}</span>
                    <span className="sr-only">
                        Keine Eingabe seit einer Weile: Das Projekt wird gleich automatisch geschlossen
                        und für Ihre Kollegen freigegeben.
                    </span>
                    <button
                        type="button"
                        onClick={handleIdleKeepAlive}
                        className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white font-bold px-3 py-1.5 rounded-lg text-xs"
                    >
                        {IDLE_KEEP_ALIVE_LABEL}
                    </button>
                </div>
            )}

            {/*
              * Das Projekt hat sich selbst geschlossen, weil eine halbe Stunde
              * lang niemand etwas angefasst hat. Blau und nicht gelb: hier ist
              * nichts schiefgegangen und nichts zu tun — die Ansicht hat sich
              * ohne Zutun des Nutzers geaendert, und das braucht eine Erklaerung,
              * keine Warnung. Bleibt stehen, bis er ihn wegklickt; er war beim
              * Rueckzug per Definition nicht am Platz und soll ihn nicht
              * verpassen.
              */}
            {idleNotice && (
                <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-[120] bg-sky-50 border border-sky-300 text-sky-900 rounded-xl shadow-2xl px-4 py-3 flex items-start gap-3 text-sm max-w-[92vw]">
                    <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{idleNotice}</span>
                    <button
                        type="button"
                        onClick={() => setIdleNotice(null)}
                        aria-label="Hinweis schließen"
                        className="shrink-0 p-1 rounded-lg hover:bg-sky-200/70 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {updateFensterOffen && (
                // Mittig und mit abgedunkeltem Hintergrund (Betreiber 03.10.2026):
                // ein Balken am unteren Rand wurde uebersehen. „Spaeter"
                // verkleinert auf den Balken darunter — am Handy liegt das
                // Fenster sonst ueber dem Mangel-Assistenten (z-[100]).
                // max-h-full + Scrollen: Handy quer / grosse Schrift — die
                // Knoepfe duerfen nie unerreichbar unter dem Rand liegen.
                <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-200">
                    <div
                        ref={setUpdateFensterEl}
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="update-fenster-titel"
                        aria-describedby="update-fenster-text"
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-full overflow-y-auto border border-slate-200"
                    >
                        <div className={`${updateAnlass === 'riegel' ? 'bg-amber-500' : 'bg-emerald-600'} p-6 flex gap-3 items-start text-white`}>
                            <div className="bg-white/20 p-2 rounded-xl">
                                {updateAnlass === 'riegel'
                                    ? <AlertTriangle className="w-6 h-6 text-white" />
                                    : <RefreshCw className="w-6 h-6 text-white" />}
                            </div>
                            <div>
                                <h3 id="update-fenster-titel" className="font-bold text-lg">
                                    {updateAnlass === 'riegel' ? 'Programmstand veraltet' : 'Neue Version verfügbar'}
                                </h3>
                                <p className="text-white/85 text-xs font-medium mt-1">Bitte jetzt aktualisieren</p>
                            </div>
                        </div>
                        <div className="p-6">
                            <p id="update-fenster-text" className="text-slate-600 text-sm leading-relaxed">
                                {updateAnlass === 'riegel'
                                    ? BUILD_VERALTET_MELDUNG
                                    : report
                                        ? 'Eine neue Version ist da. „Jetzt aktualisieren“ sichert Ihre Eingaben und lädt neu — sonst wird sie nach dem Schließen des Berichts aktiviert.'
                                        : 'Eine neue Version ist da. „Jetzt aktualisieren“ sichert Ihre Eingaben und lädt die Seite neu.'}
                            </p>
                            {updateHinweis && <p role="status" className="mt-3 text-sm font-semibold text-amber-700">{updateHinweis}</p>}
                        </div>
                        <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col-reverse sm:flex-row justify-end gap-2">
                            <button
                                type="button"
                                onClick={updateVerkleinern}
                                className="min-h-[44px] px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
                            >
                                Später
                            </button>
                            {/* Nie ungeschuetzt neu laden: offene Maske → Hinweis, sonst erst sichern (jetztAktualisieren). */}
                            <button
                                type="button"
                                data-update-haupt
                                onClick={() => { void jetztAktualisieren(); }}
                                disabled={updateSichert}
                                className="min-h-[44px] px-5 py-2 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 shadow-lg shadow-emerald-200 transition-colors"
                            >
                                {updateSichert ? 'Wird gesichert …' : 'Jetzt aktualisieren'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {updateAnlass !== null && updateKlein && (
                // Am Handy UNTER den Vollbild-Masken (Mangel-Assistent z-[100]): der
                // Balken verdeckte dort „Verwerfen" und „Anderer Eintrag". Er kommt
                // wieder, sobald die Maske zu ist; gespeichert wird lokal weiter.
                <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-[95] md:z-[120] bg-slate-800 text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-3 text-sm max-w-[92vw]">
                    {/*
                      * Beim Riegel ist Speichern bereits gesperrt — „neue
                      * Version verfuegbar" waere dort eine Untertreibung, die
                      * den Anwender weiterarbeiten liesse.
                      */}
                    <span className="min-w-0">
                        {updateAnlass === 'riegel'
                            ? BUILD_VERALTET_MELDUNG
                            : `Neue Version verfügbar${report ? ' — wird nach dem Schließen des Berichts aktiviert' : ''}.`}
                        {updateHinweis && <span role="status" className="block mt-1 text-xs font-semibold text-amber-300">{updateHinweis}</span>}
                    </span>
                    {/* Nie ungeschuetzt neu laden: offene Maske → Hinweis, sonst erst sichern (jetztAktualisieren). */}
                    <button onClick={() => { void jetztAktualisieren(); }} disabled={updateSichert} className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold px-3 py-1.5 min-h-[44px] md:min-h-0 rounded-lg text-xs shrink-0">
                        {updateSichert ? 'Wird gesichert …' : 'Jetzt aktualisieren'}
                    </button>
                </div>
            )}

            {/*
              * Kontext-"?" (Lernzentrum, Plan 2.1) — das globale Element unten
              * rechts, fuer alle Ansichten OHNE eigenes Fragezeichen.
              *
              * Nicht im Editor-Vollbild: dort ist der Platz unten rechts die
              * Arbeitsflaeche. Nicht im Lernzentrum selbst: ein "?" das auf
              * die Seite zeigt, auf der man steht, ist eine Schleife. Nicht in
              * den Ansichten aus OHNE_GLOBALES_FRAGEZEICHEN — die tragen es
              * seit dieser Verdrahtung in ihrer eigenen Kopfleiste
              * (ProjectHeader, AdminView, InvoicesView), und zwei Knoepfe
              * nebeneinander waeren eine Frage zuviel. Mobil blendet die
              * Komponente sich selbst aus (md:flex).
              */}
            {view !== 'editor' && view !== 'lernzentrum' && !OHNE_GLOBALES_FRAGEZEICHEN.includes(view) && (
                <KontextHilfe view={view} onOeffneLernzentrum={handleOpenLernzentrum} />
            )}

            <MobileNavigation
                view={view}
                setView={navigateToView}
                onToggleMenu={() => setMobileMenuOpen(true)}
                onFabClick={handleMobileFabClick}
                userId={userProfile?.id}
                appSeiteAktiv={appSeiteOffen}
                darfAnsicht={darfAnsicht}
            />

            {/*
              * Die gefuehrte Tour (Paket B). Ganz am Ende des Baums, damit ihr
              * Overlay ueber allem liegt; `kapitelId={null}` rendert nichts.
              *
              * `onNavigiere` nur, wenn das Kapitel woanders zuhause ist: das
              * Overlay bietet den Wechsel an, wenn ein Anker nicht auftaucht —
              * ein Angebot auf die Ansicht, in der wir schon stehen, waere ein
              * falscher Hinweis auf die Ursache.
              */}
            <TourOverlay
                kapitelId={tour.aktiveTourId}
                onEnde={tour.beendeTour}
                onNavigiere={tourKapitelView && tourKapitelView !== view ? wechsleZurKapitelAnsicht : undefined}
            />

            {/*
              * Der Lern-Chat „Frag OS-IA" (Plan §1.8, Paket E). Wie das
              * Kontext-„?" nur, wenn ein Profil geladen ist — ohne Rollen laeuft
              * der Kontextaufbau ins Leere. Nicht fuer den Freelancer (eigener,
              * schmalerer Zweig weiter oben, der hier gar nicht erst ankommt)
              * und nicht waehrend der Bauphase (dito). Nicht im Editor: dort ist
              * die Ecke unten rechts die Arbeitsflaeche, wie beim Kontext-„?".
              * Anders als das Kontext-„?" bleibt der Chat aber im Lernzentrum
              * selbst sichtbar — „Dazu fragen" auf einer Kapitelseite muss ihn
              * dort oeffnen koennen.
              *
              * onStarteTour: `handleStarteTour` braucht die Zielansicht des
              * Kapitels als zweites Argument (siehe `LernzentrumView.starteTour`,
              * dasselbe Muster) — der Chat kennt nur die Kapitel-Id.
              */}
            {/* Kein `role !== 'freelancer'` mehr: der Freelancer-Zweig weiter oben
              * kehrt vorher zurueck (FreelancerPortal), die Rolle kann hier gar
              * nicht mehr auftreten. */}
            {!!userProfile && !bauphase.showScreen && view !== 'editor' && (
                <HilfeChat
                    view={view}
                    rollen={tourRollen}
                    onOeffneKapitel={id => handleOpenLernzentrum(id)}
                    onStarteTour={id => {
                        const k = kapitelNachId(id);
                        if (k) handleStarteTour(id, k.view);
                    }}
                    vorbelegtesKapitel={hilfeChatKapitel}
                />
            )}
        </main>
    </div>
    </ForeignAccessContext.Provider>
    </LernzentrumOeffnerContext.Provider>
  );
}
